/**
 * @roguelik/shared-sim
 *
 * The authoritative game simulation, written as PURE, DETERMINISTIC functions
 * so the exact same code runs on:
 *   - the server (source of truth), and
 *   - the client (client-side prediction).
 *
 * Determinism rules (enforced by review + tests):
 *   - No `Date.now()`, no `Math.random()`. Randomness comes from a seeded PRNG
 *     that is threaded explicitly through the state.
 *   - Fixed timestep only. The caller (a Web Worker) owns the accumulator loop
 *     and calls `step()` with a constant `dt`.
 *
 * Phase 0 scope: local players moving with WASD inside a bounded arena.
 * Networking, enemies and combat arrive in later phases but reuse these types.
 */

// ---------------------------------------------------------------------------
// Branded types (prevent mixing up ids / ticks with plain numbers)
// ---------------------------------------------------------------------------

declare const brand: unique symbol;
type Brand<T, B extends string> = T & { readonly [brand]: B };

export type PlayerId = Brand<number, "PlayerId">;
export type Tick = Brand<number, "Tick">;

export const asPlayerId = (n: number): PlayerId => n as PlayerId;
export const asTick = (n: number): Tick => n as Tick;

// ---------------------------------------------------------------------------
// Simulation constants
// ---------------------------------------------------------------------------

/** Authoritative simulation rate. */
export const TICK_HZ = 30;
/** Fixed timestep in milliseconds (≈33.333ms). */
export const SIM_DT_MS = 1000 / TICK_HZ;
/** Fixed timestep in seconds. */
export const SIM_DT_S = SIM_DT_MS / 1000;

/** Tunable world rules. Kept in one struct so client & server share them. */
export interface WorldConfig {
  /** Player movement speed in world units / second. */
  readonly playerSpeed: number;
  /** Half-extent of the square arena (world spans [-halfSize, +halfSize]). */
  readonly arenaHalfSize: number;
  /** Player collision radius (enemies stop at playerRadius + enemyRadius). */
  readonly playerRadius: number;
  /** Fraction of an enemy-enemy overlap resolved per tick (0..1). */
  readonly enemySeparation: number;
  /** Starting/maximum player health. */
  readonly playerMaxHp: number;
  /** Run length until victory (ms). */
  readonly runDurationMs: number;
  /** XP gem auto-collect radius (world units). */
  readonly pickupCollectRadius: number;
  /** XP gem magnet (attraction) base radius; scaled by a player's magnetMul. */
  readonly pickupMagnetRadius: number;
  /** Magnet pull speed (world units / second). */
  readonly pickupSpeed: number;
  /** XP gem lifetime before it despawns (ms). */
  readonly gemTtlMs: number;
  /** Chance [0..1] a slain enemy also drops a heal pickup. */
  readonly healDropChance: number;
  /** Fraction of max HP restored by a heal pickup. */
  readonly healDropFraction: number;
  /** Revive: an alive ally within this radius of a downed player revives them. */
  readonly reviveRadius: number;
  /** Cumulative time (ms) an ally must spend reviving. */
  readonly reviveMs: number;
  /** Fraction of max HP restored on revive. */
  readonly reviveHpFraction: number;
  /** Run clock (ms) at which the optional extraction zone opens (once per run). */
  readonly extractionOpenMs: number;
  /** Extraction zone radius (world units). */
  readonly extractionRadius: number;
  /** Hold time (ms) inside the zone required to extract. */
  readonly extractionHoldMs: number;
  /** One-time enemy burst spawned when the extraction zone is activated. */
  readonly extractionHordeBurst: number;
  /** Static wall colliders (Phase D): axis-aligned boxes that block players, enemies, shots. */
  readonly walls: readonly Wall[];
}

/** An axis-aligned box collider (Phase D). Center (cx,cy) + half-extents (hx,hy). */
export interface Wall {
  readonly cx: number;
  readonly cy: number;
  readonly hx: number;
  readonly hy: number;
}

export const DEFAULT_WORLD_CONFIG: WorldConfig = {
  playerSpeed: 6,
  arenaHalfSize: 30,
  playerRadius: 0.4,
  enemySeparation: 0.5,
  playerMaxHp: 100,
  runDurationMs: 600_000,
  pickupCollectRadius: 0.7,
  pickupMagnetRadius: 3,
  pickupSpeed: 14,
  gemTtlMs: 30_000,
  healDropChance: 0.015,
  healDropFraction: 0.08,
  reviveRadius: 2.5,
  reviveMs: 3000,
  reviveHpFraction: 0.4,
  extractionOpenMs: 120_000,
  extractionRadius: 4,
  extractionHoldMs: 20_000,
  extractionHordeBurst: 60,
  walls: [], // open arena by default; a MapDef supplies walls (Phase D)
};

// ---------------------------------------------------------------------------
// Maps (Phase D): static arena geometry — walls + objective zones. A `MapDef` is
// applied at run start to build the room's WorldConfig.walls; geometry travels to
// the client in the `welcome` control message (see shared-net). New maps are a
// table edit. The in-browser editor (authoring MapDefs) is a later phase.
// ---------------------------------------------------------------------------

/** A placed objective zone (Phase D3) — generalizes the extraction hold-the-point. */
export interface ObjectiveZone {
  readonly kind: "hold" | "core";
  readonly x: number;
  readonly y: number;
  readonly radius: number;
}

export interface MapDef {
  readonly id: string;
  readonly name: string;
  readonly arenaHalfSize: number;
  readonly walls: readonly Wall[];
  readonly objectives?: readonly ObjectiveZone[];
}

const W = (cx: number, cy: number, hx: number, hy: number): Wall => ({ cx, cy, hx, hy });

export const MAPS: readonly MapDef[] = [
  // Open arena (default) — no walls, current behavior.
  { id: "arena", name: "Open Arena", arenaHalfSize: 30, walls: [] },
  // Chokepoints — four interior blocks create lanes and cover to kite around.
  {
    id: "chokepoints",
    name: "Chokepoints",
    arenaHalfSize: 30,
    walls: [
      W(-12, -12, 3, 3),
      W(12, -12, 3, 3),
      W(-12, 12, 3, 3),
      W(12, 12, 3, 3),
      W(0, 0, 1.5, 8), // central divider
    ],
  },
  // Bastion — a ring of pillars + a central holdout you can defend an objective in.
  {
    id: "bastion",
    name: "Bastion",
    arenaHalfSize: 30,
    walls: [W(0, -16, 10, 1.5), W(0, 16, 10, 1.5), W(-16, 0, 1.5, 10), W(16, 0, 1.5, 10)],
    objectives: [{ kind: "hold", x: 0, y: 0, radius: 4 }],
  },
];

export const DEFAULT_MAP = "arena";
export function mapById(id: string): MapDef | undefined {
  return MAPS.find((m) => m.id === id);
}

// ---------------------------------------------------------------------------
// Enemy types (data-driven so new enemy kinds are a table edit, not code)
// ---------------------------------------------------------------------------

export interface EnemyType {
  readonly name: string;
  /** Movement speed (world units / second). */
  readonly speed: number;
  /** Collision/separation radius. */
  readonly radius: number;
  /** Render color (client reads this from the shared table). */
  readonly color: number;
  /** Spawn/maximum hit points. */
  readonly hp: number;
  /** Damage per second dealt to a player while in contact range. */
  readonly contactDamage: number;
  /** Mini-boss "charge": periodic deterministic speed lunges toward the target. */
  readonly charge?: boolean;
  /** Boss "strafe": hold a stand-off distance from the target and orbit it (kiting). */
  readonly standOff?: number;
  /** Boss ranged attack: fire a projectile volley this often (ms). 0/undefined = melee only. */
  readonly fireCooldownMs?: number;
  /** Boss projectile: shots per volley (spread fan). */
  readonly projectileCount?: number;
  /** Boss projectile: travel speed (units/s). */
  readonly projectileSpeed?: number;
  /** Boss projectile: damage per hit. */
  readonly projectileDamage?: number;
  /**
   * Elite (mini-boss/boss) behavior archetype (item 5). Drives a telegraphed
   * special in `stepEliteBehaviors`. `undefined` = no special (plain melee/charge).
   */
  readonly elite?: EliteArchetype;
  // --- Phase B counter systems ---
  /** Incoming-physical damage multiplier (Phase B1). Default 1; <1 resists, >1 is a weakness. */
  readonly resistPhysical?: number;
  /** Incoming-magic damage multiplier (Phase B1). Default 1; <1 resists, >1 is a weakness. */
  readonly resistMagic?: number;
  /** Shield HP layer (Phase B2): depleted before `hp`, regenerates if untouched. 0 = none. */
  readonly shieldHp?: number;
  /** Aegis (Phase B3): takes heavily reduced damage from its frontal arc — must be flanked. */
  readonly aegis?: boolean;
  /** Suppressor (Phase B4): emits an aura that disables player lifesteal + regen nearby. */
  readonly suppressor?: boolean;
}

/** Damage type of a power (Phase B1). Half the arsenal is physical, half magic, so a
 * mono-type build hits a wall against the matching resistant enemy and must diversify. */
export type DamageType = "physical" | "magic";

/** Mini-boss / boss behavior archetypes (item 5). */
export type EliteArchetype =
  | "tank" // just tankier; no special
  | "dasher" // telegraphs a rectangle, then dashes along it dealing damage
  | "warder" // periodically spawns stationary "wards" that shoot at players
  | "charger" // charges a circle around itself → blast + knockback
  | "poisoner" // drops lingering poison circles around players
  | "bullet-hell"; // final boss: arena pull-in + multi-dash + dense aimed fire

/** Per-elite behavior state (timers/phase), keyed by enemy id on the world. */
export interface EliteState {
  /** Cooldown until the next special (ms). */
  cdMs: number;
  /** Active special phase: 0 = idle, 1 = telegraphing/winding up, 2 = firing/active. */
  phase: number;
  /** Phase timer remaining (ms). */
  phaseMs: number;
  /** Cached target / direction for the active special. */
  tx: number;
  ty: number;
}

/** Index into `ENEMY_TYPES`; travels in the binary snapshot as a u8. */
export type EnemyTypeId = number;

export const ENEMY_TYPES: readonly EnemyType[] = [
  { name: "grunt", speed: 3.5, radius: 0.35, color: 0xb46cff, hp: 20, contactDamage: 6 },
  { name: "runner", speed: 5.0, radius: 0.28, color: 0xff6c8a, hp: 12, contactDamage: 4 },
  { name: "brute", speed: 2.0, radius: 0.6, color: 0x6c8aff, hp: 60, contactDamage: 14 },
  // The final boss (id 3): a bullet-hell duel — pulls players into a shrunken arena,
  // dashes in multiple directions, and fires dense aimed volleys. See "bullet-hell".
  {
    name: "boss",
    speed: 3.4,
    radius: 2.0,
    color: 0xff2d4b,
    hp: 11000,
    contactDamage: 40,
    fireCooldownMs: 1100,
    projectileCount: 9,
    projectileSpeed: 10,
    projectileDamage: 16,
    elite: "bullet-hell",
    resistPhysical: 0.8,
    resistMagic: 0.8,
  },
  // Mini-boss archetypes (ids 4..8), assigned per wave. id 4 is the baseline tank.
  {
    name: "Bulwark", // tank: shielded — break the shield before its HP
    speed: 3.0,
    radius: 1.1,
    color: 0xffa033,
    hp: 1600,
    contactDamage: 28,
    charge: true,
    elite: "tank",
    shieldHp: 600,
  },
  {
    name: "Lancer", // dasher: aegis — flank it (its front shrugs off damage)
    speed: 3.0,
    radius: 1.0,
    color: 0xff5a3c,
    hp: 1900,
    contactDamage: 26,
    elite: "dasher",
    aegis: true,
  },
  {
    name: "Conduit", // warder: resists magic (weak to physical)
    speed: 2.6,
    radius: 1.1,
    color: 0x3cd0ff,
    hp: 2100,
    contactDamage: 24,
    elite: "warder",
    resistMagic: 0.5,
    resistPhysical: 1.2,
  },
  {
    name: "Colossus", // charger: armored — resists physical (weak to magic)
    speed: 2.4,
    radius: 1.3,
    color: 0xc065ff,
    hp: 2800,
    contactDamage: 30,
    elite: "charger",
    resistPhysical: 0.5,
    resistMagic: 1.2,
  },
  {
    name: "Blight", // poisoner: also a suppressor (anti-heal aura)
    speed: 3.2,
    radius: 1.0,
    color: 0x7bd14a,
    hp: 1900,
    contactDamage: 22,
    elite: "poisoner",
    suppressor: true,
  },
  // id 9: a stationary "ward" turret spawned by the Conduit; small, shoots, low HP.
  {
    name: "ward",
    speed: 0,
    radius: 0.5,
    color: 0x8ae6ff,
    hp: 120,
    contactDamage: 8,
    fireCooldownMs: 1400,
    projectileCount: 1,
    projectileSpeed: 8,
    projectileDamage: 10,
  },
  // --- Phase B counter trash (ids 10..14): mixed into mid/late waves to punish mono-builds. ---
  {
    // Armored: shrugs off PHYSICAL, melts to MAGIC. Slow, chunky.
    name: "Hardplate",
    speed: 2.6,
    radius: 0.5,
    color: 0x9fb0c8,
    hp: 46,
    contactDamage: 10,
    resistPhysical: 0.4,
    resistMagic: 1.3,
  },
  {
    // Ethereal: shrugs off MAGIC, melts to PHYSICAL. Fast, fragile.
    name: "Wisp",
    speed: 5.2,
    radius: 0.32,
    color: 0xc9a3ff,
    hp: 22,
    contactDamage: 6,
    resistPhysical: 1.3,
    resistMagic: 0.4,
  },
  {
    // Shielded: a shield layer must be broken before its HP (rewards burst over chip).
    name: "Sentinel",
    speed: 3.0,
    radius: 0.46,
    color: 0x66d9c0,
    hp: 30,
    contactDamage: 9,
    shieldHp: 40,
  },
  {
    // Aegis: heavily reduces damage to its FRONT arc — you must hit it from behind.
    name: "Reaver",
    speed: 3.2,
    radius: 0.46,
    color: 0xe0884c,
    hp: 40,
    contactDamage: 11,
    aegis: true,
  },
  {
    // Suppressor: emits an aura that disables nearby players' lifesteal + regen.
    name: "Leech",
    speed: 3.0,
    radius: 0.5,
    color: 0xb84a8c,
    hp: 55,
    contactDamage: 8,
    suppressor: true,
  },
];

/** Enemy type id of the end-of-run boss. */
export const BOSS_ENEMY_TYPE: EnemyTypeId = 3;
/** Enemy type id of the baseline (tank) per-wave mini-boss. */
export const MINIBOSS_ENEMY_TYPE: EnemyTypeId = 4;
/** First / last mini-boss archetype enemy-type ids (4..8 inclusive). */
export const MINIBOSS_TYPE_MIN: EnemyTypeId = 4;
export const MINIBOSS_TYPE_MAX: EnemyTypeId = 8;
/** Stationary ward turret spawned by the Conduit archetype. */
export const WARD_ENEMY_TYPE: EnemyTypeId = 9;
/** Phase B counter-trash type ids. */
export const ENEMY_ARMORED: EnemyTypeId = 10;
export const ENEMY_ETHEREAL: EnemyTypeId = 11;
export const ENEMY_SHIELDED: EnemyTypeId = 12;
export const ENEMY_AEGIS: EnemyTypeId = 13;
export const ENEMY_SUPPRESSOR: EnemyTypeId = 14;
/**
 * Trash composition list (Phase B): a wave's `weights[i]` is the relative weight of
 * `TRASH_TYPE_IDS[i]`. Lets counter variants live at the end of `ENEMY_TYPES` (ids 10+)
 * without colliding with the boss/mini-boss/ward id range (3..9). Index 0..2 stay
 * grunt/runner/brute so existing waves/tests are unchanged.
 */
export const TRASH_TYPE_IDS: readonly EnemyTypeId[] = [
  0, // grunt
  1, // runner
  2, // brute
  ENEMY_ARMORED,
  ENEMY_ETHEREAL,
  ENEMY_SHIELDED,
  ENEMY_AEGIS,
  ENEMY_SUPPRESSOR,
];

export function enemyType(id: EnemyTypeId): EnemyType {
  return ENEMY_TYPES[id] ?? (ENEMY_TYPES[0] as EnemyType);
}

// ---------------------------------------------------------------------------
// Waves (FINITE, kill-to-advance). Each wave spawns a fixed budget of trash and
// only advances once that budget is fully spawned AND cleared (or stragglers time
// out). The run progresses wave-by-wave; clearing the last wave summons the boss.
// ---------------------------------------------------------------------------

/** Wave lifecycle phase. */
export type WavePhase = "spawning" | "lull";

/**
 * One wave: spawn composition (`weights[i]` = relative weight of enemy type `i`),
 * a base `budget` of trash to spawn (scaled by player count at runtime), and the
 * mini-boss archetype ids that join this wave near its end (see MINIBOSS_ARCHETYPES,
 * filled in a later commit; empty = none).
 */
export interface WaveDef {
  readonly name: string;
  readonly weights: readonly number[];
  readonly budget: number;
  /** Concurrent alive-trash cap while this wave spawns (defaults to WAVE_CONCURRENT_CAP). */
  readonly cap?: number;
  /** Trash spawned per director tick while this wave spawns (defaults to DIRECTOR_SPAWN_PER_TICK). */
  readonly rate?: number;
}

/** Breather between waves once one is cleared (ms). */
export const WAVE_LULL_MS = 6_000;
/**
 * Safety: if the wave's budget is fully spawned but a few stragglers refuse to
 * die after this long, force-resolve the wave so one fleeing enemy can't soft-lock
 * the run. Remaining trash is cleared when the wave resolves.
 */
export const WAVE_STRAGGLE_MS = 12_000;
/**
 * No-wipe straggler resolve (bug B1): once the straggler timer expires, leftover
 * trash is killed at most this many per tick (they bleed off over a few ticks
 * instead of vanishing all at once, which read as a screen-wide wipe).
 */
export const STRAGGLER_TRICKLE_PER_TICK = 6;
/**
 * The straggler safety only ever mops up a genuinely SMALL number of stuck enemies —
 * never a live swarm the player is still fighting. If more than this many trash are
 * still alive when the timer expires, nothing is force-killed (the player keeps
 * clearing them by hand). This is the fix for "my whole swarm vanished into XP at ~12s":
 * the old gate force-resolved on time alone, regardless of how many were left.
 */
export const STRAGGLER_FORCE_MAX = 12;
/**
 * Master switch for the straggler auto-clear safeguard. DISABLED for now (per request):
 * waves only advance when the player has killed every enemy — no auto-resolve. Flip to
 * `true` to restore the no-wipe trickle (kills ≤STRAGGLER_FORCE_MAX stuck enemies a few
 * per tick once the timer expires).
 */
export const STRAGGLER_AUTOCLEAR = false;
/** Default concurrent alive-trash cap (per-wave `cap` overrides). Held under the
 * 1024 enemy capacity with headroom; validated for the server tick budget. */
export const WAVE_CONCURRENT_CAP = 280;

/**
 * Finite waves tuned for a ~10–15 min run that RAMPS to a flood (item 2): budgets,
 * concurrent caps, and spawn rates all climb so the late waves "just spawn a lot of
 * mobs". Base budgets sum ≈ 23k; with the per-player budget multiplier (≤×4) a co-op
 * lobby racks up ~40k cumulative kills across a run. `budget` is total trash for the
 * wave (you must clear them to advance); clearing the LAST wave summons the boss.
 * `cap` stays under the 1024 enemy capacity (perf-validated — see docs/METRICS.md).
 */
export const WAVES: readonly WaveDef[] = [
  // weights map to TRASH_TYPE_IDS: [grunt, runner, brute, armored, ethereal, shielded, aegis, suppressor]
  // Early waves are deliberately SLOW & sparse (ease-in); the flood is a late-game fantasy.
  { name: "Grunts", weights: [1, 0, 0, 0, 0, 0, 0, 0], budget: 36, cap: 50, rate: 5 },
  { name: "Skirmish", weights: [3, 2, 0, 0, 0, 0, 0, 0], budget: 80, cap: 80, rate: 8 },
  { name: "Swift Tide", weights: [1, 3, 0, 0, 1, 0, 0, 0], budget: 150, cap: 130, rate: 12 },
  { name: "Vanguard", weights: [2, 1, 1, 1, 1, 0, 0, 0], budget: 260, cap: 200, rate: 17 },
  { name: "Heavy Assault", weights: [1, 1, 2, 2, 1, 1, 0, 0], budget: 440, cap: 290, rate: 24 },
  { name: "Onslaught", weights: [2, 2, 2, 2, 2, 1, 1, 1], budget: 720, cap: 400, rate: 32 },
  { name: "Tempest", weights: [2, 3, 2, 2, 2, 1, 1, 1], budget: 1200, cap: 470, rate: 42 },
  { name: "Maelstrom", weights: [3, 2, 3, 2, 2, 2, 1, 1], budget: 2000, cap: 540, rate: 52 },
  { name: "Cataclysm", weights: [2, 2, 3, 3, 2, 2, 2, 1], budget: 2800, cap: 600, rate: 60 },
  { name: "Annihilation", weights: [3, 3, 3, 3, 3, 2, 2, 2], budget: 3600, cap: 680, rate: 72 },
  { name: "Extinction", weights: [3, 2, 4, 3, 3, 2, 2, 2], budget: 4800, cap: 720, rate: 84 },
  { name: "Apocalypse", weights: [3, 3, 4, 3, 3, 3, 2, 2], budget: 6000, cap: 760, rate: 96 },
];

/** Number of waves before the boss. */
export const WAVE_COUNT = WAVES.length;

/** The wave definition for an index, clamped to the valid range. */
export function waveByIndex(i: number): WaveDef {
  const c = i < 0 ? 0 : i >= WAVES.length ? WAVES.length - 1 : i;
  return WAVES[c] ?? (WAVES[0] as WaveDef);
}

// ---------------------------------------------------------------------------
// Powers (data-driven so Phase 6 can add/upgrade weapons via a table edit)
// ---------------------------------------------------------------------------

export type PowerKind = "projectile" | "aura" | "orbit" | "nova";

export interface PowerType {
  readonly name: string;
  readonly kind: PowerKind;
  /** Physical or magic (Phase B1): which enemy resistance this power is checked against. */
  readonly damageType: DamageType;
  /** Time between activations (ms). Continuous powers (aura/orbit) use 0. */
  readonly cooldownMs: number;
  /** projectile/nova: damage per hit/burst. aura/orbit: damage per second. */
  readonly damage: number;
  /** projectile: acquisition range. Others: see kind-specific radii. */
  readonly range: number;
  /** Render color (client). */
  readonly color: number;
  readonly projectileSpeed?: number;
  readonly projectileRadius?: number;
  /** projectile: base projectiles per shot (defaults to 1; scales with level). */
  readonly projectileCount?: number;
  /** projectile: angular spread (rad) between adjacent projectiles in a volley. */
  readonly spread?: number;
  /** projectile: how many enemies one projectile damages before despawning (0 = single-hit). */
  readonly pierce?: number;
  /** projectile: boomerang — flies out, then curves back toward its owner (item 6). */
  readonly returns?: boolean;
  readonly auraRadius?: number;
  /** orbit: blade orbit radius / angular speed (rad/s) / blade hit radius. */
  readonly orbitRadius?: number;
  readonly orbitSpeed?: number;
  readonly bladeRadius?: number;
  /** nova: burst radius. */
  readonly novaRadius?: number;
}

/** Index into `POWERS`; travels in the binary snapshot (projectiles) as a u8. */
export type PowerId = number;

export const POWERS: readonly PowerType[] = [
  {
    name: "auto-bolt",
    kind: "projectile",
    damageType: "physical",
    cooldownMs: 250,
    damage: 10,
    range: 12,
    color: 0xfff07a,
    projectileSpeed: 18,
    projectileRadius: 0.15,
  },
  {
    name: "pulse",
    kind: "aura",
    damageType: "magic",
    cooldownMs: 0,
    damage: 8,
    range: 3,
    color: 0x7af0ff,
    auraRadius: 3,
  },
  {
    name: "orbit",
    kind: "orbit",
    damageType: "magic",
    cooldownMs: 0,
    damage: 16,
    range: 0,
    color: 0xff9a3c,
    orbitRadius: 2.2,
    orbitSpeed: 3.0,
    bladeRadius: 0.5,
  },
  {
    name: "nova",
    kind: "nova",
    damageType: "magic",
    cooldownMs: 3000,
    damage: 26,
    range: 0,
    color: 0x9b7bff,
    novaRadius: 5,
  },
  {
    name: "scatter",
    kind: "projectile",
    damageType: "physical",
    cooldownMs: 600,
    damage: 7,
    range: 8,
    color: 0xff6a3c,
    projectileSpeed: 16,
    projectileRadius: 0.14,
    projectileCount: 5,
    spread: 0.34,
  },
  {
    name: "lance",
    kind: "projectile",
    damageType: "physical",
    cooldownMs: 700,
    damage: 22,
    range: 16,
    color: 0x6affc0,
    projectileSpeed: 30,
    projectileRadius: 0.18,
    pierce: 4,
  },
  {
    name: "boomerang",
    kind: "projectile",
    damageType: "magic",
    cooldownMs: 900,
    damage: 14,
    range: 14,
    color: 0x59f5d6,
    projectileSpeed: 14,
    projectileRadius: 0.4,
    pierce: 99, // hits everything along both legs
    returns: true,
  },
  // --- Tranche 3 new powers (all projectile-kind so they render by color for free) ---
  {
    name: "railgun", // sniper: slow, piercing, very high damage, fast round
    kind: "projectile",
    damageType: "physical",
    cooldownMs: 1200,
    damage: 44,
    range: 20,
    color: 0xc8d6ff,
    projectileSpeed: 42,
    projectileRadius: 0.22,
    pierce: 8,
  },
  {
    name: "flak", // close-range ring burst of pellets
    kind: "projectile",
    damageType: "physical",
    cooldownMs: 800,
    damage: 6,
    range: 7,
    color: 0xffb347,
    projectileSpeed: 16,
    projectileRadius: 0.16,
    projectileCount: 8,
    spread: 0.7,
  },
  {
    name: "twin fang", // fast piercing pair
    kind: "projectile",
    damageType: "physical",
    cooldownMs: 340,
    damage: 16,
    range: 13,
    color: 0xff5ec7,
    projectileSpeed: 24,
    projectileRadius: 0.16,
    projectileCount: 2,
    spread: 0.16,
    pierce: 1,
  },
  {
    name: "hailstorm", // rapid wide spray of small shards
    kind: "projectile",
    damageType: "physical",
    cooldownMs: 300,
    damage: 9,
    range: 11,
    color: 0x9fe8ff,
    projectileSpeed: 22,
    projectileRadius: 0.13,
    projectileCount: 3,
    spread: 0.42,
  },
];

/** Stable power ids (indices into `POWERS`) referenced by the upgrade table. */
export const POWER_BOLT = 0;
export const POWER_AURA = 1;
export const POWER_ORBIT = 2;
export const POWER_NOVA = 3;
export const POWER_SCATTER = 4;
export const POWER_LANCE = 5;
export const POWER_BOOMERANG = 6;
export const POWER_RAILGUN = 7;
export const POWER_FLAK = 8;
export const POWER_TWINFANG = 9;
export const POWER_HAIL = 10;

export function powerType(id: PowerId): PowerType {
  return POWERS[id] ?? (POWERS[0] as PowerType);
}

/** Players start with just the bolt; aura/orbit/nova are level-up acquisitions. */
export const STARTING_POWER: PowerId = POWER_BOLT;
/** Max level any single power can reach. */
export const MAX_POWER_LEVEL = 6;
/**
 * Max player level. Reaching it is the build "cap": the retuned XP curve is paced
 * so a maxed build lands near the end of the run (idea: compose a build, don't take
 * everything). Leveling stops here; further XP is ignored.
 */
export const MAX_PLAYER_LEVEL = 30;
/**
 * Equipped-power slot budget. Once a player holds this many distinct powers, no more
 * "new-power" offers appear (only power-levels and passives) — you must have a free
 * slot to acquire a new power.
 */
export const MAX_POWER_SLOTS = 5;
/**
 * Passive-augment slot budget (LoL-Swarm model, B3). A build commits to this many
 * DISTINCT passive lines; once full, new passive types stop being offered and you can
 * only LEVEL the ones you already hold (up to each def's `maxStacks`). Weapons use the
 * separate `MAX_POWER_SLOTS` budget. Together: 5 weapon slots + 6 passive slots.
 */
export const MAX_PASSIVE_SLOTS = 6;

// --- Per-power scaling by level (deterministic, data-light) -----------------
/** Damage multiplier from a power's level (level 1 = ×1). */
export function levelDamageScale(level: number): number {
  return 1 + 0.25 * (level - 1);
}
/** Bolt count by level: 1, then +1 every 2 levels (1,1,2,2,3,3). */
export function boltCount(level: number): number {
  return 1 + Math.floor((level - 1) / 2);
}
/**
 * Projectiles per volley for any projectile power at a level: its base
 * `projectileCount` (default 1) plus the same +1-every-2-levels scaling as the bolt.
 */
export function projectileCountFor(power: PowerType, level: number): number {
  return (power.projectileCount ?? 1) + Math.floor((level - 1) / 2);
}
/** Orbit blade count by level (2,3,4,5,6,7 across L1–6). */
export function orbitBladeCount(level: number): number {
  return 2 + (level - 1);
}
/** Radius growth for area powers (aura/nova) by level. */
export function levelRadiusScale(level: number): number {
  return 1 + 0.08 * (level - 1);
}

/** Projectile pool capacity (only a few dozen are alive at once). */
export const PROJECTILE_CAPACITY = 512;
/** XP gem pool capacity. */
export const PICKUP_CAPACITY = 2048;
/**
 * Max discrete hit events surfaced per tick (floating damage numbers). Transient
 * output (not part of hashed world state); bounded so the wire/render stay cheap.
 */
export const HIT_CAPACITY = 192;

// ---------------------------------------------------------------------------
// Upgrades (data-driven level-up catalogue) + XP curve
// ---------------------------------------------------------------------------

export type UpgradeKind = "new-power" | "power-level" | "passive";
/** Passive stat an upgrade modifies (parallel to `PlayerStats`). */
export type PassiveStat =
  | "moveMul"
  | "damageMul"
  | "cooldownMul"
  | "maxHpBonus"
  | "magnetMul"
  | "critChance"
  | "critMul"
  | "lifesteal"
  | "statik"
  | "regenPerSec"
  | "armor"
  | "thorns"
  | "dodgeChance"
  | "projSpeedMul"
  | "areaMul"
  | "bossDmgMul"
  | "executeFrac"
  | "goldFind"
  | "extraProjectiles"
  | "extraPierce"
  | "homing";

/** Upgrade/power rarity. Rarer offers are drawn less often (and are typically stronger). */
export type Rarity = "common" | "rare" | "epic" | "legendary";

/**
 * Draw-weight multiplier applied on top of a def's base `weight`, so rarity biases how
 * often an upgrade appears in an offer. `legendary` is reserved for future strong augments.
 */
export const RARITY_WEIGHT_MULT: Record<Rarity, number> = {
  common: 1,
  rare: 0.5,
  epic: 0.22,
  legendary: 0.08,
};

export interface UpgradeDef {
  readonly id: number;
  readonly name: string;
  readonly desc: string;
  readonly kind: UpgradeKind;
  /** Rarity tier (biases draw weight; surfaced to the client to color the choice card). */
  readonly rarity: Rarity;
  /** new-power / power-level: which power. */
  readonly power?: PowerId;
  /** passive: which stat + signed amount (multipliers are additive deltas). */
  readonly stat?: PassiveStat;
  readonly amount?: number;
  /** Max times this upgrade can be taken (passives). */
  readonly maxStacks: number;
  /** Relative draw weight when generating offers (before the rarity multiplier). */
  readonly weight: number;
}

/** Upgrade id == index into `UPGRADES` (travels in the `levelup`/`choose` msgs). */
export type UpgradeId = number;

export const UPGRADES: readonly UpgradeDef[] = [
  // New powers (each ownable once).
  {
    id: 0,
    name: "Pulse Aura",
    desc: "Gain a damaging aura.",
    kind: "new-power",
    rarity: "rare",
    power: POWER_AURA,
    maxStacks: 1,
    weight: 10,
  },
  {
    id: 1,
    name: "Orbit Blades",
    desc: "Gain orbiting blades.",
    kind: "new-power",
    rarity: "rare",
    power: POWER_ORBIT,
    maxStacks: 1,
    weight: 10,
  },
  {
    id: 2,
    name: "Nova",
    desc: "Gain a periodic burst.",
    kind: "new-power",
    rarity: "epic",
    power: POWER_NOVA,
    maxStacks: 1,
    weight: 10,
  },
  // Power level-ups (only offered if owned & below MAX_POWER_LEVEL).
  {
    id: 3,
    name: "Bolt +",
    desc: "Upgrade auto-bolt (more bolts / damage).",
    kind: "power-level",
    rarity: "common",
    power: POWER_BOLT,
    maxStacks: MAX_POWER_LEVEL,
    weight: 12,
  },
  {
    id: 4,
    name: "Aura +",
    desc: "Upgrade the aura (radius / damage).",
    kind: "power-level",
    rarity: "common",
    power: POWER_AURA,
    maxStacks: MAX_POWER_LEVEL,
    weight: 8,
  },
  {
    id: 5,
    name: "Orbit +",
    desc: "Upgrade orbit blades (count / damage).",
    kind: "power-level",
    rarity: "common",
    power: POWER_ORBIT,
    maxStacks: MAX_POWER_LEVEL,
    weight: 8,
  },
  {
    id: 6,
    name: "Nova +",
    desc: "Upgrade nova (radius / damage).",
    kind: "power-level",
    rarity: "common",
    power: POWER_NOVA,
    maxStacks: MAX_POWER_LEVEL,
    weight: 8,
  },
  // Passive stats.
  {
    id: 7,
    name: "Swift",
    desc: "+8% move speed.",
    kind: "passive",
    rarity: "common",
    stat: "moveMul",
    amount: 0.08,
    maxStacks: 5,
    weight: 8,
  },
  {
    id: 8,
    name: "Vitality",
    desc: "+20 max HP (and heal it).",
    kind: "passive",
    rarity: "common",
    stat: "maxHpBonus",
    amount: 20,
    maxStacks: 6,
    weight: 8,
  },
  {
    id: 9,
    name: "Power",
    desc: "+12% damage.",
    kind: "passive",
    rarity: "rare",
    stat: "damageMul",
    amount: 0.12,
    maxStacks: 6,
    weight: 9,
  },
  {
    id: 10,
    name: "Haste",
    desc: "-6% cooldowns.",
    kind: "passive",
    rarity: "rare",
    stat: "cooldownMul",
    amount: -0.06,
    maxStacks: 5,
    weight: 7,
  },
  {
    id: 11,
    name: "Magnet",
    desc: "+30% pickup radius.",
    kind: "passive",
    rarity: "common",
    stat: "magnetMul",
    amount: 0.3,
    maxStacks: 4,
    weight: 6,
  },
  {
    id: 12,
    name: "Precision",
    desc: "+12% critical strike chance.",
    kind: "passive",
    rarity: "rare",
    stat: "critChance",
    amount: 0.12,
    maxStacks: 5,
    weight: 7,
  },
  {
    id: 13,
    name: "Brutality",
    desc: "+25% critical strike damage.",
    kind: "passive",
    rarity: "epic",
    stat: "critMul",
    amount: 0.25,
    maxStacks: 3,
    weight: 5,
  },
  {
    id: 14,
    name: "Scatter",
    desc: "Gain a short-range shotgun spread.",
    kind: "new-power",
    rarity: "rare",
    power: POWER_SCATTER,
    maxStacks: 1,
    weight: 9,
  },
  {
    id: 15,
    name: "Scatter +",
    desc: "Upgrade scatter (more pellets / damage).",
    kind: "power-level",
    rarity: "common",
    power: POWER_SCATTER,
    maxStacks: MAX_POWER_LEVEL,
    weight: 8,
  },
  {
    id: 16,
    name: "Lance",
    desc: "Gain a piercing rail shot.",
    kind: "new-power",
    rarity: "epic",
    power: POWER_LANCE,
    maxStacks: 1,
    weight: 7,
  },
  {
    id: 17,
    name: "Lance +",
    desc: "Upgrade lance (more pierce / damage).",
    kind: "power-level",
    rarity: "common",
    power: POWER_LANCE,
    maxStacks: MAX_POWER_LEVEL,
    weight: 8,
  },
  {
    id: 18,
    name: "Vampirism",
    desc: "Heal 0.6% of the damage you deal (small, capped sustain).",
    kind: "passive",
    rarity: "epic",
    stat: "lifesteal",
    amount: 0.006,
    maxStacks: 3,
    weight: 5,
  },
  {
    id: 19,
    name: "Statikk Charge",
    desc: "Walking builds static; at full charge your next strike chains lightning between nearby foes (+1 bounce per level).",
    kind: "passive",
    rarity: "legendary",
    stat: "statik",
    amount: 1,
    maxStacks: 5,
    weight: 4,
  },
  {
    id: 20,
    name: "Boomerang",
    desc: "Throw a boomerang that flies out, pierces all, and curves back through them.",
    kind: "new-power",
    rarity: "epic",
    power: POWER_BOOMERANG,
    maxStacks: 1,
    weight: 9,
  },
  {
    id: 21,
    name: "Boomerang +",
    desc: "Upgrade the boomerang (range / damage).",
    kind: "power-level",
    rarity: "common",
    power: POWER_BOOMERANG,
    maxStacks: MAX_POWER_LEVEL,
    weight: 7,
  },
  // --- Augment pool tranche 1: pure passives (item 4) ---
  {
    id: 22,
    name: "Regeneration",
    desc: "Recover +2 HP every 5s.",
    kind: "passive",
    rarity: "common",
    stat: "regenPerSec",
    amount: 0.4,
    maxStacks: 5,
    weight: 8,
  },
  {
    id: 23,
    name: "Plating",
    desc: "Reduce all incoming damage by 6%.",
    kind: "passive",
    rarity: "rare",
    stat: "armor",
    amount: 0.06,
    maxStacks: 6,
    weight: 7,
  },
  {
    id: 24,
    name: "Spikes",
    desc: "Reflect 15% of contact damage back to attackers.",
    kind: "passive",
    rarity: "rare",
    stat: "thorns",
    amount: 0.15,
    maxStacks: 4,
    weight: 6,
  },
  {
    id: 25,
    name: "Evasion",
    desc: "+6% chance to completely dodge a hit.",
    kind: "passive",
    rarity: "rare",
    stat: "dodgeChance",
    amount: 0.06,
    maxStacks: 5,
    weight: 6,
  },
  {
    id: 26,
    name: "Velocity",
    desc: "+15% projectile speed.",
    kind: "passive",
    rarity: "common",
    stat: "projSpeedMul",
    amount: 0.15,
    maxStacks: 4,
    weight: 6,
  },
  {
    id: 27,
    name: "Expansion",
    desc: "+12% area for aura / orbit / nova.",
    kind: "passive",
    rarity: "rare",
    stat: "areaMul",
    amount: 0.12,
    maxStacks: 5,
    weight: 6,
  },
  {
    id: 28,
    name: "Reaper",
    desc: "Instantly slay weakened trash (≤4% HP). Stacks raise the threshold.",
    kind: "passive",
    rarity: "epic",
    stat: "executeFrac",
    amount: 0.04,
    maxStacks: 3,
    weight: 5,
  },
  {
    id: 29,
    name: "Fortune",
    desc: "+25% currency earned from this run.",
    kind: "passive",
    rarity: "common",
    stat: "goldFind",
    amount: 0.25,
    maxStacks: 4,
    weight: 5,
  },
  {
    id: 30,
    name: "Titan",
    desc: "+50 max HP (and heal it).",
    kind: "passive",
    rarity: "rare",
    stat: "maxHpBonus",
    amount: 50,
    maxStacks: 5,
    weight: 6,
  },
  {
    id: 31,
    name: "Overdrive",
    desc: "-8% all cooldowns.",
    kind: "passive",
    rarity: "epic",
    stat: "cooldownMul",
    amount: -0.08,
    maxStacks: 3,
    weight: 5,
  },
  // --- Augment pool tranche 2: power modifiers (item 4) ---
  {
    id: 32,
    name: "Giant Slayer",
    desc: "+25% damage to bosses & mini-bosses (bolts & nova).",
    kind: "passive",
    rarity: "epic",
    stat: "bossDmgMul",
    amount: 0.25,
    maxStacks: 4,
    weight: 5,
  },
  {
    id: 33,
    name: "Multishot",
    desc: "+1 projectile on every volley.",
    kind: "passive",
    rarity: "epic",
    stat: "extraProjectiles",
    amount: 1,
    maxStacks: 2,
    weight: 5,
  },
  {
    id: 34,
    name: "Piercing Rounds",
    desc: "Projectiles pierce +1 extra enemy.",
    kind: "passive",
    rarity: "rare",
    stat: "extraPierce",
    amount: 1,
    maxStacks: 4,
    weight: 6,
  },
  {
    id: 35,
    name: "Seeker",
    desc: "Your projectiles curve toward the nearest enemy.",
    kind: "passive",
    rarity: "legendary",
    stat: "homing",
    amount: 1,
    maxStacks: 1,
    weight: 4,
  },
  // --- Augment pool tranche 3: new powers + more passives (item 4) ---
  {
    id: 36,
    name: "Railgun",
    desc: "A slow, armor-piercing rail round that skewers a line of enemies.",
    kind: "new-power",
    rarity: "epic",
    power: POWER_RAILGUN,
    maxStacks: 1,
    weight: 7,
  },
  {
    id: 37,
    name: "Railgun +",
    desc: "Upgrade the railgun (damage / pierce).",
    kind: "power-level",
    rarity: "common",
    power: POWER_RAILGUN,
    maxStacks: MAX_POWER_LEVEL,
    weight: 7,
  },
  {
    id: 38,
    name: "Flak Cannon",
    desc: "A close-range burst of pellets in a wide arc.",
    kind: "new-power",
    rarity: "rare",
    power: POWER_FLAK,
    maxStacks: 1,
    weight: 8,
  },
  {
    id: 39,
    name: "Flak Cannon +",
    desc: "Upgrade the flak cannon (pellets / damage).",
    kind: "power-level",
    rarity: "common",
    power: POWER_FLAK,
    maxStacks: MAX_POWER_LEVEL,
    weight: 7,
  },
  {
    id: 40,
    name: "Twin Fang",
    desc: "Fire a fast piercing pair of bolts.",
    kind: "new-power",
    rarity: "rare",
    power: POWER_TWINFANG,
    maxStacks: 1,
    weight: 8,
  },
  {
    id: 41,
    name: "Twin Fang +",
    desc: "Upgrade twin fang (damage / count).",
    kind: "power-level",
    rarity: "common",
    power: POWER_TWINFANG,
    maxStacks: MAX_POWER_LEVEL,
    weight: 7,
  },
  {
    id: 42,
    name: "Hailstorm",
    desc: "A rapid wide spray of small shards.",
    kind: "new-power",
    rarity: "rare",
    power: POWER_HAIL,
    maxStacks: 1,
    weight: 8,
  },
  {
    id: 43,
    name: "Hailstorm +",
    desc: "Upgrade hailstorm (shards / damage).",
    kind: "power-level",
    rarity: "common",
    power: POWER_HAIL,
    maxStacks: MAX_POWER_LEVEL,
    weight: 7,
  },
  {
    id: 44,
    name: "Berserker",
    desc: "+18% damage.",
    kind: "passive",
    rarity: "epic",
    stat: "damageMul",
    amount: 0.18,
    maxStacks: 4,
    weight: 6,
  },
  {
    id: 45,
    name: "Fleet Feet",
    desc: "+15% move speed.",
    kind: "passive",
    rarity: "common",
    stat: "moveMul",
    amount: 0.15,
    maxStacks: 4,
    weight: 6,
  },
  {
    id: 46,
    name: "Marksman",
    desc: "+10% crit chance.",
    kind: "passive",
    rarity: "rare",
    stat: "critChance",
    amount: 0.1,
    maxStacks: 4,
    weight: 6,
  },
  {
    id: 47,
    name: "Savagery",
    desc: "+30% crit damage.",
    kind: "passive",
    rarity: "epic",
    stat: "critMul",
    amount: 0.3,
    maxStacks: 3,
    weight: 5,
  },
  {
    id: 48,
    name: "Hardy",
    desc: "+30 max HP (and heal it).",
    kind: "passive",
    rarity: "common",
    stat: "maxHpBonus",
    amount: 30,
    maxStacks: 6,
    weight: 7,
  },
  {
    id: 49,
    name: "Quickdraw",
    desc: "-6% all cooldowns.",
    kind: "passive",
    rarity: "rare",
    stat: "cooldownMul",
    amount: -0.06,
    maxStacks: 5,
    weight: 6,
  },
];

export function upgradeDef(id: UpgradeId): UpgradeDef | undefined {
  return UPGRADES[id];
}

// ---------------------------------------------------------------------------
// Artifacts (cross-session meta items; bought with currency, applied at run start)
// ---------------------------------------------------------------------------

/** Stat modifiers an artifact grants at run start (additive deltas; identity = absent). */
export interface ArtifactMods {
  readonly moveMul?: number;
  readonly damageMul?: number;
  readonly cooldownMul?: number;
  readonly magnetMul?: number;
  readonly maxHpBonus?: number;
  readonly critChance?: number;
  readonly critMul?: number;
  readonly lifesteal?: number;
}

export interface ArtifactDef {
  readonly id: string;
  readonly name: string;
  readonly desc: string;
  /** Emoji/glyph icon for the lobby grid (no art pipeline). */
  readonly icon: string;
  /** Rarity tier (grid accent + sort). */
  readonly rarity: Rarity;
  /** Currency cost to purchase (one-time; persists on the account). */
  readonly cost: number;
  /** Meta-stat unlock requirement (item 9). Absent = available from the start. */
  readonly unlock?: UnlockReq;
  readonly mods: ArtifactMods;
}

/** Meta-progression stats persisted per account (drive unlocks). */
export interface MetaStats {
  readonly totalKills: number;
  readonly bossKills: number;
  readonly bestLoop: number;
}

/** A meta-stat unlock requirement (any subset of thresholds; all must be met). */
export interface UnlockReq {
  readonly kills?: number;
  readonly bossKills?: number;
  readonly loop?: number;
}

/** Whether `meta` satisfies an unlock requirement (item 5 + 9). Undefined req = unlocked. */
export function meetsUnlock(req: UnlockReq | undefined, meta: MetaStats): boolean {
  if (req === undefined) return true;
  if (req.kills !== undefined && meta.totalKills < req.kills) return false;
  if (req.bossKills !== undefined && meta.bossKills < req.bossKills) return false;
  if (req.loop !== undefined && meta.bestLoop < req.loop) return false;
  return true;
}

/** Human-readable unlock requirement (for locked tooltips). "" = no requirement. */
export function unlockText(req: UnlockReq | undefined): string {
  if (req === undefined) return "";
  const parts: string[] = [];
  if (req.kills !== undefined) parts.push(`${req.kills.toLocaleString()} total kills`);
  if (req.bossKills !== undefined)
    parts.push(`${req.bossKills} boss kill${req.bossKills > 1 ? "s" : ""}`);
  if (req.loop !== undefined) parts.push(`reach loop ${req.loop}`);
  return parts.join(" · ");
}

/** Max artifacts a player can equip into a run loadout. */
export const MAX_EQUIPPED_ARTIFACTS = 3;

export const ARTIFACTS: readonly ArtifactDef[] = [
  // Common (no unlock requirement).
  {
    id: "swift-boots",
    name: "Swift Boots",
    icon: "👟",
    rarity: "common",
    desc: "+10% move speed.",
    cost: 150,
    mods: { moveMul: 0.1 },
  },
  {
    id: "vital-plating",
    name: "Vital Plating",
    icon: "🛡️",
    rarity: "common",
    desc: "+40 max HP.",
    cost: 150,
    mods: { maxHpBonus: 40 },
  },
  {
    id: "magnet-coil",
    name: "Magnet Coil",
    icon: "🧲",
    rarity: "common",
    desc: "+50% pickup radius.",
    cost: 100,
    mods: { magnetMul: 0.5 },
  },
  // Rare.
  {
    id: "power-core",
    name: "Power Core",
    icon: "⚙️",
    rarity: "rare",
    desc: "+15% damage.",
    cost: 250,
    mods: { damageMul: 0.15 },
  },
  {
    id: "haste-chip",
    name: "Haste Chip",
    icon: "⏱️",
    rarity: "rare",
    desc: "-10% cooldowns.",
    cost: 250,
    mods: { cooldownMul: -0.1 },
    unlock: { kills: 1500 },
  },
  {
    id: "keen-edge",
    name: "Keen Edge",
    icon: "🗡️",
    rarity: "rare",
    desc: "+10% crit chance.",
    cost: 300,
    mods: { critChance: 0.1 },
    unlock: { kills: 3000 },
  },
  {
    id: "iron-heart",
    name: "Iron Heart",
    icon: "❤️‍🔥",
    rarity: "rare",
    desc: "+80 max HP.",
    cost: 350,
    mods: { maxHpBonus: 80 },
    unlock: { kills: 4000 },
  },
  // Epic.
  {
    id: "berserker-totem",
    name: "Berserker Totem",
    icon: "🔥",
    rarity: "epic",
    desc: "+25% damage.",
    cost: 500,
    mods: { damageMul: 0.25 },
    unlock: { kills: 8000 },
  },
  {
    id: "executioner",
    name: "Executioner",
    icon: "💀",
    rarity: "epic",
    desc: "+25% crit chance, +50% crit damage.",
    cost: 600,
    mods: { critChance: 0.25, critMul: 0.5 },
    unlock: { bossKills: 2 },
  },
  {
    id: "vampire-fang",
    name: "Vampire Fang",
    icon: "🩸",
    rarity: "epic",
    desc: "Heal 0.6% of damage dealt.",
    cost: 550,
    mods: { lifesteal: 0.006 },
    unlock: { kills: 12000 },
  },
  {
    id: "overclock",
    name: "Overclock",
    icon: "⚡",
    rarity: "epic",
    desc: "-18% cooldowns, +8% move.",
    cost: 650,
    mods: { cooldownMul: -0.18, moveMul: 0.08 },
    unlock: { bossKills: 3 },
  },
  {
    id: "relic-of-extraction",
    name: "Relic of Extraction",
    icon: "📦",
    rarity: "epic",
    desc: "+20% damage, +10% move, -10% cooldowns. Earned by extracting (or bought back).",
    cost: 600,
    mods: { damageMul: 0.2, moveMul: 0.1, cooldownMul: -0.1 },
  },
  // Legendary (deep unlocks).
  {
    id: "warlords-banner",
    name: "Warlord's Banner",
    icon: "🚩",
    rarity: "legendary",
    desc: "+35% damage, +60 max HP.",
    cost: 1000,
    mods: { damageMul: 0.35, maxHpBonus: 60 },
    unlock: { loop: 1 },
  },
  {
    id: "chronobreaker",
    name: "Chronobreaker",
    icon: "⌛",
    rarity: "legendary",
    desc: "-25% cooldowns, +12% move.",
    cost: 1100,
    mods: { cooldownMul: -0.25, moveMul: 0.12 },
    unlock: { loop: 2 },
  },
  {
    id: "apex-predator",
    name: "Apex Predator",
    icon: "🦈",
    rarity: "legendary",
    desc: "+30% crit chance, +50% crit damage, 1% lifesteal.",
    cost: 1500,
    mods: { critChance: 0.3, critMul: 0.5, lifesteal: 0.01 },
    unlock: { kills: 40000 },
  },
];

export function artifactById(id: string): ArtifactDef | undefined {
  return ARTIFACTS.find((a) => a.id === id);
}

// ---------------------------------------------------------------------------
// Characters (WIP): a selectable starting kit. One playable; the rest locked.
// Per-character starting power + a stat-mod layer, applied at run start before
// the artifact loadout. New characters are a table edit. (UI_OVERHAUL_PLAN P4.)
// ---------------------------------------------------------------------------

export interface CharacterDef {
  readonly id: string;
  readonly name: string;
  readonly blurb: string;
  /** Locked characters are shown in the lobby but can't be selected (gated by meta). */
  readonly locked: boolean;
  /** Rarity tier (cosmetic + sort); the 3 "legendary" heroes are the hardest unlocks. */
  readonly rarity: Rarity;
  /** Starting power (index into POWERS). */
  readonly startingPower: PowerId;
  /** Stat modifiers applied at run start (same lever as artifacts). */
  readonly stats: ArtifactMods;
  /** Active ability (keypress skill) + its cooldown (ms). */
  readonly ability: AbilityKind;
  readonly abilityCooldownMs: number;
  /** Meta-stat unlock requirement (item 5 + 9). Absent fields = no requirement. */
  readonly unlock?: UnlockReq;
  /** Augment ids this character favors (offer-weight multiplier bias). */
  readonly poolBias?: readonly UpgradeId[];
}

/** Active-ability kinds (item 5). Resolved server-side on the ability input edge. */
export type AbilityKind = "none" | "dash" | "blink" | "shield" | "slam";

/** Ability tuning (distance / radius / damage / shield duration). */
export const ABILITY_DASH_DIST = 6;
/**
 * Dash travel time (ms): the dash covers `ABILITY_DASH_DIST` over this window so it reads
 * as a fast lunge (~5.5× run speed) instead of an instant teleport (B-dash). Blink stays a
 * true teleport (it's a blink).
 */
export const ABILITY_DASH_MS = 180;
const ABILITY_BLINK_DIST = 9;
const ABILITY_SHIELD_MS = 1800;
const ABILITY_SLAM_RADIUS = 5;
const ABILITY_SLAM_DAMAGE = 120;
const ABILITY_SLAM_KNOCKBACK = 7;

export const CHARACTERS: readonly CharacterDef[] = [
  {
    id: "vanguard",
    name: "Vanguard",
    blurb: "Balanced all-rounder. Dash to reposition.",
    locked: false,
    rarity: "common",
    startingPower: STARTING_POWER,
    stats: {},
    ability: "dash",
    abilityCooldownMs: 6000,
  },
  {
    id: "ranger",
    name: "Ranger",
    blurb: "Fast and fragile — Blink out of danger.",
    locked: false,
    rarity: "common",
    startingPower: POWER_SCATTER,
    stats: { moveMul: 0.18, maxHpBonus: -25 },
    ability: "blink",
    abilityCooldownMs: 7000,
    unlock: { kills: 2000 },
    poolBias: [45, 25, 46],
  },
  {
    id: "juggernaut",
    name: "Juggernaut",
    blurb: "Slow tank — Slam to clear space.",
    locked: false,
    rarity: "rare",
    startingPower: STARTING_POWER,
    stats: { maxHpBonus: 80, moveMul: -0.12 },
    ability: "slam",
    abilityCooldownMs: 9000,
    unlock: { kills: 5000 },
    poolBias: [30, 48, 23],
  },
  {
    id: "tinkerer",
    name: "Tinkerer",
    blurb: "Build-around — faster cooldowns, a protective Shield.",
    locked: false,
    rarity: "rare",
    startingPower: POWER_ORBIT,
    stats: { cooldownMul: -0.12, magnetMul: 0.5 },
    ability: "shield",
    abilityCooldownMs: 10000,
    unlock: { bossKills: 1 },
    poolBias: [31, 49, 27],
  },
  {
    id: "pyromancer",
    name: "Pyromancer",
    blurb: "Starts with Nova — Slam for a defensive burst.",
    locked: false,
    rarity: "epic",
    startingPower: POWER_NOVA,
    stats: { damageMul: 0.1 },
    ability: "slam",
    abilityCooldownMs: 8000,
    unlock: { kills: 10000 },
    poolBias: [6, 44, 27],
  },
  {
    id: "stormcaller",
    name: "Stormcaller",
    blurb: "Legendary. Lance-wielder who Blinks through the swarm.",
    locked: false,
    rarity: "legendary",
    startingPower: POWER_LANCE,
    stats: { critChance: 0.12, moveMul: 0.06 },
    ability: "blink",
    abilityCooldownMs: 6000,
    unlock: { loop: 1 },
    poolBias: [19, 35, 46],
  },
  {
    id: "berserker",
    name: "Berserker",
    blurb: "Legendary. Glass cannon — Dash in, melt everything.",
    locked: false,
    rarity: "legendary",
    startingPower: STARTING_POWER,
    stats: { damageMul: 0.3, maxHpBonus: -20, moveMul: 0.05 },
    ability: "dash",
    abilityCooldownMs: 5000,
    unlock: { kills: 30000 },
    poolBias: [44, 18, 33],
  },
  {
    id: "phantom",
    name: "Phantom",
    blurb: "Legendary. Evasive striker — Blink + innate dodge.",
    locked: false,
    rarity: "legendary",
    startingPower: POWER_BOOMERANG,
    stats: { moveMul: 0.2, critChance: 0.08 },
    ability: "blink",
    abilityCooldownMs: 5000,
    unlock: { bossKills: 3 },
    poolBias: [25, 35, 47],
  },
];

export const DEFAULT_CHARACTER = "vanguard";

export function characterById(id: string): CharacterDef | undefined {
  return CHARACTERS.find((c) => c.id === id);
}

/** Seed a (fresh/reset) player with a character's starting power + stats + ability. Run-start only. */
export function applyCharacter(player: PlayerState, id: string): boolean {
  const picked = characterById(id);
  const c = picked !== undefined && !picked.locked ? picked : characterById(DEFAULT_CHARACTER);
  if (c === undefined) return false;
  player.equipped.length = 0;
  player.equipped.push({ power: c.startingPower, level: 1, cooldown: 0 });
  applyStatMods(player, c.stats);
  player.abilityKind = c.ability;
  player.abilityCooldownMs = c.abilityCooldownMs;
  player.abilityCdMs = 0;
  player.shieldMs = 0;
  return true;
}

/** Apply a bag of stat modifiers to a player (at run start). Returns whether it applied. */
export function applyArtifact(player: PlayerState, id: string): boolean {
  const a = artifactById(id);
  if (a === undefined) return false;
  applyStatMods(player, a.mods);
  return true;
}

// --- Stat caps (Phase A2/A3): single source of truth, shared by applyStatMods + applyPassive ---
/** Hard cap on crit chance (Phase A2): no guaranteed crits — crits are a spike, not a baseline. */
export const CRIT_CHANCE_CAP = 0.65;
/** Floor on the cooldown multiplier (Phase A2): max ~2.5× fire-rate, down from 5×. */
export const COOLDOWN_MUL_FLOOR = 0.4;
/** Cap on flat incoming-damage reduction (Phase A3). */
export const ARMOR_CAP = 0.5;
/** Cap on dodge chance (Phase A3). */
export const DODGE_CAP = 0.35;

/** Apply a bag of stat modifiers to a player (shared by artifacts + extraction augments). */
export function applyStatMods(player: PlayerState, m: ArtifactMods): void {
  if (m.moveMul !== undefined) player.stats.moveMul += m.moveMul;
  if (m.damageMul !== undefined) player.stats.damageMul += m.damageMul;
  if (m.cooldownMul !== undefined) {
    player.stats.cooldownMul = Math.max(
      COOLDOWN_MUL_FLOOR,
      player.stats.cooldownMul + m.cooldownMul,
    );
  }
  if (m.magnetMul !== undefined) player.stats.magnetMul += m.magnetMul;
  if (m.critChance !== undefined) {
    player.stats.critChance = Math.max(
      0,
      Math.min(CRIT_CHANCE_CAP, player.stats.critChance + m.critChance),
    );
  }
  if (m.critMul !== undefined) player.stats.critMul += m.critMul;
  if (m.lifesteal !== undefined)
    player.stats.lifesteal = Math.max(0, player.stats.lifesteal + m.lifesteal);
  if (m.maxHpBonus !== undefined) {
    player.maxHp += m.maxHpBonus;
    player.hp = Math.min(player.maxHp, player.hp + m.maxHpBonus);
  }
}

/**
 * XP required to advance FROM `level` to the next (level is 1-based).
 *
 * Tuned so a typical run reaches `MAX_PLAYER_LEVEL` near the end of the timer rather
 * than within the first minute (the old linear `4 + 3*level` capped out almost
 * instantly under swarm-scale kill income). Quadratic growth stretches the back half.
 * These three constants are the single pacing knob — adjust them, not call sites.
 */
const XP_CURVE_BASE = 6;
const XP_CURVE_LINEAR = 10;
const XP_CURVE_QUAD = 3;
export function xpToNextFor(level: number): number {
  return Math.round(XP_CURVE_BASE + XP_CURVE_LINEAR * level + XP_CURVE_QUAD * level * level);
}

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

/** Movement input encoded as a bitmask (WASD / arrows map to these). */
export const Input = {
  None: 0,
  Up: 1 << 0,
  Down: 1 << 1,
  Left: 1 << 2,
  Right: 1 << 3,
  /** Active-ability trigger (character skill). Edge-consumed server-side. */
  Ability: 1 << 4,
} as const;

export type InputMask = number;

/** A single sampled input frame from a client. `seq` enables reconciliation. */
export interface InputCommand {
  readonly seq: number;
  readonly mask: InputMask;
  /** Manual-aim direction (unit-ish vector). Used only when `manualAim` is true. */
  readonly aimX?: number;
  readonly aimY?: number;
  /** True ⇒ powers fire toward (aimX,aimY) instead of auto-targeting the nearest enemy (B4). */
  readonly manualAim?: boolean;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

/** One equipped power: which power, its level, and cooldown remaining (ms). */
export interface EquippedPower {
  power: PowerId;
  level: number;
  cooldown: number;
}

/** Passive multipliers/bonuses accumulated from upgrades (identity = no boost). */
export interface PlayerStats {
  moveMul: number;
  damageMul: number;
  /** Multiplies power cooldownMs (≥0.1; lower = faster). */
  cooldownMul: number;
  magnetMul: number;
  /** Probability [0..1] a discrete hit (bolt/nova) critically strikes. */
  critChance: number;
  /** Damage multiplier applied on a critical strike (≥1). */
  critMul: number;
  /** Fraction of damage dealt healed back to the player (0 = none). */
  lifesteal: number;
  /** Statikk augment level (0 = not acquired). Drives chain-lightning bounce count. */
  statikLevel: number;
  /** HP regenerated per second (0 = none). */
  regenPerSec: number;
  /** Flat incoming-damage reduction fraction (0..0.7). */
  armor: number;
  /** Fraction of contact damage taken reflected back to the attacker (0 = none). */
  thorns: number;
  /** Probability [0..0.5] to completely avoid an incoming hit. */
  dodgeChance: number;
  /** Projectile speed multiplier (≥0.1; 1 = base). */
  projSpeedMul: number;
  /** Area/radius multiplier for aura/orbit/nova (≥0.2; 1 = base). */
  areaMul: number;
  /** Bonus damage multiplier vs bosses/mini-bosses (1 = none). */
  bossDmgMul: number;
  /** Instakill trash whose HP fell to ≤ this fraction of its base max (0 = none). */
  executeFrac: number;
  /** Currency-gain multiplier at run end (1 = base). */
  goldFind: number;
  /** Extra projectiles added to every multi-projectile volley (Multishot). */
  extraProjectiles: number;
  /** Extra pierce added to projectiles (Piercing Rounds; also makes single-hit pierce). */
  extraPierce: number;
  /** >0 ⇒ this player's projectiles home toward the nearest enemy (Seeker). */
  homing: number;
}

/** A pending level-up offer: the new level + 3 upgrade choices. */
export interface LevelUpOffer {
  readonly level: number;
  readonly choices: readonly UpgradeId[];
}

export interface PlayerState {
  readonly id: PlayerId;
  x: number;
  y: number;
  /** Last input sequence applied to this player (for client reconciliation). */
  lastSeq: number;
  /** Current health; 0 ⇒ `downed` (revivable). */
  hp: number;
  maxHp: number;
  /** Downed at 0 HP: can't move/fire until revived (or the run ends). */
  downed: boolean;
  /** Accrued revive time (ms) while an ally is near; 0..reviveMs. */
  reviveProgress: number;
  /** Progression. */
  level: number;
  xp: number;
  xpToNext: number;
  /** This player's individual kill count (credited as last-damager on death). */
  kills: number;
  /** Mutable equipped powers (per-player build). */
  readonly equipped: EquippedPower[];
  /** Passive stat block (mutated by upgrades). */
  readonly stats: PlayerStats;
  /** Times each upgrade id has been taken (for offer applicability). */
  readonly upgradeCounts: Uint8Array;
  /** Queue of pending level-up offers (front = active). Server dispatches these. */
  readonly pendingOffers: LevelUpOffer[];
  /** Transient: true on the tick this player's nova fired (for the client FX). */
  novaPulse: boolean;
  /** Transient: damage this player dealt this tick (for lifesteal); reset each combat tick. */
  dealtThisTick: number;
  /** Statikk static-charge meter [0..STATIK_MAX]; fills as you walk, discharges as a chain. */
  statikCharge: number;
  /** Active-ability kind from the chosen character (item 5; "none" = no ability). */
  abilityKind: AbilityKind;
  /** Ability cooldown length (ms); 0 = none. */
  abilityCooldownMs: number;
  /** Ability cooldown remaining (ms); ticks down each combat tick. */
  abilityCdMs: number;
  /** Active damage-immunity shield remaining (ms; the Shield ability). */
  shieldMs: number;
  /** Manual-aim direction (unit vector); identity (1,0) until the client sends one. */
  aimX: number;
  aimY: number;
  /** True ⇒ this player's projectile powers fire toward (aimX,aimY) (B4). Set from input. */
  manualAim: boolean;
  /** Active dash (B-dash): remaining ms + velocity. While >0 the player travels fast in a
   * committed direction (a lunge), instead of teleporting. 0 = not dashing. */
  dashMs: number;
  dashVx: number;
  dashVy: number;
  /** Ascension endless (B-ascend): mirror of `world.ascension`; lifts this player's
   * per-weapon/per-passive upgrade caps so the build keeps growing to infinity. */
  ascension: boolean;
}

/** Run lifecycle state machine (per room/world). */
export type RunState = "lobby" | "running" | "boss" | "victory" | "defeat";

/** Wire/runtime code for `RunState` (snapshot u8). */
export const RUN_STATE_CODES: readonly RunState[] = [
  "lobby",
  "running",
  "boss",
  "victory",
  "defeat",
];
export const runStateCode = (s: RunState): number => Math.max(0, RUN_STATE_CODES.indexOf(s));
export const runStateFromCode = (c: number): RunState => RUN_STATE_CODES[c] ?? "lobby";

/**
 * Optional in-run **extraction** (hold-the-point) event: a zone opens mid-run; an
 * alive player standing in it activates a horde + a hold timer; surviving the hold
 * extracts for a reward (in-run augment for everyone + a cross-session artifact for
 * logged-in players, committed only on victory — "lose on death"). See ADR-0019.
 */
export type ExtractionPhase = "idle" | "open" | "active" | "success" | "failed";

export const EXTRACTION_PHASE_CODES: readonly ExtractionPhase[] = [
  "idle",
  "open",
  "active",
  "success",
  "failed",
];
export const extractionPhaseCode = (p: ExtractionPhase): number =>
  Math.max(0, EXTRACTION_PHASE_CODES.indexOf(p));
export const extractionPhaseFromCode = (c: number): ExtractionPhase =>
  EXTRACTION_PHASE_CODES[c] ?? "idle";

export interface ExtractionState {
  phase: ExtractionPhase;
  /** Zone center (valid once `phase !== "idle"`). */
  x: number;
  y: number;
  radius: number;
  /** Remaining hold time (ms); counts down only while an alive player is inside. */
  holdRemainingMs: number;
  /** Full hold duration (ms) for the HUD progress fraction. */
  holdTotalMs: number;
}

export interface WorldState {
  tick: Tick;
  /** Seeded PRNG state; advanced only through `nextRandom`. */
  rngState: number;
  readonly players: Map<PlayerId, PlayerState>;
  /** Dense Struct-of-Arrays enemy store (typed arrays, cache-friendly). */
  readonly enemies: EnemyStore;
  /** Dense Struct-of-Arrays projectile store. */
  readonly projectiles: ProjectileStore;
  /** Enemy/boss-fired projectiles (damage players; never rewound). */
  readonly enemyProjectiles: EnemyProjectileStore;
  /** Dense Struct-of-Arrays XP-gem store. */
  readonly pickups: PickupStore;
  /** Transient per-tick discrete damage events (floating numbers). Not hashed. */
  readonly hits: HitStore;
  /** Transient per-tick chain-lightning arc segments (Statikk); cleared each combat tick. */
  readonly arcs: ArcStore;
  /** Active elite telegraphs (dash/charge/poison/arena), rebuilt each tick. */
  readonly telegraphs: TelegraphStore;
  /** Lazy flow-field for enemy steering around walls (Phase D2); null until a map has walls. */
  flow: FlowField | null;
  /** Per-elite behavior state, keyed by enemy id (timers/phase for archetypes). */
  readonly elites: Map<number, EliteState>;
  /** Bullet-hell arena pull-in (active during the boss): center + radius (0 = off). */
  arenaRadius: number;
  /** Cumulative enemies killed (monotonic; surfaced as a metric). */
  kills: number;
  /**
   * Shared team progression (ADR-0021): XP from gems pools here, and the whole
   * team levels together. Each player's per-`PlayerState` level/xp/xpToNext is a
   * mirror of these values; upgrade *choices* stay per-player (own build).
   */
  teamLevel: number;
  teamXp: number;
  teamXpToNext: number;
  /** Run lifecycle. */
  runState: RunState;
  /** Elapsed run time (ms) while `running`. */
  runClockMs: number;
  /** Boss enemy id while in the `boss` phase (0 = none); surfaced for the HUD HP bar. */
  bossId: number;
  bossHp: number;
  bossMaxHp: number;
  /** Boss ranged-attack cooldown remaining (ms); counts down while a boss is alive. */
  bossFireCdMs: number;
  /** Current wave index (0..WAVE_COUNT-1). Authoritative; replaces the old time-band. */
  waveIndex: number;
  /** Wave lifecycle phase. */
  wavePhase: WavePhase;
  /** Trash spawned so far for the current wave (toward its scaled budget). */
  waveSpawnedCount: number;
  /** This wave's scaled budget (total trash to spawn before it can clear). */
  waveBudget: number;
  /** Lull countdown (ms) between a cleared wave and the next. */
  waveLullMs: number;
  /** Straggler safety timer (ms): counts up once the budget is fully spawned. */
  waveStraggleMs: number;
  /** Mini-boss(es) spawned for the current wave (so we spawn them once near the end). */
  waveMinibossSpawned: boolean;
  /**
   * Active spawn **breaches** (Phase C): the directions trash pours in from this wave.
   * The director keeps a small set open (telegraphed, then emitting) and always leaves
   * a gap so the player has an escape lane — replaces the old "uniform ring of edges".
   * Deterministic (seeded); affects enemy spawn positions (hashed via enemy x/y).
   */
  breaches: BreachState[];
  /** True once the final wave has been cleared (gates the boss spawn in stepRun). */
  allWavesCleared: boolean;
  /**
   * Endless loop index (item 1). 0 = the normal run. Incremented each time the squad
   * chooses "Continue" after a boss kill; re-arms the waves and ramps enemy HP/damage.
   */
  loopIndex: number;
  /**
   * Ascension endless (B-ascend): when true, the level cap and per-weapon/per-passive
   * upgrade caps are lifted so the squad keeps leveling + upgrading their committed build
   * to infinity. False = the "keep your build" continue (carry the maxed build, no further
   * upgrades). Set by `continueEndless`.
   */
  ascension: boolean;
  /** Present mini-boss enemy ids (coexist with the swarm; for death-reward detection). */
  readonly minibossIds: Set<number>;
  /** Optional in-run extraction (hold-the-point) event state. */
  readonly extraction: ExtractionState;
}

export class EnemyStore {
  readonly capacity: number;
  count = 0;
  readonly id: Uint16Array;
  readonly type: Uint8Array;
  readonly x: Float32Array;
  readonly y: Float32Array;
  /**
   * Current hit points (server-authoritative; death at <= 0). Float (not int)
   * so fractional per-tick damage (aura DPS) accumulates without truncation.
   */
  readonly hp: Float32Array;
  /** Current shield HP (Phase B2): depleted before `hp`; regenerates if untouched. 0 = none. */
  readonly shieldHp: Float32Array;
  /** Ms since this enemy's shield last took damage (for regen gating). */
  readonly shieldRegenMs: Float32Array;
  /** Facing angle in radians (Phase B3): set from the chase vector; used for backstab. */
  readonly facing: Float32Array;
  /** Scratch buffers for the separation pass (reused, never reallocated). */
  readonly pushX: Float32Array;
  readonly pushY: Float32Array;
  /** Scratch: damage accumulated this tick before the death sweep (reused). */
  readonly dmg: Float32Array;
  /** Scratch: global PlayerId of the last damager this tick (-1 = none); for kill credit. */
  readonly lastOwner: Int32Array;
  private nextId = 1;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.id = new Uint16Array(capacity);
    this.type = new Uint8Array(capacity);
    this.x = new Float32Array(capacity);
    this.y = new Float32Array(capacity);
    this.hp = new Float32Array(capacity);
    this.shieldHp = new Float32Array(capacity);
    this.shieldRegenMs = new Float32Array(capacity);
    this.facing = new Float32Array(capacity);
    this.pushX = new Float32Array(capacity);
    this.pushY = new Float32Array(capacity);
    this.dmg = new Float32Array(capacity);
    this.lastOwner = new Int32Array(capacity);
  }

  /** Append an enemy (hp seeded from its type); returns its index, or -1 if full. */
  spawn(typeId: EnemyTypeId, x: number, y: number): number {
    if (this.count >= this.capacity) return -1;
    const i = this.count;
    this.count += 1;
    // u16 id space wraps at 65535; fine for matching across adjacent snapshots.
    this.id[i] = this.nextId & 0xffff;
    this.nextId = (this.nextId + 1) & 0xffff || 1;
    this.type[i] = typeId & 0xff;
    this.x[i] = x;
    this.y[i] = y;
    const t = enemyType(typeId);
    this.hp[i] = t.hp;
    this.shieldHp[i] = t.shieldHp ?? 0;
    this.shieldRegenMs[i] = 0;
    this.facing[i] = 0;
    return i;
  }

  /** Remove by index (swap-with-last to stay dense). */
  swapRemove(i: number): void {
    const last = this.count - 1;
    if (i < 0 || i > last) return;
    if (i !== last) {
      this.id[i] = this.id[last] ?? 0;
      this.type[i] = this.type[last] ?? 0;
      this.x[i] = this.x[last] ?? 0;
      this.y[i] = this.y[last] ?? 0;
      this.hp[i] = this.hp[last] ?? 0;
      this.shieldHp[i] = this.shieldHp[last] ?? 0;
      this.shieldRegenMs[i] = this.shieldRegenMs[last] ?? 0;
      this.facing[i] = this.facing[last] ?? 0;
    }
    this.count -= 1;
  }

  clear(): void {
    this.count = 0;
  }
}

/**
 * Dense Struct-of-Arrays projectile store. Pooled like enemies (typed arrays,
 * dense swap-remove) so the per-tick combat step allocates nothing.
 */
export class ProjectileStore {
  readonly capacity: number;
  count = 0;
  readonly id: Uint16Array;
  /** Power id that fired this projectile (index into `POWERS`). */
  readonly power: Uint8Array;
  /** Owning player id (sim-only; not sent on the wire). */
  readonly owner: Int32Array;
  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly vx: Float32Array;
  readonly vy: Float32Array;
  /** Time-to-live remaining (seconds). */
  readonly ttl: Float32Array;
  readonly damage: Float32Array;
  /** 1 if this projectile rolled a critical strike at fire time (for hit FX). */
  readonly crit: Uint8Array;
  /** Remaining enemies this projectile can still damage before despawning (0 = despawn on hit). */
  readonly pierce: Uint8Array;
  /** Seconds this projectile has been alive (sim-only; drives boomerang return curve). */
  readonly age: Float32Array;
  /** 1 if this projectile homes toward the nearest enemy (Seeker augment). */
  readonly homing: Uint8Array;
  private nextId = 1;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.id = new Uint16Array(capacity);
    this.power = new Uint8Array(capacity);
    this.owner = new Int32Array(capacity);
    this.x = new Float32Array(capacity);
    this.y = new Float32Array(capacity);
    this.vx = new Float32Array(capacity);
    this.vy = new Float32Array(capacity);
    this.ttl = new Float32Array(capacity);
    this.damage = new Float32Array(capacity);
    this.crit = new Uint8Array(capacity);
    this.pierce = new Uint8Array(capacity);
    this.age = new Float32Array(capacity);
    this.homing = new Uint8Array(capacity);
  }

  spawn(
    powerId: PowerId,
    owner: number,
    x: number,
    y: number,
    vx: number,
    vy: number,
    ttl: number,
    damage: number,
    crit = 0,
    pierce = 0,
    homing = 0,
  ): number {
    if (this.count >= this.capacity) return -1;
    const i = this.count;
    this.count += 1;
    this.id[i] = this.nextId & 0xffff;
    this.nextId = (this.nextId + 1) & 0xffff || 1;
    this.power[i] = powerId & 0xff;
    this.owner[i] = owner;
    this.x[i] = x;
    this.y[i] = y;
    this.vx[i] = vx;
    this.vy[i] = vy;
    this.ttl[i] = ttl;
    this.damage[i] = damage;
    this.crit[i] = crit & 0xff;
    this.pierce[i] = pierce & 0xff;
    this.age[i] = 0;
    this.homing[i] = homing & 0xff;
    return i;
  }

  swapRemove(i: number): void {
    const last = this.count - 1;
    if (i < 0 || i > last) return;
    if (i !== last) {
      this.id[i] = this.id[last] ?? 0;
      this.power[i] = this.power[last] ?? 0;
      this.owner[i] = this.owner[last] ?? 0;
      this.x[i] = this.x[last] ?? 0;
      this.y[i] = this.y[last] ?? 0;
      this.vx[i] = this.vx[last] ?? 0;
      this.vy[i] = this.vy[last] ?? 0;
      this.ttl[i] = this.ttl[last] ?? 0;
      this.damage[i] = this.damage[last] ?? 0;
      this.crit[i] = this.crit[last] ?? 0;
      this.pierce[i] = this.pierce[last] ?? 0;
      this.age[i] = this.age[last] ?? 0;
      this.homing[i] = this.homing[last] ?? 0;
    }
    this.count -= 1;
  }

  clear(): void {
    this.count = 0;
  }
}

/** Capacity for enemy/boss-fired projectiles (few sources, small pool). */
export const ENEMY_PROJECTILE_CAPACITY = 128;

/**
 * Dense SoA store for projectiles fired *by enemies at players* (currently the
 * boss). Separate from `ProjectileStore` so the player-projectile hot loop stays
 * untouched and these are never rewound. Damages players on contact.
 */
export class EnemyProjectileStore {
  readonly capacity: number;
  count = 0;
  readonly id: Uint16Array;
  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly vx: Float32Array;
  readonly vy: Float32Array;
  readonly ttl: Float32Array;
  readonly damage: Float32Array;
  private nextId = 1;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.id = new Uint16Array(capacity);
    this.x = new Float32Array(capacity);
    this.y = new Float32Array(capacity);
    this.vx = new Float32Array(capacity);
    this.vy = new Float32Array(capacity);
    this.ttl = new Float32Array(capacity);
    this.damage = new Float32Array(capacity);
  }

  spawn(x: number, y: number, vx: number, vy: number, ttl: number, damage: number): number {
    if (this.count >= this.capacity) return -1;
    const i = this.count;
    this.count += 1;
    this.id[i] = this.nextId & 0xffff;
    this.nextId = (this.nextId + 1) & 0xffff || 1;
    this.x[i] = x;
    this.y[i] = y;
    this.vx[i] = vx;
    this.vy[i] = vy;
    this.ttl[i] = ttl;
    this.damage[i] = damage;
    return i;
  }

  swapRemove(i: number): void {
    const last = this.count - 1;
    if (i < 0 || i > last) return;
    if (i !== last) {
      this.id[i] = this.id[last] ?? 0;
      this.x[i] = this.x[last] ?? 0;
      this.y[i] = this.y[last] ?? 0;
      this.vx[i] = this.vx[last] ?? 0;
      this.vy[i] = this.vy[last] ?? 0;
      this.ttl[i] = this.ttl[last] ?? 0;
      this.damage[i] = this.damage[last] ?? 0;
    }
    this.count -= 1;
  }

  clear(): void {
    this.count = 0;
  }
}

/**
 * Dense Struct-of-Arrays XP-gem store. Gems drop where enemies die; players
 * collect them (with a magnet) to gain XP. Pooled like the other stores.
 */
export class PickupStore {
  readonly capacity: number;
  count = 0;
  readonly id: Uint16Array;
  /** Visual tier (0 = small, 1 = big); maps to color/value. */
  readonly tier: Uint8Array;
  readonly x: Float32Array;
  readonly y: Float32Array;
  /** XP value granted on collection. */
  readonly value: Float32Array;
  /** Lifetime remaining (seconds). */
  readonly ttl: Float32Array;
  private nextId = 1;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.id = new Uint16Array(capacity);
    this.tier = new Uint8Array(capacity);
    this.x = new Float32Array(capacity);
    this.y = new Float32Array(capacity);
    this.value = new Float32Array(capacity);
    this.ttl = new Float32Array(capacity);
  }

  spawn(tier: number, x: number, y: number, value: number, ttl: number): number {
    if (this.count >= this.capacity) return -1;
    const i = this.count;
    this.count += 1;
    this.id[i] = this.nextId & 0xffff;
    this.nextId = (this.nextId + 1) & 0xffff || 1;
    this.tier[i] = tier & 0xff;
    this.x[i] = x;
    this.y[i] = y;
    this.value[i] = value;
    this.ttl[i] = ttl;
    return i;
  }

  swapRemove(i: number): void {
    const last = this.count - 1;
    if (i < 0 || i > last) return;
    if (i !== last) {
      this.id[i] = this.id[last] ?? 0;
      this.tier[i] = this.tier[last] ?? 0;
      this.x[i] = this.x[last] ?? 0;
      this.y[i] = this.y[last] ?? 0;
      this.value[i] = this.value[last] ?? 0;
      this.ttl[i] = this.ttl[last] ?? 0;
    }
    this.count -= 1;
  }

  clear(): void {
    this.count = 0;
  }
}

/**
 * Transient per-tick log of discrete damage instances (bolt/nova impacts) for
 * client-side floating damage numbers. Append-only within a tick, cleared at the
 * start of each `stepCombat`. NOT part of the hashed world state — it is ephemeral
 * output, like `CombatResult`. Bounded by `HIT_CAPACITY` (excess hits are dropped).
 */
export class HitStore {
  readonly capacity: number;
  count = 0;
  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly amount: Float32Array;
  /** 1 = critical strike. */
  readonly crit: Uint8Array;
  /** Global PlayerId of the source player (-1 = none); remapped to slot on the wire. */
  readonly owner: Int32Array;
  /** Enemy id this damage was dealt to (0 = none/player-target). Lets the client
   * anchor the floating number to the enemy and merge repeated hits (item 4). */
  readonly enemyId: Uint16Array;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.x = new Float32Array(capacity);
    this.y = new Float32Array(capacity);
    this.amount = new Float32Array(capacity);
    this.crit = new Uint8Array(capacity);
    this.owner = new Int32Array(capacity);
    this.enemyId = new Uint16Array(capacity);
  }

  push(x: number, y: number, amount: number, crit: number, owner = -1, enemyId = 0): void {
    if (this.count >= this.capacity) return;
    const i = this.count;
    this.count += 1;
    this.x[i] = x;
    this.y[i] = y;
    this.amount[i] = amount;
    this.crit[i] = crit & 0xff;
    this.owner[i] = owner;
    this.enemyId[i] = enemyId & 0xffff;
  }

  clear(): void {
    this.count = 0;
  }
}

/** Max chain-lightning arc segments broadcast per tick (transient, not hashed). */
export const ARC_CAPACITY = 64;

/**
 * Transient per-tick log of chain-lightning arc segments (Statikk discharge) for
 * client-side bolt rendering. Cleared at the start of each `stepCombat`, like the
 * HitStore. Each entry is a line from (x1,y1) to (x2,y2). NOT hashed.
 */
export class ArcStore {
  readonly capacity: number;
  count = 0;
  readonly x1: Float32Array;
  readonly y1: Float32Array;
  readonly x2: Float32Array;
  readonly y2: Float32Array;
  /** Source player (for owner color); -1 = none. */
  readonly owner: Int32Array;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.x1 = new Float32Array(capacity);
    this.y1 = new Float32Array(capacity);
    this.x2 = new Float32Array(capacity);
    this.y2 = new Float32Array(capacity);
    this.owner = new Int32Array(capacity);
  }

  push(x1: number, y1: number, x2: number, y2: number, owner = -1): void {
    if (this.count >= this.capacity) return;
    const i = this.count;
    this.count += 1;
    this.x1[i] = x1;
    this.y1[i] = y1;
    this.x2[i] = x2;
    this.y2[i] = y2;
    this.owner[i] = owner;
  }

  clear(): void {
    this.count = 0;
  }
}

/** Telegraph kinds broadcast to clients so they can draw the wind-up/danger zone. */
export const TELE_DASH = 0; // rectangle from (x,y)→(x2,y2), width=radius; progress fills it
export const TELE_CIRCLE = 1; // expanding/charging circle at (x,y) radius; progress 0..1
export const TELE_POISON = 2; // lingering poison disc at (x,y) radius (active the whole time)
export const TELE_ARENA = 3; // bullet-hell arena ring at (x,y) radius (soft wall)
export const TELE_BREACH = 4; // spawn breach at the arena edge (x,y); progress = wind-up 0..1 (Phase C)

/** Max active telegraphs broadcast per tick. */
export const TELEGRAPH_CAPACITY = 64;

/**
 * A spawn breach (Phase C): trash pours in from the arena edge at `angle`. A breach
 * first **telegraphs** for `windupMs` (a warning marker, no spawns yet), then emits for
 * `lifeMs`, then closes; the director opens a fresh one elsewhere. Kept few + never all
 * around you, so kiting away from a breach toward an open lane is always possible.
 */
export interface BreachState {
  /** Direction from arena center (radians); the edge spawn point is the ray→square hit. */
  angle: number;
  /** Remaining wind-up before this breach starts emitting (ms). */
  windupMs: number;
  /** Remaining active lifetime once emitting (ms). */
  lifeMs: number;
}

/** Breach tuning (Phase C). */
const BREACH_WINDUP_MS = 900; // telegraph warning before a breach opens
const BREACH_LIFE_MS = 7000; // how long a breach emits before relocating
const BREACH_EDGE_SPREAD = 0.22; // tangential jitter as a fraction of half-extent
const BREACH_MIN_SEPARATION = 1.4; // radians: keep breaches apart so a lane stays open
const BREACH_PLAYER_AVOID = 12; // world units: don't open a breach this close to a player

/**
 * Active elite telegraphs (dash rectangles, charge circles, poison zones, the
 * boss arena ring). Unlike HitStore these PERSIST across ticks (rebuilt each tick
 * by `stepEliteBehaviors` from elite state) and are broadcast every snapshot so
 * clients can render the wind-up + danger area. Not hashed (presentation-derived,
 * but deterministic from elite state).
 */
export class TelegraphStore {
  readonly capacity: number;
  count = 0;
  readonly kind: Uint8Array;
  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly x2: Float32Array;
  readonly y2: Float32Array;
  readonly radius: Float32Array;
  /** Wind-up progress 0..1 (1 = firing/active). */
  readonly progress: Float32Array;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.kind = new Uint8Array(capacity);
    this.x = new Float32Array(capacity);
    this.y = new Float32Array(capacity);
    this.x2 = new Float32Array(capacity);
    this.y2 = new Float32Array(capacity);
    this.radius = new Float32Array(capacity);
    this.progress = new Float32Array(capacity);
  }

  push(
    kind: number,
    x: number,
    y: number,
    x2: number,
    y2: number,
    radius: number,
    progress: number,
  ): void {
    if (this.count >= this.capacity) return;
    const i = this.count;
    this.count += 1;
    this.kind[i] = kind & 0xff;
    this.x[i] = x;
    this.y[i] = y;
    this.x2[i] = x2;
    this.y2[i] = y2;
    this.radius[i] = radius;
    this.progress[i] = progress;
  }

  clear(): void {
    this.count = 0;
  }
}

export function createPlayer(
  id: PlayerId,
  x = 0,
  y = 0,
  maxHp = DEFAULT_WORLD_CONFIG.playerMaxHp,
): PlayerState {
  return {
    id,
    x,
    y,
    lastSeq: 0,
    hp: maxHp,
    maxHp,
    downed: false,
    reviveProgress: 0,
    level: 1,
    xp: 0,
    xpToNext: xpToNextFor(1),
    kills: 0,
    equipped: [{ power: STARTING_POWER, level: 1, cooldown: 0 }],
    stats: {
      moveMul: 1,
      damageMul: 1,
      cooldownMul: 1,
      magnetMul: 1,
      critChance: 0,
      critMul: 1.75,
      lifesteal: 0,
      statikLevel: 0,
      regenPerSec: 0,
      armor: 0,
      thorns: 0,
      dodgeChance: 0,
      projSpeedMul: 1,
      areaMul: 1,
      bossDmgMul: 1,
      executeFrac: 0,
      goldFind: 1,
      extraProjectiles: 0,
      extraPierce: 0,
      homing: 0,
    },
    upgradeCounts: new Uint8Array(UPGRADES.length),
    pendingOffers: [],
    novaPulse: false,
    dealtThisTick: 0,
    statikCharge: 0,
    abilityKind: "none",
    abilityCooldownMs: 0,
    abilityCdMs: 0,
    shieldMs: 0,
    aimX: 1,
    aimY: 0,
    manualAim: false,
    dashMs: 0,
    dashVx: 0,
    dashVy: 0,
    ascension: false,
  };
}

/**
 * Copy every run-state field from `src` onto `dst`, preserving `dst.id` (and its
 * own collections' identity). Used by the server to let a reconnecting account
 * adopt its previous in-room player slot (ADR-0022) without re-keying the
 * `readonly` id. `dst` keeps its `lastSeq` (belongs to the new connection's
 * input stream); everything else (position, hp, build, progression, offers) is
 * taken from the orphaned `src`.
 */
export function adoptPlayerRunState(dst: PlayerState, src: PlayerState): void {
  dst.x = src.x;
  dst.y = src.y;
  dst.hp = src.hp;
  dst.maxHp = src.maxHp;
  dst.downed = src.downed;
  dst.reviveProgress = src.reviveProgress;
  dst.level = src.level;
  dst.xp = src.xp;
  dst.xpToNext = src.xpToNext;
  dst.kills = src.kills;
  dst.novaPulse = src.novaPulse;
  dst.equipped.length = 0;
  for (const eq of src.equipped) dst.equipped.push({ ...eq });
  dst.stats.moveMul = src.stats.moveMul;
  dst.stats.damageMul = src.stats.damageMul;
  dst.stats.cooldownMul = src.stats.cooldownMul;
  dst.stats.magnetMul = src.stats.magnetMul;
  dst.stats.critChance = src.stats.critChance;
  dst.stats.critMul = src.stats.critMul;
  dst.stats.lifesteal = src.stats.lifesteal;
  dst.stats.statikLevel = src.stats.statikLevel;
  dst.statikCharge = src.statikCharge;
  dst.upgradeCounts.set(src.upgradeCounts);
  dst.pendingOffers.length = 0;
  for (const o of src.pendingOffers) dst.pendingOffers.push(o);
}

export function createWorld(seed = 0x9e3779b9, enemyCapacity = 1024): WorldState {
  return {
    tick: asTick(0),
    rngState: seed >>> 0,
    players: new Map(),
    enemies: new EnemyStore(enemyCapacity),
    projectiles: new ProjectileStore(PROJECTILE_CAPACITY),
    enemyProjectiles: new EnemyProjectileStore(ENEMY_PROJECTILE_CAPACITY),
    pickups: new PickupStore(PICKUP_CAPACITY),
    hits: new HitStore(HIT_CAPACITY),
    arcs: new ArcStore(ARC_CAPACITY),
    telegraphs: new TelegraphStore(TELEGRAPH_CAPACITY),
    flow: null,
    elites: new Map<number, EliteState>(),
    arenaRadius: 0,
    kills: 0,
    teamLevel: 1,
    teamXp: 0,
    teamXpToNext: xpToNextFor(1),
    runState: "lobby",
    runClockMs: 0,
    bossId: 0,
    bossHp: 0,
    bossMaxHp: 0,
    bossFireCdMs: 0,
    waveIndex: 0,
    wavePhase: "spawning",
    waveSpawnedCount: 0,
    waveBudget: 0,
    waveLullMs: 0,
    waveStraggleMs: 0,
    waveMinibossSpawned: false,
    allWavesCleared: false,
    breaches: [],
    loopIndex: 0,
    ascension: false,
    minibossIds: new Set<number>(),
    extraction: freshExtraction(),
  };
}

/** A reset extraction state (closed/idle). */
function freshExtraction(): ExtractionState {
  return { phase: "idle", x: 0, y: 0, radius: 0, holdRemainingMs: 0, holdTotalMs: 0 };
}

// ---------------------------------------------------------------------------
// Seeded PRNG (mulberry32) — deterministic across machines
// ---------------------------------------------------------------------------

/** Advance `state` and return [nextState, value-in-[0,1)]. Pure. */
export function nextRandom(state: number): readonly [number, number] {
  let t = (state + 0x6d2b79f5) >>> 0;
  const next = t;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  const value = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  return [next, value];
}

// ---------------------------------------------------------------------------
// Movement
// ---------------------------------------------------------------------------

/** Decode an input mask into a normalized direction vector. */
export function directionFromMask(mask: InputMask): readonly [number, number] {
  let dx = 0;
  let dy = 0;
  if ((mask & Input.Left) !== 0) dx -= 1;
  if ((mask & Input.Right) !== 0) dx += 1;
  // Screen-up should move "north" in world space.
  if ((mask & Input.Up) !== 0) dy -= 1;
  if ((mask & Input.Down) !== 0) dy += 1;
  if (dx === 0 && dy === 0) return [0, 0];
  const inv = 1 / Math.sqrt(dx * dx + dy * dy);
  return [dx * inv, dy * inv];
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

// --- Phase D: deterministic circle-vs-AABB wall collision (shared client + server) ---
/** Scratch result for `resolveWalls` (avoids per-call allocation in the hot loop). */
const _wallOut = { x: 0, y: 0 };
/**
 * Push a circle of radius `r` at (x,y) out of any wall AABB it overlaps. Returns the
 * corrected position (via the shared `_wallOut` scratch — copy out before reuse). Pure +
 * deterministic; iterates the (small) wall list. Used for players, enemies, and abilities.
 */
function resolveWalls(
  x: number,
  y: number,
  r: number,
  walls: readonly Wall[],
): { x: number; y: number } {
  for (const w of walls) {
    const minX = w.cx - w.hx;
    const maxX = w.cx + w.hx;
    const minY = w.cy - w.hy;
    const maxY = w.cy + w.hy;
    // Closest point on the box to the circle center.
    const qx = x < minX ? minX : x > maxX ? maxX : x;
    const qy = y < minY ? minY : y > maxY ? maxY : y;
    const dx = x - qx;
    const dy = y - qy;
    const d2 = dx * dx + dy * dy;
    if (d2 >= r * r) continue; // no overlap
    if (d2 > 1e-9) {
      const d = Math.sqrt(d2);
      x = qx + (dx / d) * r;
      y = qy + (dy / d) * r;
    } else {
      // Center is inside the box: eject along the least-penetration axis.
      const pl = x - minX;
      const pr = maxX - x;
      const pb = y - minY;
      const pt = maxY - y;
      const mX = pl < pr ? pl : pr;
      const mY = pb < pt ? pb : pt;
      if (mX < mY) x = pl < pr ? minX - r : maxX + r;
      else y = pb < pt ? minY - r : maxY + r;
    }
  }
  _wallOut.x = x;
  _wallOut.y = y;
  return _wallOut;
}

/** True if point (x,y) is inside any wall AABB (projectile blocking). */
export function pointInWalls(x: number, y: number, walls: readonly Wall[]): boolean {
  for (const w of walls) {
    if (x >= w.cx - w.hx && x <= w.cx + w.hx && y >= w.cy - w.hy && y <= w.cy + w.hy) return true;
  }
  return false;
}

/** A 2D position. Used for prediction/reconciliation without a full PlayerState. */
export interface Vec2 {
  x: number;
  y: number;
}

/**
 * Pure movement integration: advance `(x,y)` by one input `mask` for `dt`.
 * The single source of truth for movement, shared by `stepPlayer` (server) and
 * client-side prediction/replay. Deterministic.
 */
export function integrate(
  x: number,
  y: number,
  mask: InputMask,
  cfg: WorldConfig,
  dtSeconds: number,
): Vec2 {
  const [dx, dy] = directionFromMask(mask);
  const dist = cfg.playerSpeed * dtSeconds;
  const half = cfg.arenaHalfSize;
  let nx = clamp(x + dx * dist, -half, half);
  let ny = clamp(y + dy * dist, -half, half);
  if (cfg.walls.length > 0) {
    const r = resolveWalls(nx, ny, cfg.playerRadius, cfg.walls);
    nx = r.x;
    ny = r.y;
  }
  return { x: nx, y: ny };
}

/** Statikk augment (idea 7): static charge fills as you walk; a full meter
 * discharges as chain lightning between nearby enemies. */
export const STATIK_MAX = 100;
/** Charge gained per world-unit walked (≈ a few seconds of running to fill). */
const STATIK_PER_UNIT = 7;
/** Max distance a bolt can jump to the next enemy. */
const STATIK_JUMP_RANGE = 4.5;
/** Base damage per chain hit (scaled by damageMul + a small per-level bonus). */
const STATIK_BASE_DAMAGE = 14;

/**
 * Apply one input command to one player for `dt`. Mutates `player` in place
 * (the caller owns the state object). Deterministic.
 */
export function stepPlayer(
  player: PlayerState,
  cmd: InputCommand,
  cfg: WorldConfig,
  dtSeconds: number = SIM_DT_S,
): void {
  player.lastSeq = cmd.seq;
  // Latch the manual-aim direction from this command (B4). Aim is input state, so it
  // is applied here regardless of `downed` (a downed player simply doesn't fire).
  if (cmd.manualAim !== undefined) player.manualAim = cmd.manualAim;
  if (cmd.manualAim === true && cmd.aimX !== undefined && cmd.aimY !== undefined) {
    const len = Math.hypot(cmd.aimX, cmd.aimY);
    if (len > 1e-6) {
      player.aimX = cmd.aimX / len;
      player.aimY = cmd.aimY / len;
    }
  }
  if (player.downed) return; // downed players can't move until revived
  // Active dash (B-dash): travel fast along the committed dash velocity (overriding
  // directional input) until the window elapses, then fall back to normal movement.
  let next: Vec2;
  if (player.dashMs > 0) {
    const half = cfg.arenaHalfSize;
    // Clamp the final partial tick to the remaining dash time so the dash covers
    // exactly ABILITY_DASH_DIST regardless of how dt divides the window.
    const dashDt = Math.min(dtSeconds, player.dashMs / 1000);
    next = {
      x: clamp(player.x + player.dashVx * dashDt, -half, half),
      y: clamp(player.y + player.dashVy * dashDt, -half, half),
    };
    if (cfg.walls.length > 0) {
      const r = resolveWalls(next.x, next.y, cfg.playerRadius, cfg.walls);
      next = { x: r.x, y: r.y };
    }
    player.dashMs = Math.max(0, player.dashMs - dashDt * 1000);
  } else {
    const speed = cfg.playerSpeed * player.stats.moveMul;
    next = integrate(player.x, player.y, cmd.mask, { ...cfg, playerSpeed: speed }, dtSeconds);
  }
  // Statikk: walking builds static charge (idea 7). Accrue by distance moved.
  if (player.stats.statikLevel > 0) {
    const moved = Math.hypot(next.x - player.x, next.y - player.y);
    player.statikCharge = Math.min(STATIK_MAX, player.statikCharge + moved * STATIK_PER_UNIT);
  }
  player.x = next.x;
  player.y = next.y;
}

/**
 * Resolve a player's active ability (item 5) on the ability input edge. Server-authoritative
 * (not predicted): dash/blink reposition, shield grants brief immunity, slam clears space.
 * Direction = current movement input, else toward the nearest enemy, else +x. Returns whether
 * it fired. No-op while downed, on cooldown, or with no ability.
 */
export function triggerAbility(
  world: WorldState,
  player: PlayerState,
  mask: InputMask,
  cfg: WorldConfig = DEFAULT_WORLD_CONFIG,
): boolean {
  if ((mask & Input.Ability) === 0) return false;
  if (player.downed || player.abilityCdMs > 0 || player.abilityKind === "none") return false;
  const half = cfg.arenaHalfSize;
  // Aim: movement direction, else toward the nearest enemy, else +x.
  let [fx, fy] = directionFromMask(mask);
  if (fx === 0 && fy === 0) {
    const t = nearestEnemy(world.enemies, player.x, player.y, 999);
    if (t >= 0) {
      const ndx = (world.enemies.x[t] ?? 0) - player.x;
      const ndy = (world.enemies.y[t] ?? 0) - player.y;
      const nl = Math.hypot(ndx, ndy) || 1;
      fx = ndx / nl;
      fy = ndy / nl;
    } else {
      fx = 1;
      fy = 0;
    }
  }
  switch (player.abilityKind) {
    case "dash": {
      // Committed lunge: travel fast in `(fx,fy)` over ABILITY_DASH_MS (stepPlayer
      // integrates dashVx/dashVy each tick) — no teleport (B-dash).
      const speed = ABILITY_DASH_DIST / (ABILITY_DASH_MS / 1000);
      player.dashVx = fx * speed;
      player.dashVy = fy * speed;
      player.dashMs = ABILITY_DASH_MS;
      break;
    }
    case "blink": {
      let bx = clampToArena(player.x + fx * ABILITY_BLINK_DIST, half);
      let by = clampToArena(player.y + fy * ABILITY_BLINK_DIST, half);
      if (cfg.walls.length > 0) {
        const r = resolveWalls(bx, by, cfg.playerRadius, cfg.walls);
        bx = r.x;
        by = r.y;
      }
      player.x = bx;
      player.y = by;
      break;
    }
    case "shield":
      player.shieldMs = ABILITY_SHIELD_MS;
      break;
    case "slam": {
      const e = world.enemies;
      const r2 = ABILITY_SLAM_RADIUS * ABILITY_SLAM_RADIUS;
      for (let j = 0; j < e.count; j += 1) {
        const dx = (e.x[j] ?? 0) - player.x;
        const dy = (e.y[j] ?? 0) - player.y;
        const d2 = dx * dx + dy * dy;
        if (d2 <= r2) {
          e.dmg[j] = (e.dmg[j] ?? 0) + ABILITY_SLAM_DAMAGE;
          e.lastOwner[j] = player.id as number;
          const d = Math.sqrt(d2) || 1;
          e.x[j] = clampToArena((e.x[j] ?? 0) + (dx / d) * ABILITY_SLAM_KNOCKBACK, half);
          e.y[j] = clampToArena((e.y[j] ?? 0) + (dy / d) * ABILITY_SLAM_KNOCKBACK, half);
        }
      }
      break;
    }
    default:
      break;
  }
  player.abilityCdMs = player.abilityCooldownMs;
  return true;
}

/**
 * Replay a sequence of inputs from a starting position (client reconciliation:
 * re-apply not-yet-acknowledged inputs on top of the authoritative state).
 * Each command may carry its own `dtSeconds`; defaults to the fixed step.
 */
export function replay(
  start: Vec2,
  cmds: Iterable<{ readonly mask: InputMask; readonly dtSeconds?: number }>,
  cfg: WorldConfig = DEFAULT_WORLD_CONFIG,
): Vec2 {
  let pos: Vec2 = { x: start.x, y: start.y };
  for (const cmd of cmds) {
    pos = integrate(pos.x, pos.y, cmd.mask, cfg, cmd.dtSeconds ?? SIM_DT_S);
  }
  return pos;
}

/**
 * Advance the whole world by one fixed tick given the latest input per player.
 * Players without input this tick simply hold position.
 */
export function step(
  world: WorldState,
  inputs: ReadonlyMap<PlayerId, InputCommand>,
  cfg: WorldConfig = DEFAULT_WORLD_CONFIG,
): void {
  for (const player of world.players.values()) {
    const cmd = inputs.get(player.id);
    if (cmd !== undefined) {
      stepPlayer(player, cmd, cfg);
    }
  }
  world.tick = asTick(world.tick + 1);
}

// ---------------------------------------------------------------------------
// Fixed-step accumulator (owned by the worker loop)
// ---------------------------------------------------------------------------

export interface FixedStep {
  /**
   * Feed elapsed wall-clock ms; returns how many fixed steps to run now and
   * the interpolation `alpha` in [0,1) for rendering between steps.
   */
  advance(elapsedMs: number): { steps: number; alpha: number };
}

export function createFixedStep(dtMs: number = SIM_DT_MS, maxStepsPerFrame = 5): FixedStep {
  let acc = 0;
  return {
    advance(elapsedMs: number) {
      acc += elapsedMs;
      let steps = 0;
      while (acc >= dtMs && steps < maxStepsPerFrame) {
        acc -= dtMs;
        steps += 1;
      }
      // If we are badly behind (e.g. tab was backgrounded), drop the backlog
      // instead of spiraling.
      if (acc > dtMs * maxStepsPerFrame) {
        acc = 0;
      }
      return { steps, alpha: acc / dtMs };
    },
  };
}

// ---------------------------------------------------------------------------
// Enemy simulation: spatial grid + chase/contact-stop/separation + spawning
// ---------------------------------------------------------------------------

/** A dense position source the spatial grid can bucket (live store or history frame). */
export interface GridSource {
  readonly count: number;
  readonly x: ArrayLike<number>;
  readonly y: ArrayLike<number>;
}

/**
 * Uniform spatial hash grid over the arena, using a linked-list bucketing
 * (`head`/`next` typed arrays) so building it each tick is allocation-free.
 * Turns O(n²) neighbor queries into ~O(n) for separation (and Phase-5 hits).
 */
export class SpatialGrid {
  private readonly cell: number;
  private readonly cols: number;
  private readonly half: number;
  private readonly head: Int32Array;
  private readonly next: Int32Array;

  constructor(arenaHalfSize: number, cellSize: number, capacity: number) {
    this.cell = cellSize;
    this.half = arenaHalfSize;
    this.cols = Math.max(1, Math.ceil((arenaHalfSize * 2) / cellSize));
    this.head = new Int32Array(this.cols * this.cols);
    this.next = new Int32Array(capacity);
  }

  private cellIndex(x: number, y: number): number {
    const c = this.cols;
    const cx = Math.min(c - 1, Math.max(0, Math.floor((x + this.half) / this.cell)));
    const cy = Math.min(c - 1, Math.max(0, Math.floor((y + this.half) / this.cell)));
    return cy * c + cx;
  }

  /**
   * Rebuild from a position source (dense indices `[0, count)`). The live
   * `EnemyStore` and a historical `{count,x,y}` frame both satisfy this.
   */
  build(store: GridSource): void {
    this.head.fill(-1);
    const n = store.count;
    const x = store.x;
    const y = store.y;
    for (let i = 0; i < n; i += 1) {
      const idx = this.cellIndex(x[i] ?? 0, y[i] ?? 0);
      this.next[i] = this.head[idx] ?? -1;
      this.head[idx] = i;
    }
  }

  /** Invoke `cb(j)` for every enemy index in the 3×3 cells around (x,y). */
  forEachNeighbor(x: number, y: number, cb: (j: number) => void): void {
    const c = this.cols;
    const cx = Math.min(c - 1, Math.max(0, Math.floor((x + this.half) / this.cell)));
    const cy = Math.min(c - 1, Math.max(0, Math.floor((y + this.half) / this.cell)));
    for (let gy = Math.max(0, cy - 1); gy <= Math.min(c - 1, cy + 1); gy += 1) {
      for (let gx = Math.max(0, cx - 1); gx <= Math.min(c - 1, cx + 1); gx += 1) {
        let j = this.head[gy * c + gx] ?? -1;
        while (j !== -1) {
          cb(j);
          j = this.next[j] ?? -1;
        }
      }
    }
  }
}

/** Flow-field nav cell size (world units) — coarse grid for enemy pathing around walls. */
const FLOW_CELL = 2;
/** Recompute the flow field this often (ticks) — tracks players without per-tick cost. */
const FLOW_REBUILD_TICKS = 6;

/**
 * Coarse flow-field for enemy steering around walls (Phase D2). A multi-source BFS
 * from every living player's cell over walkable cells yields a distance field; each
 * cell's flow direction points down the steepest distance gradient (8-neighbour), so
 * enemies route around obstacles instead of piling on them. Deterministic, allocation-
 * free after construction; O(cells) per rebuild (≈900 cells) every few ticks.
 */
export class FlowField {
  readonly cols: number;
  private readonly cell: number;
  private readonly half: number;
  private readonly blocked: Uint8Array;
  private readonly dist: Float32Array;
  private readonly dirX: Float32Array;
  private readonly dirY: Float32Array;
  private readonly queue: Int32Array;
  private blockedBuilt = false;
  hasField = false;

  constructor(arenaHalfSize: number, cell: number = FLOW_CELL) {
    this.cell = cell;
    this.half = arenaHalfSize;
    this.cols = Math.max(1, Math.ceil((arenaHalfSize * 2) / cell));
    const n = this.cols * this.cols;
    this.blocked = new Uint8Array(n);
    this.dist = new Float32Array(n);
    this.dirX = new Float32Array(n);
    this.dirY = new Float32Array(n);
    this.queue = new Int32Array(n);
  }

  private clampCol(v: number): number {
    return v < 0 ? 0 : v >= this.cols ? this.cols - 1 : v;
  }
  private colOf(x: number): number {
    return this.clampCol(Math.floor((x + this.half) / this.cell));
  }
  private cellCenter(c: number): number {
    return (c + 0.5) * this.cell - this.half;
  }

  /** Mark blocked cells from the wall list (once per map; cheap to redo). */
  buildBlocked(walls: readonly Wall[], margin: number): void {
    const c = this.cols;
    for (let cy = 0; cy < c; cy += 1) {
      const wy = this.cellCenter(cy);
      for (let cx = 0; cx < c; cx += 1) {
        const wx = this.cellCenter(cx);
        let blk = 0;
        for (const w of walls) {
          if (
            wx >= w.cx - w.hx - margin &&
            wx <= w.cx + w.hx + margin &&
            wy >= w.cy - w.hy - margin &&
            wy <= w.cy + w.hy + margin
          ) {
            blk = 1;
            break;
          }
        }
        this.blocked[cy * c + cx] = blk;
      }
    }
    this.blockedBuilt = true;
  }

  /** Multi-source BFS from each player's cell → distance field → flow directions. */
  compute(world: WorldState): void {
    if (!this.blockedBuilt) return;
    const c = this.cols;
    const n = c * c;
    this.dist.fill(Number.POSITIVE_INFINITY);
    let head = 0;
    let tail = 0;
    for (const p of world.players.values()) {
      if (p.downed) continue;
      const cx = this.colOf(p.x);
      const cy = this.colOf(p.y);
      const idx = cy * c + cx;
      if (this.blocked[idx] === 1 || this.dist[idx] === 0) continue;
      this.dist[idx] = 0;
      this.queue[tail] = idx;
      tail = (tail + 1) % n;
    }
    // 4-neighbour BFS (uniform cost) over walkable cells.
    while (head !== tail) {
      const idx = this.queue[head] ?? 0;
      head = (head + 1) % n;
      const cx = idx % c;
      const cy = (idx / c) | 0;
      const d = (this.dist[idx] ?? 0) + 1;
      for (let k = 0; k < 4; k += 1) {
        const nx = cx + (k === 0 ? 1 : k === 1 ? -1 : 0);
        const ny = cy + (k === 2 ? 1 : k === 3 ? -1 : 0);
        if (nx < 0 || nx >= c || ny < 0 || ny >= c) continue;
        const ni = ny * c + nx;
        if (this.blocked[ni] === 1 || this.dist[ni] !== Number.POSITIVE_INFINITY) continue;
        if (d < (this.dist[ni] ?? Number.POSITIVE_INFINITY)) {
          this.dist[ni] = d;
          this.queue[tail] = ni;
          tail = (tail + 1) % n;
        }
      }
    }
    // Flow direction = toward the 8-neighbour with the lowest distance (down-gradient).
    for (let cy = 0; cy < c; cy += 1) {
      for (let cx = 0; cx < c; cx += 1) {
        const idx = cy * c + cx;
        this.dirX[idx] = 0;
        this.dirY[idx] = 0;
        if (this.blocked[idx] === 1 || this.dist[idx] === Number.POSITIVE_INFINITY) continue;
        let bestD = this.dist[idx] ?? Number.POSITIVE_INFINITY;
        let bx = 0;
        let by = 0;
        for (let dy = -1; dy <= 1; dy += 1) {
          for (let dx = -1; dx <= 1; dx += 1) {
            if (dx === 0 && dy === 0) continue;
            const nx = cx + dx;
            const ny = cy + dy;
            if (nx < 0 || nx >= c || ny < 0 || ny >= c) continue;
            const nd = this.dist[ny * c + nx] ?? Number.POSITIVE_INFINITY;
            if (nd < bestD) {
              bestD = nd;
              bx = dx;
              by = dy;
            }
          }
        }
        const len = Math.hypot(bx, by);
        if (len > 0) {
          this.dirX[idx] = bx / len;
          this.dirY[idx] = by / len;
        }
      }
    }
    this.hasField = true;
  }

  /** Flow direction at a world point (writes into `_flowOut`); zero vector if none. */
  sample(x: number, y: number): { x: number; y: number } {
    const idx = this.colOf(y) * this.cols + this.colOf(x);
    _flowOut.x = this.dirX[idx] ?? 0;
    _flowOut.y = this.dirY[idx] ?? 0;
    return _flowOut;
  }
}
const _flowOut = { x: 0, y: 0 };

/**
 * Nearest *living* player to (x, y). Downed players are ignored so enemies
 * retarget to living allies instead of piling on a body. Returns null when all
 * players are downed (enemies idle until the run ends in defeat).
 */
function nearestPlayer(world: WorldState, x: number, y: number): PlayerState | null {
  let best: PlayerState | null = null;
  let bestD = Number.POSITIVE_INFINITY;
  for (const p of world.players.values()) {
    if (p.downed) continue;
    const dx = p.x - x;
    const dy = p.y - y;
    const d = dx * dx + dy * dy;
    if (d < bestD) {
      bestD = d;
      best = p;
    }
  }
  return best;
}

/**
 * Advance all enemies one fixed tick: chase the nearest player and stop at
 * contact range (pass 1), then resolve enemy-enemy overlap via the grid
 * (passes 2–3, read-all/write-all so it's order-independent & deterministic).
 */
export function stepEnemies(
  world: WorldState,
  grid: SpatialGrid,
  cfg: WorldConfig = DEFAULT_WORLD_CONFIG,
  dtSeconds: number = SIM_DT_S,
): void {
  if (isFrozen(world)) return;
  const e = world.enemies;
  const half = cfg.arenaHalfSize;

  // Pass 1: chase nearest player, stop at contact range.
  const chargePhase = (world.tick as number) * dtSeconds;
  const trashSpeedMul = enemySpeedMul(world); // D0: enemies shamble early, speed up later
  // Phase D2: with walls, build/refresh a flow field so trash route AROUND obstacles.
  const useFlow = cfg.walls.length > 0;
  if (useFlow) {
    if (world.flow === null) {
      world.flow = new FlowField(half);
      world.flow.buildBlocked(cfg.walls, cfg.playerRadius);
    }
    if ((world.tick as number) % FLOW_REBUILD_TICKS === 0 || !world.flow.hasField) {
      world.flow.compute(world);
    }
  }
  const flow = useFlow ? world.flow : null;
  for (let i = 0; i < e.count; i += 1) {
    const t = enemyType(e.type[i] ?? 0);
    const target = nearestPlayer(world, e.x[i] ?? 0, e.y[i] ?? 0);
    if (target === null) continue;
    const dx = target.x - (e.x[i] ?? 0);
    const dy = target.y - (e.y[i] ?? 0);
    const dist = Math.hypot(dx, dy);
    const contact = cfg.playerRadius + t.radius;
    // Phase B3: face the chased player (used for the backstab/aegis arc).
    if (dist > 1e-5) e.facing[i] = Math.atan2(dy, dx);
    // Mini-boss "charge": deterministic lunges (slow wind-up, fast dash) keyed off
    // the enemy id so each is out of phase. No per-enemy state needed.
    let speed = t.speed;
    // D0: trash shamble early / speed up late; elites (boss/mini-boss) keep their own pace.
    if ((e.type[i] ?? 0) < BOSS_ENEMY_TYPE) speed *= trashSpeedMul;
    if (t.charge === true) {
      const phase = chargePhase * 1.4 + (e.id[i] ?? 0) * 0.7;
      speed *= Math.sin(phase) > 0.6 ? 2.4 : 0.55; // ~dash vs ~wind-up
    }
    // Boss "strafe": hold a stand-off ring and orbit the target (kiting) instead of
    // shouldering it — makes the final boss visibly mobile.
    if (t.standOff !== undefined && dist > 1e-3) {
      const ux = dx / dist;
      const uy = dy / dist;
      const ring = Math.max(contact, t.standOff);
      const radial = dist - ring; // >0 approach, <0 back off
      // Tangential orbit direction alternates slowly so it weaves rather than circling forever.
      const orbitDir = Math.sin(chargePhase * 0.5 + (e.id[i] ?? 0)) >= 0 ? 1 : -1;
      const tx = -uy * orbitDir;
      const ty = ux * orbitDir;
      const radialStep = Math.sign(radial) * Math.min(speed * dtSeconds, Math.abs(radial));
      const tangStep = speed * dtSeconds * 0.8;
      e.x[i] = (e.x[i] ?? 0) + ux * radialStep + tx * tangStep;
      e.y[i] = (e.y[i] ?? 0) + uy * radialStep + ty * tangStep;
    } else if (dist > contact && dist > 1e-5) {
      const stepDist = Math.min(speed * dtSeconds, dist - contact);
      // Phase D2: follow the flow field around walls; fall back to straight chase when
      // the field has no direction here (open ground / near the target).
      let mx = dx / dist;
      let my = dy / dist;
      if (flow !== null && (e.type[i] ?? 0) < BOSS_ENEMY_TYPE) {
        const f = flow.sample(e.x[i] ?? 0, e.y[i] ?? 0);
        if (f.x !== 0 || f.y !== 0) {
          mx = f.x;
          my = f.y;
        }
      }
      e.x[i] = (e.x[i] ?? 0) + mx * stepDist;
      e.y[i] = (e.y[i] ?? 0) + my * stepDist;
    }
  }

  // Pass 2: accumulate separation pushes (reads post-chase positions).
  grid.build(e);
  e.pushX.fill(0, 0, e.count);
  e.pushY.fill(0, 0, e.count);
  for (let i = 0; i < e.count; i += 1) {
    const ri = enemyType(e.type[i] ?? 0).radius;
    const xi = e.x[i] ?? 0;
    const yi = e.y[i] ?? 0;
    grid.forEachNeighbor(xi, yi, (j) => {
      if (j <= i) return; // resolve each pair once
      const rj = enemyType(e.type[j] ?? 0).radius;
      const minDist = ri + rj;
      const dx = xi - (e.x[j] ?? 0);
      const dy = yi - (e.y[j] ?? 0);
      const d2 = dx * dx + dy * dy;
      if (d2 >= minDist * minDist || d2 <= 1e-9) return;
      const d = Math.sqrt(d2);
      const overlap = (minDist - d) * 0.5 * cfg.enemySeparation;
      const nx = dx / d;
      const ny = dy / d;
      e.pushX[i] = (e.pushX[i] ?? 0) + nx * overlap;
      e.pushY[i] = (e.pushY[i] ?? 0) + ny * overlap;
      e.pushX[j] = (e.pushX[j] ?? 0) - nx * overlap;
      e.pushY[j] = (e.pushY[j] ?? 0) - ny * overlap;
    });
  }

  // Pass 3: apply pushes, clamp to the arena.
  for (let i = 0; i < e.count; i += 1) {
    const nx = (e.x[i] ?? 0) + (e.pushX[i] ?? 0);
    const ny = (e.y[i] ?? 0) + (e.pushY[i] ?? 0);
    let cx = nx < -half ? -half : nx > half ? half : nx;
    let cy = ny < -half ? -half : ny > half ? half : ny;
    if (cfg.walls.length > 0) {
      const r = resolveWalls(cx, cy, enemyType(e.type[i] ?? 0).radius, cfg.walls);
      cx = r.x;
      cy = r.y;
    }
    e.x[i] = cx;
    e.y[i] = cy;
  }
}

/**
 * Spawn up to `count` enemies at random arena-edge positions using the world's
 * seeded PRNG (deterministic). Returns the number actually spawned.
 */
export function spawnEnemies(
  world: WorldState,
  count: number,
  cfg: WorldConfig = DEFAULT_WORLD_CONFIG,
  maxTypeId: number = ENEMY_TYPES.length - 1,
  weights?: readonly number[],
): number {
  const e = world.enemies;
  const half = cfg.arenaHalfSize;
  const typeCount = Math.min(ENEMY_TYPES.length, Math.max(1, maxTypeId + 1));
  // Precompute the total weight once (weighted composition), else uniform fallback.
  let totalW = 0;
  if (weights !== undefined) {
    for (let k = 0; k < typeCount; k += 1) totalW += weights[k] ?? 0;
  }
  let spawned = 0;
  let rng = world.rngState;
  // Phase C: when breaches are open, enemies pour in clustered at those edge directions
  // (packs from a side) instead of uniformly around the whole perimeter.
  const breachAngles = activeBreachAngles(world);
  for (let n = 0; n < count && e.count < e.capacity; n += 1) {
    let r0: number;
    let r1: number;
    let r2: number;
    [rng, r0] = nextRandom(rng);
    [rng, r1] = nextRandom(rng);
    [rng, r2] = nextRandom(rng);
    let x = 0;
    let y = 0;
    if (breachAngles.length > 0) {
      // Pick a breach, place at its edge point + tangential jitter (a cohesive pack).
      const bi = Math.min(breachAngles.length - 1, Math.floor(r0 * breachAngles.length));
      const ang = breachAngles[bi] ?? 0;
      const pt = breachEdgePoint(ang, half);
      const spread = half * BREACH_EDGE_SPREAD;
      // Tangent to the radial direction, so jitter slides along the edge, not inward.
      const tx = -Math.sin(ang);
      const ty = Math.cos(ang);
      const j = (r1 * 2 - 1) * spread;
      x = clamp(pt.x + tx * j, -half, half);
      y = clamp(pt.y + ty * j, -half, half);
    } else {
      // Fallback (no breaches, e.g. direct test calls): the old random-edge spread.
      const edge = Math.floor(r0 * 4);
      const along = (r1 * 2 - 1) * half;
      if (edge === 0) {
        x = -half;
        y = along;
      } else if (edge === 1) {
        x = half;
        y = along;
      } else if (edge === 2) {
        x = along;
        y = -half;
      } else {
        x = along;
        y = half;
      }
    }
    let typeId: number;
    if (weights !== undefined && totalW > 0) {
      let r = r2 * totalW;
      typeId = typeCount - 1;
      for (let k = 0; k < typeCount; k += 1) {
        r -= weights[k] ?? 0;
        if (r <= 0) {
          typeId = k;
          break;
        }
      }
    } else {
      typeId = Math.min(typeCount - 1, Math.floor(r2 * typeCount));
    }
    const idx = e.spawn(typeId, x, y);
    if (idx >= 0) {
      // Within-run ramp (Phase A1) × endless loop ramp (item 1): later waves/loops spawn
      // tankier trash. Always applied (trashHpMul is ×1 on wave 0, loopHpMul ×1 on loop 0).
      const hpMul = trashHpMul(world) * loopHpMul(world);
      e.hp[idx] = Math.max(1, Math.round((e.hp[idx] ?? 1) * hpMul));
      if ((e.shieldHp[idx] ?? 0) > 0) e.shieldHp[idx] = Math.round((e.shieldHp[idx] ?? 0) * hpMul);
      spawned += 1;
    }
  }
  world.rngState = rng >>> 0;
  return spawned;
}

// ---------------------------------------------------------------------------
// Combat: powers (auto-projectile + aura), enemy/player HP, lag-comp hit test
// ---------------------------------------------------------------------------

/**
 * A queryable set of enemy positions for projectile hit detection. The live
 * world satisfies this with identity index mapping; the server can also supply
 * a **historical** field (enemies rewound to the shooter's view-time) whose
 * `presentIndex` maps a historical neighbor index back to the present enemy so
 * damage lands on the live entity (CS-style lag compensation — see netcode.md).
 */
export interface EnemyHitField {
  /** Spatial grid already built over `x`/`y`. */
  readonly grid: SpatialGrid;
  readonly x: ArrayLike<number>;
  readonly y: ArrayLike<number>;
  /** Present enemy index for neighbor index `j`, or -1 if it has since died. */
  presentIndex(j: number): number;
}

/** Per-tick combat outcome (small, allocated once per room tick — not hot). */
export interface CombatResult {
  /** Enemies that died this tick. */
  kills: number;
  /** Projectiles spawned this tick. */
  projectilesFired: number;
  /** Total damage dealt to players this tick (contact). */
  playerDamage: number;
  /** Total damage dealt BY players to enemies this tick (Phase A5: DPS telemetry). */
  enemyDamage: number;
  /** Total HP restored to players this tick via lifesteal+regen (Phase A5: sustain telemetry). */
  healed: number;
}

const emptyCombatResult = (): CombatResult => ({
  kills: 0,
  projectilesFired: 0,
  playerDamage: 0,
  enemyDamage: 0,
  healed: 0,
});

// --- Phase B counter-system combat helpers ---
/** Aegis arc (Phase B3): a frontal hit is heavily reduced, a flanking hit lands clean/bonus. */
const AEGIS_FRONT_MUL = 0.25;
const AEGIS_SIDE_MUL = 0.6;
const AEGIS_BACK_MUL = 1.25;
const AEGIS_ARC_COS = 0.5; // cos(60°): ±60° front/back arcs
/** Shield (Phase B2): regenerates after this idle delay, at this fraction of max per second. */
const SHIELD_REGEN_DELAY_MS = 2500;
const SHIELD_REGEN_FRAC = 0.5;
/** Suppressor (Phase B4): players within this radius of a suppressor get no lifesteal/regen. */
const SUPPRESS_RADIUS = 4.5;

/**
 * Damage multiplier for one hit on an enemy (Phase B): physical/magic resistance ×
 * the aegis front/back arc. `hitDirX/Y` is the direction the damage travels INTO the
 * enemy (projectile velocity, or source→enemy for area powers); compared to the
 * enemy's `facing` to tell a frontal hit (reduced) from a flank/backstab (full/bonus).
 */
export function enemyDmgMultiplier(
  t: EnemyType,
  facingAng: number,
  dmgType: DamageType,
  hitDirX: number,
  hitDirY: number,
): number {
  let m = dmgType === "physical" ? (t.resistPhysical ?? 1) : (t.resistMagic ?? 1);
  if (t.aegis === true) {
    const len = Math.hypot(hitDirX, hitDirY) || 1;
    const dot = (Math.cos(facingAng) * hitDirX + Math.sin(facingAng) * hitDirY) / len;
    // dot ≈ +1: damage travels the way the enemy faces → it came from BEHIND (backstab).
    // dot ≈ -1: enemy is looking straight into the damage → FRONT (blocked).
    if (dot > AEGIS_ARC_COS) m *= AEGIS_BACK_MUL;
    else if (dot < -AEGIS_ARC_COS) m *= AEGIS_FRONT_MUL;
    else m *= AEGIS_SIDE_MUL;
  }
  return m;
}

/** Phase B4: is a player inside any Suppressor enemy's anti-heal aura this tick? */
function playerSuppressed(world: WorldState, p: PlayerState): boolean {
  const e = world.enemies;
  const r2 = SUPPRESS_RADIUS * SUPPRESS_RADIUS;
  for (let j = 0; j < e.count; j += 1) {
    if (enemyType(e.type[j] ?? 0).suppressor !== true) continue;
    const dx = (e.x[j] ?? 0) - p.x;
    const dy = (e.y[j] ?? 0) - p.y;
    if (dx * dx + dy * dy <= r2) return true;
  }
  return false;
}

/** Nearest live enemy to (x,y) within `maxRange` (linear scan); -1 if none. */
function nearestEnemy(e: EnemyStore, x: number, y: number, maxRange: number): number {
  let best = -1;
  let bestD2 = maxRange * maxRange;
  for (let i = 0; i < e.count; i += 1) {
    const dx = (e.x[i] ?? 0) - x;
    const dy = (e.y[i] ?? 0) - y;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = i;
    }
  }
  return best;
}

// Reused scratch for the projectile-collision neighbor callback (zero-alloc).
let _hitEnemies: EnemyStore | null = null;
let _hitField: EnemyHitField | null = null;
let _hitPx = 0;
let _hitPy = 0;
let _hitProjRadius = 0;
let _hitBestD2 = 0;
let _hitBestPresent = -1;

function projectileHitCb(j: number): void {
  const field = _hitField;
  const e = _hitEnemies;
  if (field === null || e === null) return;
  const present = field.presentIndex(j);
  if (present < 0 || present >= e.count) return; // enemy already dead/gone
  const er = enemyType(e.type[present] ?? 0).radius;
  const dx = (field.x[j] ?? 0) - _hitPx;
  const dy = (field.y[j] ?? 0) - _hitPy;
  const d2 = dx * dx + dy * dy;
  const reach = _hitProjRadius + er;
  if (d2 <= reach * reach && d2 < _hitBestD2) {
    _hitBestD2 = d2;
    _hitBestPresent = present;
  }
}

/** Angular spread between adjacent bolts when a bolt power fires more than one. */
const BOLT_SPREAD_RAD = 0.18;

/**
 * Per-tick, per-player context handed to a power behavior. A single instance is
 * reused across all powers in a `stepCombat` call (fields are mutated per power),
 * so dispatch allocates nothing. The hot per-enemy work lives *inside* each
 * behavior's `apply` as a raw typed-array loop (no per-enemy dispatch).
 */
interface PowerContext {
  world: WorldState;
  cfg: WorldConfig;
  dtSeconds: number;
  enemies: EnemyStore;
  projectiles: ProjectileStore;
  out: CombatResult;
  /** Shared orbit phase = tick · dt (so all orbit powers share a clock). */
  orbitAngle: number;
  // Mutated per equipped power:
  player: PlayerState;
  eq: EquippedPower;
  power: PowerType;
  /** power.damage · levelDamageScale(level) · stats.damageMul (precomputed). */
  baseDamage: number;
}

/**
 * A power's runtime behavior. The registry (`POWER_BEHAVIORS`) is keyed by
 * `PowerKind`, so adding a power = one data row in `POWERS` + one entry here
 * (the compiler enforces every `PowerKind` has an entry). `stepCombat` does one
 * `Record` lookup per equipped-power per player per tick — cold relative to the
 * swarm, which the inline loops below still own.
 */
interface PowerBehavior {
  /** Run this power for one player this tick (mutates ctx.enemies.dmg / projectiles). */
  apply(ctx: PowerContext): void;
  /** Extra per-player wire scalar this power contributes (e.g. orbit blade count). */
  wireScalar?(eq: EquippedPower): number;
}

/**
 * Roll a critical strike for `p` using the seeded world RNG (advances rngState only
 * when the player can crit, so a 0%-crit build leaves the stream untouched). Called
 * a handful of times per tick (per discrete fire), not per enemy.
 */
function rollCrit(world: WorldState, p: PlayerState): boolean {
  const cc = p.stats.critChance;
  if (cc <= 0) return false;
  const [next, r] = nextRandom(world.rngState);
  world.rngState = next >>> 0;
  return r < cc;
}

/**
 * Mitigate one incoming hit against a player by their defensive augments (item 4):
 * a seeded `dodgeChance` to take zero, then flat `armor` reduction. Returns the damage
 * actually dealt (0 if dodged). Thorns is handled by the caller (contact only).
 */
function incomingDamage(world: WorldState, p: PlayerState, raw: number): number {
  if (raw <= 0) return 0;
  if (p.shieldMs > 0) return 0; // active Shield ability → full immunity (item 5)
  if (p.stats.dodgeChance > 0) {
    const [next, r] = nextRandom(world.rngState);
    world.rngState = next >>> 0;
    if (r < p.stats.dodgeChance) return 0;
  }
  return raw * (1 - p.stats.armor);
}

const projectileBehavior: PowerBehavior = {
  apply(ctx) {
    const { eq, power, player: p, enemies: e, projectiles: proj, out } = ctx;
    if (eq.cooldown > 0) return;
    // Manual aim (B4): fire toward the player's aim vector even with no enemy in range
    // (fire into empty space). Auto-aim falls back to acquiring the nearest enemy.
    const manual = p.manualAim && (p.aimX !== 0 || p.aimY !== 0);
    let baseAngle: number;
    if (manual) {
      baseAngle = Math.atan2(p.aimY, p.aimX);
    } else {
      const target = nearestEnemy(e, p.x, p.y, power.range);
      if (target < 0) return;
      const aimX = (e.x[target] ?? 0) - p.x;
      const aimY = (e.y[target] ?? 0) - p.y;
      if (aimX * aimX + aimY * aimY <= 1e-10) return;
      baseAngle = Math.atan2(aimY, aimX);
    }
    const speed = (power.projectileSpeed ?? 18) * p.stats.projSpeedMul;
    // Boomerangs need time for the round trip (out + curve back); others = range/speed.
    const ttl = power.returns === true ? (power.range / speed) * 3 + 0.8 : power.range / speed;
    const count = projectileCountFor(power, eq.level) + Math.round(p.stats.extraProjectiles);
    const spread = power.spread ?? BOLT_SPREAD_RAD;
    const basePierce = power.pierce ?? 0;
    // Piercing powers gain +1 pierce every 2 levels; non-piercing stay single-hit —
    // unless the Piercing Rounds augment grants global extra pierce (item 4 tranche 2).
    const extraPierce = Math.round(p.stats.extraPierce);
    const pierce =
      basePierce > 0 ? basePierce + Math.floor((eq.level - 1) / 2) + extraPierce : extraPierce;
    const homing = p.stats.homing > 0 && power.returns !== true ? 1 : 0;
    for (let b = 0; b < count; b += 1) {
      const angle = baseAngle + (b - (count - 1) / 2) * spread;
      // Each bolt rolls its own crit (so multi-bolt builds sparkle independently).
      const crit = rollCrit(ctx.world, p);
      const dmg = crit ? ctx.baseDamage * p.stats.critMul : ctx.baseDamage;
      if (
        proj.spawn(
          eq.power,
          p.id,
          p.x,
          p.y,
          Math.cos(angle) * speed,
          Math.sin(angle) * speed,
          ttl,
          dmg,
          crit ? 1 : 0,
          pierce,
          homing,
        ) >= 0
      ) {
        out.projectilesFired += 1;
      }
    }
    eq.cooldown = power.cooldownMs * p.stats.cooldownMul;
  },
};

/** Continuous-DPS powers (aura/orbit) emit a batched floating number this often (ticks). */
const DPS_NUMBER_TICKS = 15; // ~0.5s at 30Hz
/** Max lifesteal heal per tick as a fraction of max HP (item 3: capped sustain). */
const LIFESTEAL_TICK_CAP_FRAC = 0.0004; // ≈1.2%/s of maxHp at 30Hz, full uptime (nerfed)
/**
 * Combined in-combat sustain cap (Phase A3): total heal/s from lifesteal + regen may not
 * exceed this fraction of max HP per second. Stops "out-heal everything, stop playing".
 */
export const SUSTAIN_CAP_FRAC = 0.02; // 2% maxHp/s combined ceiling (nerfed)

const auraBehavior: PowerBehavior = {
  apply(ctx) {
    const { power, player: p, enemies: e } = ctx;
    const r = (power.auraRadius ?? power.range) * levelRadiusScale(ctx.eq.level) * p.stats.areaMul;
    const r2 = r * r;
    const dmg = ctx.baseDamage * ctx.dtSeconds;
    const dtype = power.damageType;
    let acc = 0;
    for (let j = 0; j < e.count; j += 1) {
      const dx = (e.x[j] ?? 0) - p.x;
      const dy = (e.y[j] ?? 0) - p.y;
      if (dx * dx + dy * dy <= r2) {
        // Phase B: resist × aegis (hit dir = source→enemy = player→enemy).
        const d =
          dmg * enemyDmgMultiplier(enemyType(e.type[j] ?? 0), e.facing[j] ?? 0, dtype, dx, dy);
        e.dmg[j] = (e.dmg[j] ?? 0) + d;
        e.lastOwner[j] = p.id as number;
        acc += d;
      }
    }
    // Periodic owner-colored floating number for continuous DPS (every ~0.5s).
    if (acc > 0) {
      p.dealtThisTick += acc;
      if ((ctx.world.tick as number) % DPS_NUMBER_TICKS === 0) {
        ctx.world.hits.push(p.x, p.y, acc * DPS_NUMBER_TICKS, 0, p.id as number);
      }
    }
  },
};

const orbitBehavior: PowerBehavior = {
  apply(ctx) {
    const { power, player: p, enemies: e } = ctx;
    const blades = orbitBladeCount(ctx.eq.level);
    const orbitR = (power.orbitRadius ?? 2.2) * p.stats.areaMul;
    const bladeR = power.bladeRadius ?? 0.5;
    const bladeR2 = bladeR * bladeR;
    const dmg = ctx.baseDamage * ctx.dtSeconds;
    const phase = ctx.orbitAngle * (power.orbitSpeed ?? 3);
    const dtype = power.damageType;
    let acc = 0;
    for (let b = 0; b < blades; b += 1) {
      const a = phase + (b * (Math.PI * 2)) / blades;
      const bx = p.x + Math.cos(a) * orbitR;
      const by = p.y + Math.sin(a) * orbitR;
      for (let j = 0; j < e.count; j += 1) {
        const dx = (e.x[j] ?? 0) - bx;
        const dy = (e.y[j] ?? 0) - by;
        if (dx * dx + dy * dy <= bladeR2) {
          // Phase B: resist × aegis (hit dir = blade→enemy).
          const d =
            dmg * enemyDmgMultiplier(enemyType(e.type[j] ?? 0), e.facing[j] ?? 0, dtype, dx, dy);
          e.dmg[j] = (e.dmg[j] ?? 0) + d;
          e.lastOwner[j] = p.id as number;
          acc += d;
        }
      }
    }
    if (acc > 0) {
      p.dealtThisTick += acc;
      if ((ctx.world.tick as number) % DPS_NUMBER_TICKS === 0) {
        ctx.world.hits.push(p.x, p.y, acc * DPS_NUMBER_TICKS, 0, p.id as number);
      }
    }
  },
  wireScalar: (eq) => orbitBladeCount(eq.level),
};

const novaBehavior: PowerBehavior = {
  apply(ctx) {
    const { eq, power, player: p, enemies: e } = ctx;
    if (eq.cooldown > 0) return;
    const r = (power.novaRadius ?? power.range) * levelRadiusScale(eq.level) * p.stats.areaMul;
    const r2 = r * r;
    // One crit roll for the whole burst; surface a single floating number at the player.
    const crit = rollCrit(ctx.world, p);
    const dmg = crit ? ctx.baseDamage * p.stats.critMul : ctx.baseDamage;
    const dtype = power.damageType;
    let hitAny = false;
    for (let j = 0; j < e.count; j += 1) {
      const dx = (e.x[j] ?? 0) - p.x;
      const dy = (e.y[j] ?? 0) - p.y;
      if (dx * dx + dy * dy <= r2) {
        const isElite =
          (e.type[j] ?? 0) >= BOSS_ENEMY_TYPE || ctx.world.minibossIds.has(e.id[j] ?? 0);
        // Giant Slayer (item 4) × Phase B resist/aegis (hit dir = player→enemy).
        const mul = enemyDmgMultiplier(enemyType(e.type[j] ?? 0), e.facing[j] ?? 0, dtype, dx, dy);
        const d = dmg * (isElite ? p.stats.bossDmgMul : 1) * mul;
        e.dmg[j] = (e.dmg[j] ?? 0) + d;
        e.lastOwner[j] = p.id as number;
        p.dealtThisTick += d;
        hitAny = true;
      }
    }
    if (hitAny) ctx.world.hits.push(p.x, p.y, dmg, crit ? 1 : 0, p.id as number);
    eq.cooldown = power.cooldownMs * p.stats.cooldownMul;
    p.novaPulse = true;
  },
};

/**
 * Power behaviors keyed by `PowerKind`. The `Record` type makes this exhaustive:
 * adding a `PowerKind` without an entry is a compile error. Adding a power is a
 * `POWERS` row + an entry here (+ a client visual + an optional wire flag).
 */
export const POWER_BEHAVIORS: Readonly<Record<PowerKind, PowerBehavior>> = {
  projectile: projectileBehavior,
  aura: auraBehavior,
  orbit: orbitBehavior,
  nova: novaBehavior,
};

/** Per-player wire scalar (orbit blade count) derived from the registry. Pure. */
export function playerOrbitBlades(p: PlayerState): number {
  let n = 0;
  for (const eq of p.equipped) {
    const scalar = POWER_BEHAVIORS[powerType(eq.power).kind].wireScalar;
    if (scalar !== undefined) n = Math.max(n, scalar(eq));
  }
  return n;
}

/** Reused power-dispatch context (zero per-tick allocation; populated per use). */
const powerCtx = {} as PowerContext;
/** Reused scratch of enemy indices already struck by the current statik chain (no per-tick alloc). */
const statikHitScratch: number[] = [];

/**
 * Advance combat one fixed tick (server-authoritative; not predicted on the
 * client). Mutates enemy/player hp and the projectile store in place:
 *   1. tick per-player power cooldowns + reset the nova FX flag;
 *   2. move projectiles, expire by ttl / arena bounds, resolve hits (vs the
 *      `rewindFor(owner)` field if provided, else the live world);
 *   3. per player, dispatch each equipped power via `POWER_BEHAVIORS` (fires
 *      bolts / accumulates aura/orbit/nova damage into a per-enemy buffer);
 *   4. apply accumulated damage + sweep deaths (swap-remove, bump kills, drop gems);
 *   5. surviving enemies in contact range damage the nearest player (dps·dt).
 *
 * `grid` is rebuilt over the live enemies for aura/identity hit tests.
 */
export function stepCombat(
  world: WorldState,
  grid: SpatialGrid,
  cfg: WorldConfig = DEFAULT_WORLD_CONFIG,
  dtSeconds: number = SIM_DT_S,
  out: CombatResult = emptyCombatResult(),
  rewindFor?: (ownerId: number) => EnemyHitField | null,
): CombatResult {
  out.kills = 0;
  out.projectilesFired = 0;
  out.playerDamage = 0;
  out.enemyDamage = 0;
  out.healed = 0;

  // Clear the transient floating-damage buffer up-front (even when frozen) so a
  // pre-freeze hit isn't re-broadcast every paused tick → stacking damage
  // numbers on the client during a co-op level-up freeze.
  world.hits.clear();
  world.arcs.clear();

  // Frozen (someone is choosing a level-up): no combat advances this tick.
  if (isFrozen(world)) return out;

  const e = world.enemies;
  const proj = world.projectiles;
  const half = cfg.arenaHalfSize;
  const dtMs = dtSeconds * 1000;
  const orbitAngle = (world.tick as number) * dtSeconds; // base phase for orbit blades

  e.dmg.fill(0, 0, e.count);
  e.lastOwner.fill(-1, 0, e.count);
  grid.build(e);

  // Live (identity) hit field over the present enemies.
  const liveField: EnemyHitField = { grid, x: e.x, y: e.y, presentIndex: (j) => j };

  // (1) cooldowns + reset transient FX flags.
  for (const p of world.players.values()) {
    p.novaPulse = false;
    p.dealtThisTick = 0;
    for (const eq of p.equipped) {
      if (eq.cooldown > 0) eq.cooldown = Math.max(0, eq.cooldown - dtMs);
    }
  }

  // (2)+(3) move bolts, expire, and resolve hits into the damage buffer.
  let i = 0;
  while (i < proj.count) {
    const pt = powerType(proj.power[i] ?? 0);
    proj.age[i] = (proj.age[i] ?? 0) + dtSeconds;
    // Seeker (item 4): steer this projectile toward the nearest enemy.
    if ((proj.homing[i] ?? 0) === 1) {
      const tgt = nearestEnemy(e, proj.x[i] ?? 0, proj.y[i] ?? 0, 18);
      if (tgt >= 0) {
        const spd = Math.hypot(proj.vx[i] ?? 0, proj.vy[i] ?? 0) || 1;
        const tdx = (e.x[tgt] ?? 0) - (proj.x[i] ?? 0);
        const tdy = (e.y[tgt] ?? 0) - (proj.y[i] ?? 0);
        const td = Math.hypot(tdx, tdy) || 1;
        const pull = 0.18; // gentle curve so it still feels like a projectile
        const vx = (proj.vx[i] ?? 0) * (1 - pull) + (tdx / td) * spd * pull;
        const vy = (proj.vy[i] ?? 0) * (1 - pull) + (tdy / td) * spd * pull;
        const vlen = Math.hypot(vx, vy) || 1;
        proj.vx[i] = (vx / vlen) * spd;
        proj.vy[i] = (vy / vlen) * spd;
      }
    }
    // Boomerang (item 6): fly straight out, then curve back toward the owner.
    if (pt.returns === true) {
      const owner = world.players.get(asPlayerId(proj.owner[i] ?? -1));
      const age = proj.age[i] ?? 0;
      if (owner !== undefined && age > 0.4) {
        const spd = pt.projectileSpeed ?? 14;
        const tdx = owner.x - (proj.x[i] ?? 0);
        const tdy = owner.y - (proj.y[i] ?? 0);
        const td = Math.hypot(tdx, tdy) || 1;
        // Steer velocity toward the owner (strength grows with age → smooth arc).
        const pull = Math.min(1, (age - 0.4) * 2.4);
        const vx = (proj.vx[i] ?? 0) * (1 - pull) + (tdx / td) * spd * pull;
        const vy = (proj.vy[i] ?? 0) * (1 - pull) + (tdy / td) * spd * pull;
        const vlen = Math.hypot(vx, vy) || 1;
        proj.vx[i] = (vx / vlen) * spd;
        proj.vy[i] = (vy / vlen) * spd;
        // Caught it: despawn once it returns near the owner (after the out leg).
        if (age > 0.7 && td < 1.2) {
          proj.swapRemove(i);
          continue;
        }
      }
    }
    const nx = (proj.x[i] ?? 0) + (proj.vx[i] ?? 0) * dtSeconds;
    const ny = (proj.y[i] ?? 0) + (proj.vy[i] ?? 0) * dtSeconds;
    proj.x[i] = nx;
    proj.y[i] = ny;
    proj.ttl[i] = (proj.ttl[i] ?? 0) - dtSeconds;
    // Boomerangs ride the arena edge (don't despawn at bounds); others expire there.
    const outOfBounds = nx < -half || nx > half || ny < -half || ny > half;
    if ((proj.ttl[i] ?? 0) <= 0 || (outOfBounds && pt.returns !== true)) {
      proj.swapRemove(i);
      continue;
    }
    // Phase D: shots are blocked by walls (boomerangs phase through so they still return).
    if (pt.returns !== true && cfg.walls.length > 0 && pointInWalls(nx, ny, cfg.walls)) {
      proj.swapRemove(i);
      continue;
    }
    if (outOfBounds) {
      proj.x[i] = nx < -half ? -half : nx > half ? half : nx;
      proj.y[i] = ny < -half ? -half : ny > half ? half : ny;
    }
    const field = (rewindFor ? rewindFor(proj.owner[i] ?? 0) : null) ?? liveField;
    _hitEnemies = e;
    _hitField = field;
    _hitPx = nx;
    _hitPy = ny;
    _hitProjRadius = powerType(proj.power[i] ?? 0).projectileRadius ?? 0.15;
    _hitBestD2 = Number.POSITIVE_INFINITY;
    _hitBestPresent = -1;
    field.grid.forEachNeighbor(nx, ny, projectileHitCb);
    if (_hitBestPresent >= 0) {
      const owner = proj.owner[i] ?? -1;
      const op = owner >= 0 ? world.players.get(asPlayerId(owner)) : undefined;
      const tgtType = e.type[_hitBestPresent] ?? 0;
      const isElite =
        tgtType >= BOSS_ENEMY_TYPE || world.minibossIds.has(e.id[_hitBestPresent] ?? 0);
      // Phase B: physical/magic resist × aegis front/back arc (hit dir = projectile velocity).
      const mul = enemyDmgMultiplier(
        enemyType(tgtType),
        e.facing[_hitBestPresent] ?? 0,
        powerType(proj.power[i] ?? 0).damageType,
        proj.vx[i] ?? 0,
        proj.vy[i] ?? 0,
      );
      const dmg = (proj.damage[i] ?? 0) * (isElite ? (op?.stats.bossDmgMul ?? 1) : 1) * mul;
      e.dmg[_hitBestPresent] = (e.dmg[_hitBestPresent] ?? 0) + dmg;
      e.lastOwner[_hitBestPresent] = owner;
      if (op !== undefined) op.dealtThisTick += dmg;
      world.hits.push(nx, ny, dmg, proj.crit[i] ?? 0, owner, e.id[_hitBestPresent] ?? 0); // anchored floating dmg
      if ((proj.pierce[i] ?? 0) > 0) {
        proj.pierce[i] = (proj.pierce[i] ?? 0) - 1; // pierce: keep flying to the next enemy
      } else {
        proj.swapRemove(i); // single-hit projectile
        continue;
      }
    }
    i += 1;
  }
  _hitEnemies = null;
  _hitField = null;

  // (3) per-player powers: dispatch each equipped power via the registry. The
  // shared context is reused (mutated per power) so dispatch allocates nothing;
  // each behavior owns its raw per-enemy loop (no per-enemy dispatch).
  powerCtx.world = world;
  powerCtx.cfg = cfg;
  powerCtx.dtSeconds = dtSeconds;
  powerCtx.enemies = e;
  powerCtx.projectiles = proj;
  powerCtx.out = out;
  powerCtx.orbitAngle = orbitAngle;
  for (const p of world.players.values()) {
    if (p.downed) continue;
    const dmgMul = p.stats.damageMul;
    powerCtx.player = p;
    for (const eq of p.equipped) {
      const power = powerType(eq.power);
      powerCtx.eq = eq;
      powerCtx.power = power;
      powerCtx.baseDamage = power.damage * levelDamageScale(eq.level) * dmgMul;
      POWER_BEHAVIORS[power.kind].apply(powerCtx);
    }
  }

  // (3.5) Statikk discharge (idea 7): a fully-charged player arcs chain lightning
  // from the nearest enemy, jumping to successive nearby enemies. Damage feeds the
  // same buffers as powers (so kills/lifesteal/hit-numbers all apply), then resets.
  for (const p of world.players.values()) {
    if (p.downed || p.stats.statikLevel <= 0 || p.statikCharge < STATIK_MAX) continue;
    if (e.count === 0) continue;
    p.statikCharge = 0;
    const bounces = 1 + p.stats.statikLevel; // L1 = 2 targets, +1 per level
    const dmg = STATIK_BASE_DAMAGE * (1 + 0.2 * (p.stats.statikLevel - 1)) * p.stats.damageMul;
    const owner = p.id as number;
    let fromX = p.x;
    let fromY = p.y;
    let range = STATIK_JUMP_RANGE * STATIK_JUMP_RANGE;
    const hitMask = statikHitScratch;
    hitMask.length = 0;
    for (let b = 0; b < bounces; b += 1) {
      let best = -1;
      let bestD2 = range;
      for (let j = 0; j < e.count; j += 1) {
        if (hitMask.includes(j)) continue;
        const dx = (e.x[j] ?? 0) - fromX;
        const dy = (e.y[j] ?? 0) - fromY;
        const d2 = dx * dx + dy * dy;
        if (d2 < bestD2) {
          bestD2 = d2;
          best = j;
        }
      }
      if (best < 0) break;
      hitMask.push(best);
      const tx = e.x[best] ?? 0;
      const ty = e.y[best] ?? 0;
      // Phase B: statik is MAGIC; resist × aegis (hit dir = arc source→target).
      const sdmg =
        dmg *
        enemyDmgMultiplier(
          enemyType(e.type[best] ?? 0),
          e.facing[best] ?? 0,
          "magic",
          tx - fromX,
          ty - fromY,
        );
      e.dmg[best] = (e.dmg[best] ?? 0) + sdmg;
      e.lastOwner[best] = owner;
      p.dealtThisTick += sdmg;
      world.hits.push(tx, ty, sdmg, 0, owner, e.id[best] ?? 0);
      world.arcs.push(fromX, fromY, tx, ty, owner); // visible lightning bolt (item 2)
      fromX = tx;
      fromY = ty;
      range = STATIK_JUMP_RANGE * STATIK_JUMP_RANGE; // subsequent jumps use the same reach
    }
  }

  // (4) lifesteal: heal each player a fraction of the damage they dealt this tick,
  // CAPPED at a small % of max HP per tick (item 3) so at high DPS it's modest
  // sustain — never "you stop having to play". 30Hz × cap ≈ a few %/s of maxHp.
  for (const p of world.players.values()) {
    if (p.downed) continue;
    // Tick ability cooldown + active shield (item 5).
    if (p.abilityCdMs > 0) p.abilityCdMs = Math.max(0, p.abilityCdMs - dtSeconds * 1000);
    if (p.shieldMs > 0) p.shieldMs = Math.max(0, p.shieldMs - dtSeconds * 1000);
    out.enemyDamage += p.dealtThisTick; // Phase A5: total player→enemy damage this tick (DPS)
    // Phase B4: a nearby Suppressor enemy disables this player's lifesteal + regen entirely.
    if (playerSuppressed(world, p)) continue;
    // Combined sustain (Phase A3): lifesteal (own cap) + passive regen, then a single
    // in-combat ceiling so high DPS / heavy regen can't out-heal everything. Modest only.
    let healThisTick = 0;
    if (p.stats.lifesteal > 0 && p.dealtThisTick > 0) {
      healThisTick += Math.min(
        p.dealtThisTick * p.stats.lifesteal,
        p.maxHp * LIFESTEAL_TICK_CAP_FRAC,
      );
    }
    if (p.stats.regenPerSec > 0) healThisTick += p.stats.regenPerSec * dtSeconds;
    if (healThisTick > 0) {
      const cap = p.maxHp * SUSTAIN_CAP_FRAC * dtSeconds;
      const applied = Math.min(healThisTick, cap);
      const before = p.hp;
      p.hp = Math.min(p.maxHp, p.hp + applied);
      out.healed += p.hp - before; // Phase A5: actual HP restored (sustain telemetry)
    }
  }

  // (5) apply accumulated damage; sweep deaths (swap-remove keeps it dense).
  let k = 0;
  while (k < e.count) {
    const incoming = e.dmg[k] ?? 0;
    let dmg = incoming;
    // Phase B2: a shield layer soaks damage before HP (and resets its regen timer).
    if (dmg > 0 && (e.shieldHp[k] ?? 0) > 0) {
      const absorbed = Math.min(e.shieldHp[k] ?? 0, dmg);
      e.shieldHp[k] = (e.shieldHp[k] ?? 0) - absorbed;
      dmg -= absorbed;
      e.shieldRegenMs[k] = 0;
    }
    let hp = (e.hp[k] ?? 0) - dmg;
    // Execute (item 4): a player's augment instakills low-HP trash it just damaged.
    if (hp > 0 && incoming > 0 && (e.type[k] ?? 0) < BOSS_ENEMY_TYPE) {
      const dmgr = e.lastOwner[k] ?? -1;
      if (dmgr >= 0) {
        const ef = world.players.get(asPlayerId(dmgr))?.stats.executeFrac ?? 0;
        if (ef > 0 && hp <= enemyType(e.type[k] ?? 0).hp * ef) hp = 0;
      }
    }
    if (hp <= 0) {
      // Credit the last damager (per-player kill count).
      const owner = e.lastOwner[k] ?? -1;
      if (owner >= 0) {
        const killer = world.players.get(asPlayerId(owner));
        if (killer !== undefined) killer.kills += 1;
      }
      // Drop an XP gem where the enemy fell (before swap overwrites its slot).
      const dx = e.x[k] ?? 0;
      const dy = e.y[k] ?? 0;
      const enemyId = e.id[k] ?? 0;
      if (world.minibossIds.has(enemyId)) {
        // Mini-boss reward: a burst of XP gems + one glowing vacuum-all gem.
        world.minibossIds.delete(enemyId);
        dropMiniBossReward(world, dx, dy, cfg);
      } else {
        dropGem(world, dx, dy, e.type[k] ?? 0, cfg);
        // Occasional heal pickup (seeded ~HEAL_DROP_CHANCE).
        let roll: number;
        [world.rngState, roll] = nextRandom(world.rngState);
        if (roll < cfg.healDropChance) {
          world.pickups.spawn(GEM_TIER_HEAL, dx, dy, 0, cfg.gemTtlMs / 1000);
        }
      }
      e.swapRemove(k);
      e.dmg[k] = e.dmg[e.count] ?? 0; // keep the accumulator aligned with the swap
      e.lastOwner[k] = e.lastOwner[e.count] ?? -1;
      out.kills += 1;
    } else {
      e.hp[k] = hp;
      // Phase B2: shield regen — if untouched for a while, refill toward its type max.
      const maxSh = enemyType(e.type[k] ?? 0).shieldHp ?? 0;
      if (maxSh > 0 && (e.shieldHp[k] ?? 0) < maxSh) {
        if (incoming > 0) e.shieldRegenMs[k] = 0;
        else {
          e.shieldRegenMs[k] = (e.shieldRegenMs[k] ?? 0) + dtMs;
          if ((e.shieldRegenMs[k] ?? 0) >= SHIELD_REGEN_DELAY_MS) {
            e.shieldHp[k] = Math.min(
              maxSh,
              (e.shieldHp[k] ?? 0) + maxSh * SHIELD_REGEN_FRAC * dtSeconds,
            );
          }
        }
      }
      k += 1;
    }
  }
  world.kills += out.kills;

  // (6) surviving enemies in contact range deal contactDamage·dt to nearest player.
  const eliteDmg = eliteDmgMul(world); // elites/boss hit harder with more players (item 6)
  const loopDmg = loopDmgMul(world); // every enemy hits harder each endless loop (item 1)
  const runDmg = runDmgMul(world); // within-run ramp: late-wave trash chips harder (Phase A1)
  for (let j = 0; j < e.count; j += 1) {
    const typeId = e.type[j] ?? 0;
    const t = enemyType(typeId);
    const target = nearestPlayer(world, e.x[j] ?? 0, e.y[j] ?? 0);
    if (target === null || target.downed) continue;
    const dx = target.x - (e.x[j] ?? 0);
    const dy = target.y - (e.y[j] ?? 0);
    const contact = cfg.playerRadius + t.radius + 0.1;
    if (dx * dx + dy * dy <= contact * contact) {
      const isElite = typeId >= BOSS_ENEMY_TYPE || world.minibossIds.has(e.id[j] ?? 0);
      const raw = t.contactDamage * dtSeconds * (isElite ? eliteDmg : 1) * loopDmg * runDmg;
      const dmg = incomingDamage(world, target, raw); // dodge + armor (item 4)
      target.hp = Math.max(0, target.hp - dmg);
      out.playerDamage += dmg;
      // Thorns: reflect a fraction of contact damage taken back to the attacker (next
      // tick's sweep resolves any kill). Applied to HP directly (dmg accumulator is
      // zeroed at tick start, so a post-sweep write there would be lost).
      if (dmg > 0 && target.stats.thorns > 0) e.hp[j] = (e.hp[j] ?? 0) - dmg * target.stats.thorns;
      if (target.hp <= 0) {
        target.downed = true;
        target.reviveProgress = 0;
      }
    }
  }

  return out;
}

/** Pickup visual/behavior tiers (wire u8 in the snapshot). */
export const GEM_TIER_SMALL = 0;
export const GEM_TIER_BIG = 1;
export const GEM_TIER_HEAL = 2; // heals on pickup instead of granting XP
export const GEM_TIER_MAGNET = 3; // glowing: collects all gems on the map

/** XP value of a gem dropped by an enemy type. */
function xpValueFor(typeId: EnemyTypeId): number {
  return typeId === 2 ? 3 : 1; // brute drops a (smaller, Phase A4) big gem
}

/** Drop an XP gem at (x,y) for a slain enemy (deterministic). */
function dropGem(
  world: WorldState,
  x: number,
  y: number,
  typeId: EnemyTypeId,
  cfg: WorldConfig,
): void {
  const value = xpValueFor(typeId);
  const tier = value >= 3 ? 1 : 0; // brute (3+) still drops the chunkier big-gem visual
  world.pickups.spawn(tier, x, y, value, cfg.gemTtlMs / 1000);
}

/** Mini-boss death reward: a ring of big XP gems + one glowing vacuum-all gem. */
function dropMiniBossReward(world: WorldState, x: number, y: number, cfg: WorldConfig): void {
  const ttl = cfg.gemTtlMs / 1000;
  const ring = 8;
  for (let n = 0; n < ring; n += 1) {
    const a = (n / ring) * Math.PI * 2;
    // Phase A4: trimmed from 10 → 5 XP each (8×5 = 40 XP) to slow the mini-boss snowball.
    world.pickups.spawn(GEM_TIER_BIG, x + Math.cos(a) * 1.2, y + Math.sin(a) * 1.2, 5, ttl);
  }
  // The glowing gem sits at the center; collecting it vacuums every gem on the map.
  world.pickups.spawn(GEM_TIER_MAGNET, x, y, 0, ttl);
}

// ---------------------------------------------------------------------------
// Progression: XP gems, leveling, upgrade offers/application
// ---------------------------------------------------------------------------

function findEquipped(player: PlayerState, powerId: PowerId): EquippedPower | undefined {
  return player.equipped.find((eq) => eq.power === powerId);
}

function isUpgradeValid(player: PlayerState, u: UpgradeDef): boolean {
  // Ascension endless (B-ascend) lifts the *level/stack* caps (keep upgrading to infinity),
  // but NOT the slot budgets — you still upgrade your committed 5-weapon / 6-passive build.
  const asc = player.ascension;
  if (u.kind === "new-power") {
    // A free power slot is required to acquire a new power.
    if (player.equipped.length >= MAX_POWER_SLOTS) return false;
    return findEquipped(player, u.power ?? -1) === undefined;
  }
  if (u.kind === "power-level") {
    const eq = findEquipped(player, u.power ?? -1);
    if (eq === undefined) return false;
    if (asc) return true; // ascension lifts the level cap
    // Anti-snowball (Phase A4): a power can't outpace the player — its level is gated to
    // floor(playerLevel/2)+1, so maxing a primary (L6) requires roughly player level 10.
    const levelGate = Math.min(MAX_POWER_LEVEL, Math.floor(player.level / 2) + 1);
    return eq.level < levelGate;
  }
  // Passive: a brand-new passive line needs a free passive slot (B3). Lines already
  // held can keep leveling — up to their `maxStacks`, or without limit in ascension.
  const held = (player.upgradeCounts[u.id] ?? 0) > 0;
  if (!held && distinctPassiveCount(player) >= MAX_PASSIVE_SLOTS) return false;
  return asc || (player.upgradeCounts[u.id] ?? 0) < u.maxStacks;
}

/** Number of DISTINCT passive lines a player currently holds (count>0). The passive
 * "slots used" for the 6-slot budget (B3). */
export function distinctPassiveCount(player: PlayerState): number {
  let n = 0;
  for (let id = 0; id < player.upgradeCounts.length; id += 1) {
    if ((player.upgradeCounts[id] ?? 0) > 0 && UPGRADES[id]?.kind === "passive") n += 1;
  }
  return n;
}

/** A def's effective draw weight (base weight biased by its rarity tier). */
function effectiveWeight(u: UpgradeDef): number {
  return u.weight * (RARITY_WEIGHT_MULT[u.rarity] ?? 1);
}

/** Generate a deterministic (seeded) 3-choice offer for a player, or null if dry. */
export function generateOffer(world: WorldState, player: PlayerState): LevelUpOffer | null {
  const avail: UpgradeId[] = [];
  for (const u of UPGRADES) if (isUpgradeValid(player, u)) avail.push(u.id);
  if (avail.length === 0) return null;
  const choices: UpgradeId[] = [];
  let rng = world.rngState;
  const n = Math.min(3, avail.length);
  for (let c = 0; c < n; c += 1) {
    let total = 0;
    for (const id of avail) total += UPGRADES[id] ? effectiveWeight(UPGRADES[id]) : 0;
    let r: number;
    [rng, r] = nextRandom(rng);
    r *= total;
    let pick = avail.length - 1;
    for (let idx = 0; idx < avail.length; idx += 1) {
      const def = UPGRADES[avail[idx] ?? 0];
      r -= def ? effectiveWeight(def) : 0;
      if (r <= 0) {
        pick = idx;
        break;
      }
    }
    choices.push(avail[pick] ?? 0);
    avail.splice(pick, 1);
  }
  world.rngState = rng >>> 0;
  return { level: player.level, choices };
}

function applyPassive(player: PlayerState, u: UpgradeDef): void {
  const amt = u.amount ?? 0;
  switch (u.stat) {
    case "moveMul":
      player.stats.moveMul += amt;
      break;
    case "damageMul":
      player.stats.damageMul += amt;
      break;
    case "cooldownMul":
      player.stats.cooldownMul = Math.max(COOLDOWN_MUL_FLOOR, player.stats.cooldownMul + amt);
      break;
    case "magnetMul":
      player.stats.magnetMul += amt;
      break;
    case "critChance":
      player.stats.critChance = Math.max(
        0,
        Math.min(CRIT_CHANCE_CAP, player.stats.critChance + amt),
      );
      break;
    case "critMul":
      player.stats.critMul += amt;
      break;
    case "lifesteal":
      player.stats.lifesteal = Math.max(0, player.stats.lifesteal + amt);
      break;
    case "statik":
      player.stats.statikLevel += amt;
      break;
    case "regenPerSec":
      player.stats.regenPerSec = Math.max(0, player.stats.regenPerSec + amt);
      break;
    case "armor":
      player.stats.armor = Math.max(0, Math.min(ARMOR_CAP, player.stats.armor + amt));
      break;
    case "thorns":
      player.stats.thorns = Math.max(0, player.stats.thorns + amt);
      break;
    case "dodgeChance":
      player.stats.dodgeChance = Math.max(0, Math.min(DODGE_CAP, player.stats.dodgeChance + amt));
      break;
    case "projSpeedMul":
      player.stats.projSpeedMul = Math.max(0.1, player.stats.projSpeedMul + amt);
      break;
    case "areaMul":
      player.stats.areaMul = Math.max(0.2, player.stats.areaMul + amt);
      break;
    case "bossDmgMul":
      player.stats.bossDmgMul = Math.max(1, player.stats.bossDmgMul + amt);
      break;
    case "executeFrac":
      player.stats.executeFrac = Math.max(0, Math.min(0.2, player.stats.executeFrac + amt));
      break;
    case "goldFind":
      player.stats.goldFind = Math.max(1, player.stats.goldFind + amt);
      break;
    case "extraProjectiles":
      player.stats.extraProjectiles = Math.max(0, player.stats.extraProjectiles + amt);
      break;
    case "extraPierce":
      player.stats.extraPierce = Math.max(0, player.stats.extraPierce + amt);
      break;
    case "homing":
      player.stats.homing = Math.max(0, player.stats.homing + amt);
      break;
    case "maxHpBonus":
      player.maxHp += amt;
      player.hp = Math.min(player.maxHp, player.hp + amt);
      break;
    default:
      break;
  }
}

/** Apply an upgrade to a player if currently valid. Returns whether it applied. */
export function applyUpgrade(player: PlayerState, id: UpgradeId): boolean {
  const u = UPGRADES[id];
  if (u === undefined || !isUpgradeValid(player, u)) return false;
  player.upgradeCounts[id] = (player.upgradeCounts[id] ?? 0) + 1;
  if (u.kind === "new-power") {
    player.equipped.push({ power: u.power ?? STARTING_POWER, level: 1, cooldown: 0 });
  } else if (u.kind === "power-level") {
    const eq = findEquipped(player, u.power ?? -1);
    if (eq !== undefined) eq.level += 1;
  } else {
    applyPassive(player, u);
  }
  return true;
}

/**
 * Resolve the player's front level-up offer with `upgradeId` (must be one of the
 * offered choices; falls back to the first). Pops the offer. Returns success.
 */
export function chooseUpgrade(player: PlayerState, upgradeId: UpgradeId): boolean {
  const offer = player.pendingOffers[0];
  if (offer === undefined) return false;
  const id = offer.choices.includes(upgradeId) ? upgradeId : (offer.choices[0] ?? -1);
  player.pendingOffers.shift();
  return applyUpgrade(player, id);
}

/** Mirror the shared team progression onto one player (display/HUD/snapshot). */
export function syncPlayerProgression(world: WorldState, player: PlayerState): void {
  player.level = world.teamLevel;
  player.xp = world.teamXp;
  player.xpToNext = world.teamXpToNext;
}

/**
 * Grant XP to the shared team pool (ADR-0021). On each threshold cross the whole
 * team levels up: every player's progression is mirrored from the pool and each
 * gets their own level-up offer (per-player build). Deterministic: offers are
 * generated in id order so the seeded RNG advances identically on client/server.
 */
function grantTeamXp(world: WorldState, amount: number): void {
  // Ascension endless lifts the level cap (B-ascend): keep leveling to infinity.
  const capped = !world.ascension;
  if (capped && world.teamLevel >= MAX_PLAYER_LEVEL) {
    // Maxed: keep mirrors pinned at the cap (no overflow bar) and ignore XP.
    for (const p of world.players.values()) syncPlayerProgression(world, p);
    return;
  }
  world.teamXp += amount;
  let leveled = 0;
  let guard = 0;
  while (
    (!capped || world.teamLevel < MAX_PLAYER_LEVEL) &&
    world.teamXp >= world.teamXpToNext &&
    guard < 64
  ) {
    world.teamXp -= world.teamXpToNext;
    world.teamLevel += 1;
    world.teamXpToNext = xpToNextFor(world.teamLevel);
    leveled += 1;
    guard += 1;
  }
  if (capped && world.teamLevel >= MAX_PLAYER_LEVEL) world.teamXp = 0; // sit at the cap
  // Mirror the new pool onto everyone, and queue one offer per level gained per
  // player. Iterate in sorted id order for deterministic RNG advancement.
  const ids = [...world.players.keys()].sort((a, b) => a - b);
  for (const id of ids) {
    const p = world.players.get(id);
    if (p === undefined) continue;
    syncPlayerProgression(world, p);
    for (let n = 0; n < leveled; n += 1) {
      const offer = generateOffer(world, p);
      if (offer !== null) p.pendingOffers.push(offer);
    }
  }
}

/** Vacuum every XP gem on the map into the team pool (the glowing magnet gem's
 * effect). Heal gems are left in place. Iterates from the end (swap-remove safe). */
function collectAllGems(world: WorldState): void {
  const g = world.pickups;
  for (let i = g.count - 1; i >= 0; i -= 1) {
    const tier = g.tier[i] ?? 0;
    if (tier === GEM_TIER_HEAL || tier === GEM_TIER_MAGNET) continue;
    grantTeamXp(world, g.value[i] ?? 0);
    g.swapRemove(i);
  }
}

/** Advance XP gems: magnet toward / collect by the nearest alive player; expire. */
export function stepPickups(
  world: WorldState,
  cfg: WorldConfig = DEFAULT_WORLD_CONFIG,
  dtSeconds: number = SIM_DT_S,
): void {
  if (isFrozen(world)) return;
  const g = world.pickups;
  const collect2 = cfg.pickupCollectRadius * cfg.pickupCollectRadius;
  let i = 0;
  while (i < g.count) {
    g.ttl[i] = (g.ttl[i] ?? 0) - dtSeconds;
    if ((g.ttl[i] ?? 0) <= 0) {
      g.swapRemove(i);
      continue;
    }
    const gx = g.x[i] ?? 0;
    const gy = g.y[i] ?? 0;
    let best: PlayerState | null = null;
    let bestD2 = Number.POSITIVE_INFINITY;
    for (const p of world.players.values()) {
      if (p.downed) continue;
      const dx = p.x - gx;
      const dy = p.y - gy;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = p;
      }
    }
    if (best === null) {
      i += 1;
      continue;
    }
    if (bestD2 <= collect2) {
      const tier = g.tier[i] ?? 0;
      if (tier === GEM_TIER_HEAL) {
        best.hp = Math.min(best.maxHp, best.hp + best.maxHp * cfg.healDropFraction);
      } else if (tier === GEM_TIER_MAGNET) {
        g.swapRemove(i); // remove the magnet gem first, then vacuum the rest
        collectAllGems(world);
        continue;
      } else {
        grantTeamXp(world, g.value[i] ?? 0);
      }
      g.swapRemove(i);
      continue;
    }
    const magnet = cfg.pickupMagnetRadius * best.stats.magnetMul;
    if (bestD2 <= magnet * magnet) {
      const d = Math.sqrt(bestD2) || 1;
      const stepDist = cfg.pickupSpeed * dtSeconds;
      g.x[i] = gx + ((best.x - gx) / d) * stepDist;
      g.y[i] = gy + ((best.y - gy) / d) * stepDist;
    }
    i += 1;
  }
}

// ---------------------------------------------------------------------------
// Run lifecycle: director (difficulty ramp), state transitions, revive
// ---------------------------------------------------------------------------

/**
 * A world is **frozen** while any player has a pending level-up offer. The entire
 * authoritative sim halts — run clock, swarm spawning/AI, combat, XP gems, revive,
 * run-state transitions, and player movement — until every pending selection
 * resolves (player `choose` or auto-pick). This makes co-op level-ups a global
 * pause so players level in lockstep (ADR-0018, superseding the Phase-6 "no global
 * pause" decision). Derived from `pendingOffers` (not stored) and reflected to the
 * client via the snapshot `paused` flag so prediction suspends in step.
 */
export function isFrozen(world: WorldState): boolean {
  for (const p of world.players.values()) if (p.pendingOffers.length > 0) return true;
  return false;
}

const DIRECTOR_SPAWN_PER_TICK = 20;
/**
 * Co-op difficulty (idea 4): more players ⇒ a bigger wave budget + concurrent
 * swarm. Each extra alive player adds this fraction. Clamped so a full room can't
 * blow past sane swarm sizes.
 */
const DIRECTOR_PER_PLAYER_FACTOR = 0.55;
const DIRECTOR_PLAYER_MULT_MAX = 4;
/** Opening grace: the first wave trickles in extra-gently (so the squad banks ~L4). */
const DIRECTOR_OPENING_SPAWN_FACTOR = 0.4;

/** Count of players currently alive (not downed). */
function countAlivePlayers(world: WorldState): number {
  let n = 0;
  for (const p of world.players.values()) if (!p.downed) n += 1;
  return n;
}

/** Per-player budget/cap multiplier (idea 4). */
function playerMult(world: WorldState): number {
  const players = Math.max(1, countAlivePlayers(world));
  return Math.min(DIRECTOR_PLAYER_MULT_MAX, 1 + DIRECTOR_PER_PLAYER_FACTOR * (players - 1));
}

/**
 * Within-run trash HP curve (Phase A1 + D0): later waves spawn much tankier trash. The
 * curve is **quadratic** so the *early* game stays gentle (trash near base HP, very mowable)
 * while *late* stages get a steeper "more life in general" climb (×1 → ×~2.9 by the boss).
 * loop 0 / wave 0 = ×1.
 */
export const RUN_TRASH_HP_LIN = 0.04;
export const RUN_TRASH_HP_QUAD = 0.012;
export function trashHpMul(world: WorldState): number {
  const w = Math.max(0, world.waveIndex);
  return 1 + RUN_TRASH_HP_LIN * w + RUN_TRASH_HP_QUAD * w * w;
}

/**
 * Within-run enemy contact-DAMAGE curve (Phase A1 + D0): starts BELOW 1 (gentle early —
 * "less damage altogether" at the start) and ramps up so a dense late swarm actually chips.
 * ×0.6 at wave 0 → ×~2.2 by the final wave. loop 0 baseline.
 */
export const RUN_DMG_BASE = 0.6;
export const RUN_DMG_PER_WAVE = 0.15;
export function runDmgMul(world: WorldState): number {
  return RUN_DMG_BASE + RUN_DMG_PER_WAVE * Math.max(0, world.waveIndex);
}

/**
 * Within-run enemy SPEED curve (D0): the early game is slower-paced — enemies shamble
 * (×0.7) and speed up to full (×1) by mid-run, with a small late overshoot (×1.15).
 * Applied to chase speed in `stepEnemies`.
 */
export function enemySpeedMul(world: WorldState): number {
  const m = 0.7 + 0.05 * Math.max(0, world.waveIndex);
  return m < 0.7 ? 0.7 : m > 1.15 ? 1.15 : m;
}

/** Elite/boss HP scale (item 6 + Phase A1). Co-op term (more players ⇒ tankier elites) PLUS
 * a solo within-run term so a *solo* boss/mini-boss is no longer ×1 and dies in <1s. Capped. */
export function eliteHpMul(world: WorldState): number {
  const players = Math.max(1, countAlivePlayers(world));
  const coop = 1 + 0.6 * (players - 1);
  const solo = 1 + 0.14 * Math.max(0, world.waveIndex); // ×1 → ×~2.5 by the final wave/boss
  return Math.min(9, coop * solo);
}

/** Elite/boss DAMAGE scale (item 6 + Phase A1). Gentler than HP so a big team gets tankier
 * fights without one-shot deaths; includes a mild solo within-run term. Capped. */
export function eliteDmgMul(world: WorldState): number {
  const players = Math.max(1, countAlivePlayers(world));
  const coop = 1 + 0.25 * (players - 1);
  const solo = 1 + 0.06 * Math.max(0, world.waveIndex);
  return Math.min(3.5, coop * solo);
}

/** Endless HP ramp (item 1): each loop makes EVERY enemy tankier. loop 0 = ×1. */
export function loopHpMul(world: WorldState): number {
  return 1 + 0.5 * Math.max(0, world.loopIndex);
}

/** Endless DAMAGE ramp (item 1): each loop makes every enemy hit harder. loop 0 = ×1. */
export function loopDmgMul(world: WorldState): number {
  return 1 + 0.3 * Math.max(0, world.loopIndex);
}

/** Count of alive *trash* enemies (excludes bosses/mini-bosses). */
function aliveTrashCount(world: WorldState): number {
  const e = world.enemies;
  let n = 0;
  for (let i = 0; i < e.count; i += 1) {
    const id = e.id[i] ?? 0;
    if ((e.type[i] ?? 0) < BOSS_ENEMY_TYPE && !world.minibossIds.has(id)) n += 1;
  }
  return n;
}

// --- Spawn breaches (Phase C): directional, telegraphed, escapable spawning ----------------

/** Where a breach at `angle` meets the square arena edge (ray from center → boundary). */
function breachEdgePoint(angle: number, half: number): { x: number; y: number } {
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);
  const m = Math.max(Math.abs(dx), Math.abs(dy)) || 1;
  return { x: (dx / m) * half, y: (dy / m) * half };
}

/** How many breaches the director wants open at once: 1 base, +scale with players & wave,
 * but always ≤ 3 so a chunk of the perimeter stays open as an escape lane. */
function desiredBreachCount(world: WorldState): number {
  const players = Math.max(1, countAlivePlayers(world));
  const byWave = world.waveIndex >= 7 ? 1 : 0;
  return Math.min(3, 1 + (players >= 3 ? 1 : 0) + byWave);
}

/** Smallest absolute angular distance between two angles (radians, 0..π). */
function angleDist(a: number, b: number): number {
  let d = Math.abs(a - b) % (Math.PI * 2);
  if (d > Math.PI) d = Math.PI * 2 - d;
  return d;
}

/** Pick a seeded breach angle that's away from players and from existing breaches (keeps a
 * lane open + avoids spawning on top of someone). Samples a few candidates, takes the best. */
function pickBreachAngle(world: WorldState, cfg: WorldConfig): number | null {
  const half = cfg.arenaHalfSize;
  let rng = world.rngState;
  let best = -1;
  let bestScore = -Infinity;
  for (let s = 0; s < 8; s += 1) {
    let r: number;
    [rng, r] = nextRandom(rng);
    const ang = r * Math.PI * 2;
    // Reject if too close (angularly) to an existing breach → preserves the open lane.
    let tooClose = false;
    for (const b of world.breaches) {
      if (angleDist(ang, b.angle) < BREACH_MIN_SEPARATION) {
        tooClose = true;
        break;
      }
    }
    if (tooClose) continue;
    // Score by distance from the nearest player (farther = safer entrance).
    const p = breachEdgePoint(ang, half);
    let minD = Infinity;
    for (const pl of world.players.values()) {
      const d = Math.hypot(pl.x - p.x, pl.y - p.y);
      if (d < minD) minD = d;
    }
    const score = minD === Infinity ? 1 : minD;
    if (score > bestScore) {
      bestScore = score;
      best = ang;
    }
  }
  world.rngState = rng >>> 0;
  if (best < 0) return null;
  // Reject openings right on top of a player (no pop-in in your face).
  if (bestScore < BREACH_PLAYER_AVOID && world.breaches.length > 0) return null;
  return best;
}

/** Advance breach lifetimes and open fresh ones up to the desired count (Phase C). */
function stepBreaches(world: WorldState, cfg: WorldConfig, dtMs: number): void {
  // Age existing breaches; drop expired ones.
  let w = 0;
  for (const b of world.breaches) {
    if (b.windupMs > 0) b.windupMs = Math.max(0, b.windupMs - dtMs);
    else b.lifeMs -= dtMs;
    if (b.lifeMs > 0) world.breaches[w++] = b;
  }
  world.breaches.length = w;
  // Open new breaches (telegraphed) until we hit the desired count.
  const want = desiredBreachCount(world);
  let guard = 0;
  while (world.breaches.length < want && guard < 4) {
    guard += 1;
    const ang = pickBreachAngle(world, cfg);
    if (ang === null) break;
    world.breaches.push({ angle: ang, windupMs: BREACH_WINDUP_MS, lifeMs: BREACH_LIFE_MS });
  }
}

/** Active (done telegraphing) breach angles; trash spawns from these. */
function activeBreachAngles(world: WorldState): number[] {
  const out: number[] = [];
  for (const b of world.breaches) if (b.windupMs <= 0) out.push(b.angle);
  return out;
}

/** Begin a wave: compute its scaled budget and reset per-wave counters. */
function beginWave(world: WorldState, index: number): void {
  world.waveIndex = index;
  world.wavePhase = "spawning";
  world.waveSpawnedCount = 0;
  world.waveBudget = Math.max(1, Math.round(waveByIndex(index).budget * playerMult(world)));
  world.waveStraggleMs = 0;
  world.waveMinibossSpawned = false;
}

/**
 * Finite-wave director (item 1): spawn the current wave's roster up to its scaled
 * budget (bounded by a concurrent cap), then wait until every wave enemy is dead
 * before a short lull and the next wave. Stragglers are force-cleared after a
 * timeout so one fleeing enemy can't soft-lock the run. Clearing the last wave
 * sets `allWavesCleared` (stepRun then summons the boss). Only runs while
 * `running`. The run clock still advances (HUD + boss timers).
 */
export function stepDirector(
  world: WorldState,
  cfg: WorldConfig = DEFAULT_WORLD_CONFIG,
  dtSeconds: number = SIM_DT_S,
): void {
  if (world.runState !== "running" || isFrozen(world)) return;
  const dtMs = dtSeconds * 1000;
  world.runClockMs += dtMs;
  // Phase C: keep the directional spawn breaches alive/relocating (telegraphed openings).
  stepBreaches(world, cfg, dtMs);

  if (world.wavePhase === "lull") {
    world.waveLullMs -= dtMs;
    if (world.waveLullMs <= 0) {
      if (world.waveIndex + 1 >= WAVE_COUNT) {
        world.allWavesCleared = true; // boss next (stepRun)
      } else {
        beginWave(world, world.waveIndex + 1);
      }
    }
    return;
  }

  // Spawning phase.
  const wave = waveByIndex(world.waveIndex);
  const remainingBudget = world.waveBudget - world.waveSpawnedCount;
  const aliveTrash = aliveTrashCount(world);

  if (remainingBudget > 0) {
    const opening = world.waveIndex === 0 ? DIRECTOR_OPENING_SPAWN_FACTOR : 1;
    const rate = wave.rate ?? DIRECTOR_SPAWN_PER_TICK;
    const cap = wave.cap ?? WAVE_CONCURRENT_CAP;
    const perTick = Math.max(1, Math.floor(rate * opening));
    const concurrentRoom = cap - aliveTrash;
    const toSpawn = Math.min(remainingBudget, perTick, Math.max(0, concurrentRoom));
    if (toSpawn > 0) {
      const before = world.enemies.count;
      spawnEnemies(world, toSpawn, cfg, BOSS_ENEMY_TYPE - 1, wave.weights);
      world.waveSpawnedCount += world.enemies.count - before;
    }
  } else {
    // Budget fully spawned: run the straggler safety timer.
    world.waveStraggleMs += dtMs;
  }

  // Mini-boss(es) join near the wave's end: budget spent + only a few trash left.
  if (
    !world.waveMinibossSpawned &&
    remainingBudget <= 0 &&
    aliveTrash <= Math.max(3, Math.floor(world.waveBudget * 0.12))
  ) {
    spawnWaveMinibosses(world, cfg);
    world.waveMinibossSpawned = true;
  }

  // Straggler auto-clear safeguard DISABLED (per request): stragglers are never
  // force-resolved — the wave only advances once the player has actually killed every
  // wave enemy. (Re-enable by flipping STRAGGLER_AUTOCLEAR if soft-locks resurface.)
  const budgetSpent = remainingBudget <= 0;
  if (
    STRAGGLER_AUTOCLEAR &&
    budgetSpent &&
    world.waveStraggleMs >= WAVE_STRAGGLE_MS &&
    aliveTrash <= STRAGGLER_FORCE_MAX
  ) {
    trickleClearTrash(world, cfg, STRAGGLER_TRICKLE_PER_TICK);
  }

  // Wave clears when the budget is spent and every wave enemy (trash + mini-boss)
  // is dead. (Recompute the trash count: a trickle this tick may have just emptied
  // it.) Stragglers that linger below the timer simply get hunted / carried — they
  // never vanish in a batch.
  if (budgetSpent && world.minibossIds.size === 0 && aliveTrashCount(world) === 0) {
    world.wavePhase = "lull";
    world.waveLullMs = WAVE_LULL_MS;
  }
}

/**
 * Wave straggler safety (no-wipe, bug B1): once the straggler timer expires, resolve
 * leftover trash by killing at most `maxThisTick` of them (each drops its XP gem +
 * counts toward kills) — never the whole batch on one tick. Bleeding them off over a
 * few ticks reads as "the stragglers died" instead of a sudden screen-wide wipe.
 * Leaves bosses/mini-bosses untouched. Deterministic (front-to-back, swap-remove).
 */
function trickleClearTrash(world: WorldState, cfg: WorldConfig, maxThisTick: number): void {
  const e = world.enemies;
  let killed = 0;
  let i = 0;
  while (i < e.count && killed < maxThisTick) {
    const id = e.id[i] ?? 0;
    if ((e.type[i] ?? 0) < BOSS_ENEMY_TYPE && !world.minibossIds.has(id)) {
      dropGem(world, e.x[i] ?? 0, e.y[i] ?? 0, e.type[i] ?? 0, cfg);
      world.kills += 1;
      e.swapRemove(i); // swapped-in element now sits at i; do not advance
      killed += 1;
    } else {
      i += 1;
    }
  }
}

/**
 * Mini-boss roster per wave (item 5). A wave's "headliner" archetype escalates
 * with the wave; later waves also bring back EARLIER archetypes alongside it, so
 * the fights compound. Type ids 4..8 = tank/dasher/warder/charger/poisoner.
 * Indexed by wave; clamped for any extra waves.
 */
export const WAVE_MINIBOSSES: readonly (readonly EnemyTypeId[])[] = [
  [4], // Grunts → Bulwark (tank)
  [5], // Skirmish → Lancer (dasher)
  [6, 4], // Swift Tide → Conduit (warder) + a Bulwark
  [7, 5], // Vanguard → Colossus (charger) + a Lancer
  [8, 6], // Heavy Assault → Blight (poisoner) + a Conduit
  [7, 8, 4], // Onslaught → Colossus + Blight + Bulwark
];

/** Spawn the current wave's mini-boss roster (item 5: per-wave archetypes + escalation). */
function spawnWaveMinibosses(world: WorldState, cfg: WorldConfig): void {
  const roster = WAVE_MINIBOSSES[Math.min(world.waveIndex, WAVE_MINIBOSSES.length - 1)] ?? [
    MINIBOSS_ENEMY_TYPE,
  ];
  for (const typeId of roster) spawnMiniBoss(world, cfg, typeId);
}

/** Spawn one mini-boss of `typeId` — from an active breach edge if one is open (so it
 * enters with the swarm from a telegraphed direction), else a random ring point. */
function spawnMiniBoss(
  world: WorldState,
  cfg: WorldConfig,
  typeId: EnemyTypeId = MINIBOSS_ENEMY_TYPE,
): void {
  const e = world.enemies;
  const [rng, r] = nextRandom(world.rngState);
  world.rngState = rng >>> 0;
  const breaches = activeBreachAngles(world);
  let sx: number;
  let sy: number;
  if (breaches.length > 0) {
    const ang = breaches[Math.min(breaches.length - 1, Math.floor(r * breaches.length))] ?? 0;
    const pt = breachEdgePoint(ang, cfg.arenaHalfSize * 0.92);
    sx = pt.x;
    sy = pt.y;
  } else {
    const half = cfg.arenaHalfSize * 0.85;
    const ang = r * Math.PI * 2;
    sx = Math.cos(ang) * half;
    sy = Math.sin(ang) * half;
  }
  const idx = e.spawn(typeId, sx, sy);
  if (idx < 0) return;
  // Scale HP up by alive-player count so co-op fights aren't trivial (item 6).
  const mbMul = eliteHpMul(world) * loopHpMul(world);
  e.hp[idx] = Math.max(1, Math.round((e.hp[idx] ?? 1) * mbMul));
  if ((e.shieldHp[idx] ?? 0) > 0) e.shieldHp[idx] = Math.round((e.shieldHp[idx] ?? 0) * mbMul);
  const id = e.id[idx] ?? 0;
  world.minibossIds.add(id);
  world.elites.set(id, { cdMs: 2500, phase: 0, phaseMs: 0, tx: 0, ty: 0 });
}

/** Spawn the single boss enemy (clearing trash first for a clean duel). */
function spawnBoss(world: WorldState, cfg: WorldConfig): void {
  world.enemies.clear();
  world.minibossIds.clear(); // trash (incl. mini-bosses) cleared for the duel
  const e = world.enemies;
  const idx = e.spawn(BOSS_ENEMY_TYPE, 0, -cfg.arenaHalfSize * 0.6);
  if (idx < 0) return;
  // Scale boss HP by alive-player count (item 6).
  e.hp[idx] = Math.max(1, Math.round((e.hp[idx] ?? 1) * eliteHpMul(world) * loopHpMul(world)));
  world.bossId = e.id[idx] ?? 0;
  world.bossMaxHp = e.hp[idx] ?? enemyType(BOSS_ENEMY_TYPE).hp;
  world.bossHp = world.bossMaxHp;
  world.elites.set(world.bossId, { cdMs: 3000, phase: 0, phaseMs: 0, tx: 0, ty: 0 });
  // Bullet-hell: pull players into a shrunken arena around the boss.
  world.arenaRadius = cfg.arenaHalfSize * 0.62;
  world.breaches.length = 0; // no trash breaches during the boss duel (Phase C)
}

/** Present index of the boss enemy (by id), or -1 if it is gone (dead). */
function findBossIndex(world: WorldState): number {
  const e = world.enemies;
  for (let i = 0; i < e.count; i += 1) if ((e.id[i] ?? 0) === world.bossId) return i;
  return -1;
}

/**
 * Run state transitions:
 *  - any phase: defeat when all players are downed;
 *  - `running`: surviving the timer enters the `boss` phase (clears trash, spawns the boss);
 *  - `boss`: victory when the boss enemy is killed; mirror its HP for the HUD.
 */
export function stepRun(world: WorldState, cfg: WorldConfig = DEFAULT_WORLD_CONFIG): void {
  if (world.players.size === 0) return;
  if (world.runState !== "running" && world.runState !== "boss") return;
  if (isFrozen(world)) return;
  let anyAlive = false;
  for (const p of world.players.values()) if (!p.downed) anyAlive = true;
  if (!anyAlive) {
    world.runState = "defeat";
    return;
  }
  if (world.runState === "running") {
    if (world.allWavesCleared) {
      spawnBoss(world, cfg);
      world.runState = "boss";
    }
    return;
  }
  // boss phase
  const idx = findBossIndex(world);
  if (idx < 0) {
    world.bossHp = 0;
    world.runState = "victory";
    return;
  }
  world.bossHp = Math.max(0, world.enemies.hp[idx] ?? 0);
}

/**
 * Boss ranged attack: while a boss enemy is alive, count down its fire cooldown
 * and, when it elapses, fire a spread fan of projectiles at the nearest living
 * player. Deterministic (aim derived from positions; fixed fan). Only the boss
 * enemy type with `fireCooldownMs` participates.
 */
export function stepBossAttacks(
  world: WorldState,
  _cfg: WorldConfig = DEFAULT_WORLD_CONFIG,
  dtSeconds: number = SIM_DT_S,
): void {
  if (isFrozen(world)) return;
  const idx = findBossIndex(world);
  if (idx < 0) {
    world.bossFireCdMs = 0;
    return;
  }
  const e = world.enemies;
  const t = enemyType(e.type[idx] ?? BOSS_ENEMY_TYPE);
  const cdMs = t.fireCooldownMs ?? 0;
  if (cdMs <= 0) return;
  world.bossFireCdMs -= dtSeconds * 1000;
  if (world.bossFireCdMs > 0) return;
  world.bossFireCdMs = cdMs;

  const bx = e.x[idx] ?? 0;
  const by = e.y[idx] ?? 0;
  const target = nearestPlayer(world, bx, by);
  if (target === null) return;
  const count = Math.max(1, t.projectileCount ?? 1);
  const speed = t.projectileSpeed ?? 8;
  const dmg = (t.projectileDamage ?? 15) * eliteDmgMul(world) * loopDmgMul(world); // players (item 6) + loop (item 1)
  const ttl = 4;
  if (t.elite === "bullet-hell") {
    // Bullet-hell: alternate a tight aimed fan with a full radial ring (bursts
    // keyed off the tick so it's deterministic). Dense but readable.
    const ring = ((world.tick as number) >> 2) % 3 === 0;
    if (ring) {
      for (let n = 0; n < count; n += 1) {
        const ang = (n / count) * Math.PI * 2;
        world.enemyProjectiles.spawn(
          bx,
          by,
          Math.cos(ang) * speed,
          Math.sin(ang) * speed,
          ttl,
          dmg,
        );
      }
    } else {
      const baseAng = Math.atan2(target.y - by, target.x - bx);
      const spread = 0.14;
      for (let n = 0; n < count; n += 1) {
        const ang = baseAng + (n - (count - 1) / 2) * spread;
        world.enemyProjectiles.spawn(
          bx,
          by,
          Math.cos(ang) * speed,
          Math.sin(ang) * speed,
          ttl,
          dmg,
        );
      }
    }
    return;
  }
  // Default (e.g. wards via their own path): a simple aimed fan.
  const baseAng = Math.atan2(target.y - by, target.x - bx);
  const spread = 0.18;
  for (let n = 0; n < count; n += 1) {
    const ang = baseAng + (n - (count - 1) / 2) * spread;
    world.enemyProjectiles.spawn(bx, by, Math.cos(ang) * speed, Math.sin(ang) * speed, ttl, dmg);
  }
}

/**
 * Advance enemy/boss projectiles: integrate, expire by ttl/arena bounds, and
 * damage any living player they touch (one hit then despawn). Pushes a floating
 * damage number (owner = -1, "enemy"). Not rewound.
 */
export function stepEnemyProjectiles(
  world: WorldState,
  cfg: WorldConfig = DEFAULT_WORLD_CONFIG,
  dtSeconds: number = SIM_DT_S,
): void {
  if (isFrozen(world)) return;
  const ep = world.enemyProjectiles;
  const half = cfg.arenaHalfSize;
  const hitR = cfg.playerRadius + 0.25;
  let i = 0;
  while (i < ep.count) {
    const nx = (ep.x[i] ?? 0) + (ep.vx[i] ?? 0) * dtSeconds;
    const ny = (ep.y[i] ?? 0) + (ep.vy[i] ?? 0) * dtSeconds;
    ep.x[i] = nx;
    ep.y[i] = ny;
    ep.ttl[i] = (ep.ttl[i] ?? 0) - dtSeconds;
    if ((ep.ttl[i] ?? 0) <= 0 || nx < -half || nx > half || ny < -half || ny > half) {
      ep.swapRemove(i);
      continue;
    }
    let hit = false;
    for (const p of world.players.values()) {
      if (p.downed) continue;
      const dx = p.x - nx;
      const dy = p.y - ny;
      if (dx * dx + dy * dy <= hitR * hitR) {
        const dmg = incomingDamage(world, p, ep.damage[i] ?? 0); // dodge + armor (item 4)
        p.hp = Math.max(0, p.hp - dmg);
        world.hits.push(nx, ny, dmg, 0, -1);
        if (p.hp <= 0) {
          p.downed = true;
          p.reviveProgress = 0;
        }
        hit = true;
        break;
      }
    }
    if (hit) {
      ep.swapRemove(i);
      continue;
    }
    i += 1;
  }
}

// --- Elite (mini-boss/boss) telegraphed behaviors (item 5) -------------------

const ELITE_DASH_TELEGRAPH_MS = 900;
const ELITE_DASH_FIRE_MS = 260;
const ELITE_DASH_LEN = 11;
const ELITE_DASH_WIDTH = 2.6;
const ELITE_DASH_DMG = 36;
const ELITE_CHARGE_TELEGRAPH_MS = 1400;
const ELITE_CHARGE_RADIUS = 6.5;
const ELITE_CHARGE_DMG = 48;
const ELITE_CHARGE_KNOCKBACK = 9;
const ELITE_POISON_RADIUS = 2.6;
const ELITE_POISON_DPS = 14;
const ELITE_POISON_LIFE_MS = 5000;
const WARD_FIRE_RANGE = 16;

/** Find the present enemy index for an id, or -1. */
function enemyIndexById(world: WorldState, id: number): number {
  const e = world.enemies;
  for (let i = 0; i < e.count; i += 1) if ((e.id[i] ?? 0) === id) return i;
  return -1;
}

/** Point-in-rotated-rectangle test (segment from (ax,ay)→(bx,by), half-width hw). */
function pointInCapsule(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  hw: number,
): boolean {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy || 1;
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const cx = ax + dx * t;
  const cy = ay + dy * t;
  const ex = px - cx;
  const ey = py - cy;
  return ex * ex + ey * ey <= hw * hw;
}

/**
 * Active poison zones (item 5, Blight). Server-side list; each is rebroadcast as a
 * telegraph and damages players standing in it. Kept off WorldState's hashed core
 * (deterministic from elite ticks + RNG already threaded).
 */
interface PoisonZone {
  x: number;
  y: number;
  lifeMs: number;
}

/**
 * Advance every elite's telegraphed special and rebuild the telegraph list for the
 * snapshot. Runs after movement so positions are current. Deterministic.
 */
export function stepEliteBehaviors(
  world: WorldState,
  cfg: WorldConfig = DEFAULT_WORLD_CONFIG,
  dtSeconds: number = SIM_DT_S,
): void {
  if (isFrozen(world)) return;
  const dtMs = dtSeconds * 1000;
  const e = world.enemies;
  world.telegraphs.clear();

  // Phase C: surface spawn breaches as edge markers (progress = wind-up; 1 = open/emitting).
  for (const b of world.breaches) {
    const pt = breachEdgePoint(b.angle, cfg.arenaHalfSize);
    const prog = b.windupMs > 0 ? 1 - b.windupMs / BREACH_WINDUP_MS : 1;
    world.telegraphs.push(TELE_BREACH, pt.x, pt.y, 0, 0, cfg.arenaHalfSize * 0.06, prog);
  }

  // Drop elite state for dead elites.
  for (const id of [...world.elites.keys()]) {
    if (enemyIndexById(world, id) < 0) world.elites.delete(id);
  }

  // Persistent poison zones live on a module map keyed by world (one room/world).
  const zones = poisonZonesFor(world);
  for (let z = zones.length - 1; z >= 0; z -= 1) {
    const zone = zones[z];
    if (zone === undefined) continue;
    zone.lifeMs -= dtMs;
    if (zone.lifeMs <= 0) {
      zones.splice(z, 1);
      continue;
    }
    world.telegraphs.push(TELE_POISON, zone.x, zone.y, 0, 0, ELITE_POISON_RADIUS, 1);
    // Damage players standing in the zone.
    for (const p of world.players.values()) {
      if (p.downed) continue;
      const dx = p.x - zone.x;
      const dy = p.y - zone.y;
      if (dx * dx + dy * dy <= ELITE_POISON_RADIUS * ELITE_POISON_RADIUS) {
        const d = incomingDamage(
          world,
          p,
          ELITE_POISON_DPS * dtSeconds * eliteDmgMul(world) * loopDmgMul(world),
        );
        p.hp = Math.max(0, p.hp - d);
        if (p.hp <= 0) {
          p.downed = true;
          p.reviveProgress = 0;
        }
      }
    }
  }

  // Bullet-hell arena ring telegraph (the boss pull-in).
  if (world.arenaRadius > 0) {
    world.telegraphs.push(TELE_ARENA, 0, 0, 0, 0, world.arenaRadius, 1);
    // Soft-wall: pull any player outside the ring back to its edge.
    const r = world.arenaRadius;
    for (const p of world.players.values()) {
      const d = Math.hypot(p.x, p.y);
      if (d > r) {
        p.x = (p.x / d) * r;
        p.y = (p.y / d) * r;
      }
    }
  }

  for (const [id, st] of world.elites) {
    const idx = enemyIndexById(world, id);
    if (idx < 0) continue;
    const t = enemyType(e.type[idx] ?? 0);
    const ex = e.x[idx] ?? 0;
    const ey = e.y[idx] ?? 0;
    if (st.phase === 9) {
      stepWardShot(world, st, ex, ey, dtMs); // ward turret
      continue;
    }
    switch (t.elite) {
      case "dasher":
        stepDasher(world, st, idx, ex, ey, dtMs);
        break;
      case "warder":
        stepWarder(world, cfg, st, idx, ex, ey, dtMs);
        break;
      case "charger":
        stepCharger(world, st, ex, ey, dtMs);
        break;
      case "poisoner":
        stepPoisoner(world, st, ex, ey, dtMs, zones);
        break;
      default:
        break; // tank / bullet-hell handled by movement + stepBossAttacks
    }
  }
}

/** Dasher (Lancer): telegraph a rectangle aimed at a player, then dash along it. */
function stepDasher(
  world: WorldState,
  st: EliteState,
  idx: number,
  ex: number,
  ey: number,
  dtMs: number,
): void {
  const e = world.enemies;
  if (st.phase === 0) {
    st.cdMs -= dtMs;
    if (st.cdMs <= 0) {
      const target = nearestPlayer(world, ex, ey);
      if (target !== null) {
        const ang = Math.atan2(target.y - ey, target.x - ex);
        st.tx = Math.cos(ang);
        st.ty = Math.sin(ang);
        st.phase = 1;
        st.phaseMs = ELITE_DASH_TELEGRAPH_MS;
      }
    }
    return;
  }
  const bx = ex + st.tx * ELITE_DASH_LEN;
  const by = ey + st.ty * ELITE_DASH_LEN;
  if (st.phase === 1) {
    const prog = 1 - st.phaseMs / ELITE_DASH_TELEGRAPH_MS;
    world.telegraphs.push(TELE_DASH, ex, ey, bx, by, ELITE_DASH_WIDTH * 0.5, prog * 0.6);
    st.phaseMs -= dtMs;
    if (st.phaseMs <= 0) {
      st.phase = 2;
      st.phaseMs = ELITE_DASH_FIRE_MS;
    }
    return;
  }
  // phase 2: the dash itself — sweep + damage anyone in the rectangle.
  world.telegraphs.push(TELE_DASH, ex, ey, bx, by, ELITE_DASH_WIDTH * 0.5, 1);
  e.x[idx] = ex + st.tx * (ELITE_DASH_LEN / (ELITE_DASH_FIRE_MS / (dtMs || 1)));
  e.y[idx] = ey + st.ty * (ELITE_DASH_LEN / (ELITE_DASH_FIRE_MS / (dtMs || 1)));
  for (const p of world.players.values()) {
    if (p.downed) continue;
    if (pointInCapsule(p.x, p.y, ex, ey, bx, by, ELITE_DASH_WIDTH * 0.5)) {
      const dmg = incomingDamage(world, p, ELITE_DASH_DMG * eliteDmgMul(world) * loopDmgMul(world));
      p.hp = Math.max(0, p.hp - dmg);
      world.hits.push(p.x, p.y, dmg, 0, -1);
      if (p.hp <= 0) {
        p.downed = true;
        p.reviveProgress = 0;
      }
    }
  }
  st.phaseMs -= dtMs;
  if (st.phaseMs <= 0) {
    st.phase = 0;
    st.cdMs = 2600;
  }
}

/** Warder (Conduit): periodically spawn a stationary ward turret. */
function stepWarder(
  world: WorldState,
  cfg: WorldConfig,
  st: EliteState,
  _idx: number,
  ex: number,
  ey: number,
  dtMs: number,
): void {
  st.cdMs -= dtMs;
  if (st.cdMs > 0) return;
  st.cdMs = 4200;
  const e = world.enemies;
  const [rng, r] = nextRandom(world.rngState);
  world.rngState = rng >>> 0;
  const ang = r * Math.PI * 2;
  const wx = clampToArena(ex + Math.cos(ang) * 3.5, cfg.arenaHalfSize);
  const wy = clampToArena(ey + Math.sin(ang) * 3.5, cfg.arenaHalfSize);
  const wi = e.spawn(WARD_ENEMY_TYPE, wx, wy);
  if (wi < 0) return;
  // Wards fire on their own cadence via a lightweight elite entry (warderShot phase).
  world.elites.set(e.id[wi] ?? 0, { cdMs: 1000, phase: 9, phaseMs: 0, tx: 0, ty: 0 });
}

/** Charger (Colossus): charge a circle around itself → blast + knockback. */
function stepCharger(
  world: WorldState,
  st: EliteState,
  ex: number,
  ey: number,
  dtMs: number,
): void {
  if (st.phase === 0) {
    st.cdMs -= dtMs;
    if (st.cdMs <= 0) {
      st.phase = 1;
      st.phaseMs = ELITE_CHARGE_TELEGRAPH_MS;
    }
    return;
  }
  if (st.phase === 1) {
    const prog = 1 - st.phaseMs / ELITE_CHARGE_TELEGRAPH_MS;
    world.telegraphs.push(TELE_CIRCLE, ex, ey, 0, 0, ELITE_CHARGE_RADIUS, prog);
    st.phaseMs -= dtMs;
    if (st.phaseMs <= 0) {
      // Blast: damage + knockback everyone inside.
      for (const p of world.players.values()) {
        if (p.downed) continue;
        const dx = p.x - ex;
        const dy = p.y - ey;
        const d2 = dx * dx + dy * dy;
        if (d2 <= ELITE_CHARGE_RADIUS * ELITE_CHARGE_RADIUS) {
          const dmg = incomingDamage(
            world,
            p,
            ELITE_CHARGE_DMG * eliteDmgMul(world) * loopDmgMul(world),
          );
          p.hp = Math.max(0, p.hp - dmg);
          world.hits.push(p.x, p.y, dmg, 0, -1);
          const d = Math.sqrt(d2) || 1;
          p.x = clampToArena(
            p.x + (dx / d) * ELITE_CHARGE_KNOCKBACK,
            DEFAULT_WORLD_CONFIG.arenaHalfSize,
          );
          p.y = clampToArena(
            p.y + (dy / d) * ELITE_CHARGE_KNOCKBACK,
            DEFAULT_WORLD_CONFIG.arenaHalfSize,
          );
          if (p.hp <= 0) {
            p.downed = true;
            p.reviveProgress = 0;
          }
        }
      }
      st.phase = 0;
      st.cdMs = 3200;
    }
  }
}

/** Poisoner (Blight): periodically drop a lingering poison circle on a player. */
function stepPoisoner(
  world: WorldState,
  st: EliteState,
  ex: number,
  ey: number,
  dtMs: number,
  zones: PoisonZone[],
): void {
  st.cdMs -= dtMs;
  if (st.cdMs > 0) return;
  st.cdMs = 2000;
  const target = nearestPlayer(world, ex, ey);
  if (target === null || zones.length >= 24) return;
  zones.push({ x: target.x, y: target.y, lifeMs: ELITE_POISON_LIFE_MS });
}

function clampToArena(v: number, half: number): number {
  return v < -half ? -half : v > half ? half : v;
}

/** Server-side poison-zone lists per world (elite behaviors run server-only). */
const poisonZonesByWorld = new WeakMap<WorldState, PoisonZone[]>();
function poisonZonesFor(world: WorldState): PoisonZone[] {
  let z = poisonZonesByWorld.get(world);
  if (z === undefined) {
    z = [];
    poisonZonesByWorld.set(world, z);
  }
  return z;
}

/** A ward turret fires a single aimed shot at the nearest player on a cadence. */
function stepWardShot(
  world: WorldState,
  st: EliteState,
  ex: number,
  ey: number,
  dtMs: number,
): void {
  st.cdMs -= dtMs;
  if (st.cdMs > 0) return;
  st.cdMs = 1400;
  const target = nearestPlayer(world, ex, ey);
  if (target === null) return;
  const dx = target.x - ex;
  const dy = target.y - ey;
  if (dx * dx + dy * dy > WARD_FIRE_RANGE * WARD_FIRE_RANGE) return;
  const ang = Math.atan2(dy, dx);
  world.enemyProjectiles.spawn(ex, ey, Math.cos(ang) * 8, Math.sin(ang) * 8, 4, 10);
}

export interface ExtractionAugment {
  readonly name: string;
  readonly mods: ArtifactMods;
}
export const EXTRACTION_AUGMENTS: readonly ExtractionAugment[] = [
  { name: "Overcharged Core", mods: { damageMul: 0.4 } },
  { name: "Adrenaline Rush", mods: { moveMul: 0.25, cooldownMul: -0.15 } },
  { name: "Bulwark", mods: { maxHpBonus: 80 } },
  { name: "Hair Trigger", mods: { cooldownMul: -0.3 } },
];

/** Cross-session artifact awarded (to logged-in players) on a successful extraction. */
export const EXTRACTION_ARTIFACT_ID = "relic-of-extraction";

function anyAlivePlayerInZone(world: WorldState, ex: ExtractionState): boolean {
  const r2 = ex.radius * ex.radius;
  for (const p of world.players.values()) {
    if (p.downed) continue;
    const dx = p.x - ex.x;
    const dy = p.y - ex.y;
    if (dx * dx + dy * dy <= r2) return true;
  }
  return false;
}

/** Deterministically pick one augment (seeded) and apply it to every alive player. */
function grantExtractionAugment(world: WorldState): void {
  const [nextRng, r] = nextRandom(world.rngState);
  world.rngState = nextRng >>> 0;
  const aug =
    EXTRACTION_AUGMENTS[Math.floor(r * EXTRACTION_AUGMENTS.length)] ?? EXTRACTION_AUGMENTS[0];
  if (aug === undefined) return;
  for (const p of world.players.values()) if (!p.downed) applyStatMods(p, aug.mods);
}

/**
 * Optional extraction event (hold-the-point). Runs only during the `running` phase
 * (and never while frozen). Lifecycle: `idle` → `open` (zone appears at a seeded
 * spot once `runClockMs ≥ extractionOpenMs`) → `active` (an alive player stepped in:
 * spawn a horde + start the hold timer, which counts down only while someone is
 * inside — it pauses when the zone is empty) → `success` (held to 0: grant the squad
 * an augment) or `failed` (party wiped). Deterministic.
 */
export function stepExtraction(
  world: WorldState,
  cfg: WorldConfig = DEFAULT_WORLD_CONFIG,
  dtSeconds: number = SIM_DT_S,
): void {
  if (isFrozen(world)) return;
  if (world.runState !== "running") return;
  const ex = world.extraction;
  const dtMs = dtSeconds * 1000;

  if (ex.phase === "idle") {
    if (world.runClockMs < cfg.extractionOpenMs) return;
    const [s1, rx] = nextRandom(world.rngState);
    const [s2, ry] = nextRandom(s1);
    world.rngState = s2 >>> 0;
    const span = cfg.arenaHalfSize * 0.6;
    ex.x = (rx * 2 - 1) * span;
    ex.y = (ry * 2 - 1) * span;
    ex.radius = cfg.extractionRadius;
    ex.holdTotalMs = cfg.extractionHoldMs;
    ex.holdRemainingMs = cfg.extractionHoldMs;
    ex.phase = "open";
    return;
  }

  if (ex.phase === "open") {
    if (anyAlivePlayerInZone(world, ex)) {
      ex.phase = "active";
      spawnEnemies(
        world,
        cfg.extractionHordeBurst,
        cfg,
        BOSS_ENEMY_TYPE - 1,
        waveByIndex(world.waveIndex).weights,
      );
    }
    return;
  }

  if (ex.phase === "active") {
    let anyAlive = false;
    for (const p of world.players.values()) if (!p.downed) anyAlive = true;
    if (!anyAlive) {
      ex.phase = "failed";
      return;
    }
    // Hold counts down only while occupied; pauses (no decay) when the zone is empty.
    if (anyAlivePlayerInZone(world, ex)) {
      ex.holdRemainingMs -= dtMs;
      if (ex.holdRemainingMs <= 0) {
        ex.holdRemainingMs = 0;
        ex.phase = "success";
        grantExtractionAugment(world);
      }
    }
  }
}

/** Co-op revive: a downed player near an alive ally accrues revive time. */
export function stepRevive(
  world: WorldState,
  cfg: WorldConfig = DEFAULT_WORLD_CONFIG,
  dtSeconds: number = SIM_DT_S,
): void {
  const r2 = cfg.reviveRadius * cfg.reviveRadius;
  const dtMs = dtSeconds * 1000;
  if (isFrozen(world)) return;
  for (const p of world.players.values()) {
    if (!p.downed) continue;
    let reviver = false;
    for (const a of world.players.values()) {
      if (a.id === p.id || a.downed) continue;
      const dx = a.x - p.x;
      const dy = a.y - p.y;
      if (dx * dx + dy * dy <= r2) {
        reviver = true;
        break;
      }
    }
    if (reviver) {
      p.reviveProgress += dtMs;
      if (p.reviveProgress >= cfg.reviveMs) {
        p.downed = false;
        p.hp = Math.max(1, p.maxHp * cfg.reviveHpFraction);
        p.reviveProgress = 0;
      }
    } else {
      p.reviveProgress = Math.max(0, p.reviveProgress - dtMs * 2);
    }
  }
}

/** Begin the run (lobby → running), resetting the clock. */
export function startRun(world: WorldState): void {
  if (world.runState === "running") return;
  world.runState = "running";
  world.runClockMs = 0;
  world.allWavesCleared = false;
  world.loopIndex = 0;
  world.ascension = false;
  world.minibossIds.clear();
  resetTeamProgression(world);
  resetExtraction(world.extraction);
  beginWave(world, 0);
}

/**
 * Endless "Continue" (item 1): from a victory, start the next loop. Keeps the squad's
 * cumulative kills, team level/XP, and per-player builds; clears the arena and boss,
 * heals everyone to full, bumps `loopIndex` (ramps enemy HP/damage), and re-arms wave 0.
 * No-op unless the run is at victory.
 *
 * `ascension` (B-ascend): true ⇒ lift the level + per-weapon/per-passive upgrade caps so
 * the squad keeps leveling and upgrading their committed build to infinity; false ⇒ the
 * "keep your build" loop (carry the maxed build, no further upgrades). Sticky for the loop.
 */
export function continueEndless(
  world: WorldState,
  _cfg: WorldConfig = DEFAULT_WORLD_CONFIG,
  ascension = false,
): void {
  if (world.runState !== "victory") return;
  world.loopIndex += 1;
  world.ascension = ascension;
  world.enemies.clear();
  world.projectiles.clear();
  world.enemyProjectiles.clear();
  world.pickups.clear();
  world.hits.clear();
  world.arcs.clear();
  world.telegraphs.clear();
  world.elites.clear();
  world.minibossIds.clear();
  world.arenaRadius = 0;
  world.bossId = 0;
  world.bossHp = 0;
  world.bossMaxHp = 0;
  world.bossFireCdMs = 0;
  world.allWavesCleared = false;
  // Fresh, full-HP start to the new loop (builds/levels/kills carry over).
  for (const p of world.players.values()) {
    p.hp = p.maxHp;
    p.downed = false;
    p.reviveProgress = 0;
    p.ascension = ascension; // mirror onto each player for upgrade-validity checks
  }
  world.runState = "running";
  beginWave(world, 0);
}

/** Reset the shared team XP pool to a fresh run (level 1). */
function resetTeamProgression(world: WorldState): void {
  world.teamLevel = 1;
  world.teamXp = 0;
  world.teamXpToNext = xpToNextFor(1);
}

/** Reset the extraction event to closed/idle (mutates in place). */
function resetExtraction(ex: ExtractionState): void {
  ex.phase = "idle";
  ex.x = 0;
  ex.y = 0;
  ex.radius = 0;
  ex.holdRemainingMs = 0;
  ex.holdTotalMs = 0;
}

/** Reset a player to a fresh run state (keeps id; mutates in place). */
function resetPlayer(p: PlayerState, cfg: WorldConfig): void {
  p.hp = cfg.playerMaxHp;
  p.maxHp = cfg.playerMaxHp;
  p.downed = false;
  p.reviveProgress = 0;
  p.level = 1;
  p.xp = 0;
  p.xpToNext = xpToNextFor(1);
  p.kills = 0;
  p.equipped.length = 0;
  p.equipped.push({ power: STARTING_POWER, level: 1, cooldown: 0 });
  p.stats.moveMul = 1;
  p.stats.damageMul = 1;
  p.stats.cooldownMul = 1;
  p.stats.magnetMul = 1;
  p.stats.critChance = 0;
  p.stats.critMul = 2;
  p.stats.lifesteal = 0;
  p.stats.statikLevel = 0;
  p.stats.regenPerSec = 0;
  p.stats.armor = 0;
  p.stats.thorns = 0;
  p.stats.dodgeChance = 0;
  p.stats.projSpeedMul = 1;
  p.stats.areaMul = 1;
  p.stats.bossDmgMul = 1;
  p.stats.executeFrac = 0;
  p.stats.goldFind = 1;
  p.stats.extraProjectiles = 0;
  p.stats.extraPierce = 0;
  p.stats.homing = 0;
  p.upgradeCounts.fill(0);
  p.pendingOffers.length = 0;
  p.novaPulse = false;
  p.dealtThisTick = 0;
  p.statikCharge = 0;
  p.abilityKind = "none";
  p.abilityCooldownMs = 0;
  p.abilityCdMs = 0;
  p.shieldMs = 0;
  p.aimX = 1;
  p.aimY = 0;
  p.manualAim = false;
  p.dashMs = 0;
  p.dashVx = 0;
  p.dashVy = 0;
  p.ascension = false;
}

/** Wipe the world to a fresh, pre-run state (optionally reseed). Shared by
 * `restartRun` (→ running) and `returnToLobby` (→ lobby). Does not set runState. */
function wipeWorld(world: WorldState, cfg: WorldConfig, seed?: number): void {
  world.enemies.clear();
  world.projectiles.clear();
  world.enemyProjectiles.clear();
  world.pickups.clear();
  world.hits.clear();
  world.arcs.clear();
  world.telegraphs.clear();
  world.elites.clear();
  world.arenaRadius = 0;
  world.kills = 0;
  world.runClockMs = 0;
  world.bossId = 0;
  world.bossHp = 0;
  world.bossMaxHp = 0;
  world.bossFireCdMs = 0;
  world.waveIndex = 0;
  world.wavePhase = "spawning";
  world.waveSpawnedCount = 0;
  world.waveBudget = 0;
  world.waveLullMs = 0;
  world.waveStraggleMs = 0;
  world.waveMinibossSpawned = false;
  world.allWavesCleared = false;
  world.breaches.length = 0;
  world.flow = null; // rebuilt for the run's map (Phase D2)
  world.loopIndex = 0;
  world.ascension = false;
  world.minibossIds.clear();
  world.tick = asTick(0);
  if (seed !== undefined) world.rngState = seed >>> 0;
  resetTeamProgression(world);
  for (const p of world.players.values()) resetPlayer(p, cfg);
  resetExtraction(world.extraction);
}

/** Restart: wipe the world to a fresh run (optionally reseed) and start it. */
export function restartRun(
  world: WorldState,
  cfg: WorldConfig = DEFAULT_WORLD_CONFIG,
  seed?: number,
): void {
  wipeWorld(world, cfg, seed);
  world.runState = "running";
  // Arm wave 0 just like startRun. Without this, wipeWorld leaves waveBudget=0,
  // so stepDirector sees remainingBudget<=0 with zero trash and immediately spawns
  // the wave's mini-boss roster on a fresh run (bug: restart drops you onto a
  // mini-boss with no preceding wave).
  beginWave(world, 0);
}

/** Return to the lobby: wipe the world to a fresh pre-game state (optionally
 * reseed) and sit in `lobby` until someone starts the next run. */
export function returnToLobby(
  world: WorldState,
  cfg: WorldConfig = DEFAULT_WORLD_CONFIG,
  seed?: number,
): void {
  wipeWorld(world, cfg, seed);
  world.runState = "lobby";
}

// ---------------------------------------------------------------------------
// State hashing (cheap desync detector for prediction vs authority)
// ---------------------------------------------------------------------------

/** FNV-1a over a quantized snapshot of players + enemies. Order-independent. */
export function hashWorld(world: WorldState): number {
  let h = 0x811c9dc5;
  h = fnv1a(h, runStateCode(world.runState));
  h = fnv1a(h, Math.round(world.runClockMs));
  h = fnv1a(h, extractionPhaseCode(world.extraction.phase));
  h = fnv1a(h, Math.round(world.extraction.holdRemainingMs));
  // Shared team progression (per-player level/xp are mirrors of these, so we
  // hash the authoritative pool once instead of each mirror — a freshly-joined
  // player that hasn't been mirrored yet can't desync the hash).
  h = fnv1a(h, world.teamLevel);
  h = fnv1a(h, Math.round(world.teamXp));
  h = fnv1a(h, world.waveIndex);
  h = fnv1a(h, world.wavePhase === "lull" ? 1 : 0);
  h = fnv1a(h, world.waveSpawnedCount);
  h = fnv1a(h, world.allWavesCleared ? 1 : 0);
  h = fnv1a(h, world.loopIndex);
  // Sort ids so client/server agree regardless of Map insertion order.
  const ids = [...world.players.keys()].sort((a, b) => a - b);
  for (const id of ids) {
    const p = world.players.get(id);
    if (p === undefined) continue;
    // Quantize to 1/1000 world unit to avoid float noise across engines.
    h = fnv1a(h, id);
    h = fnv1a(h, Math.round(p.x * 1000));
    h = fnv1a(h, Math.round(p.y * 1000));
    h = fnv1a(h, Math.round(p.hp * 1000));
    h = fnv1a(h, p.downed ? 1 : 0);
    h = fnv1a(h, p.kills);
    h = fnv1a(h, Math.round(p.statikCharge));
    for (const eq of p.equipped) {
      h = fnv1a(h, eq.power);
      h = fnv1a(h, eq.level);
    }
  }
  // Enemies, sorted by id for order-independence.
  const e = world.enemies;
  const order = Array.from({ length: e.count }, (_, i) => i).sort(
    (a, b) => (e.id[a] ?? 0) - (e.id[b] ?? 0),
  );
  for (const i of order) {
    h = fnv1a(h, e.id[i] ?? 0);
    h = fnv1a(h, Math.round((e.x[i] ?? 0) * 1000));
    h = fnv1a(h, Math.round((e.y[i] ?? 0) * 1000));
    h = fnv1a(h, Math.round((e.hp[i] ?? 0) * 1000));
  }
  // Projectiles, sorted by id for order-independence.
  const proj = world.projectiles;
  const porder = Array.from({ length: proj.count }, (_, i) => i).sort(
    (a, b) => (proj.id[a] ?? 0) - (proj.id[b] ?? 0),
  );
  for (const i of porder) {
    h = fnv1a(h, proj.id[i] ?? 0);
    h = fnv1a(h, Math.round((proj.x[i] ?? 0) * 1000));
    h = fnv1a(h, Math.round((proj.y[i] ?? 0) * 1000));
  }
  // Enemy/boss projectiles, sorted by id.
  const ep = world.enemyProjectiles;
  const eporder = Array.from({ length: ep.count }, (_, i) => i).sort(
    (a, b) => (ep.id[a] ?? 0) - (ep.id[b] ?? 0),
  );
  for (const i of eporder) {
    h = fnv1a(h, ep.id[i] ?? 0);
    h = fnv1a(h, Math.round((ep.x[i] ?? 0) * 1000));
    h = fnv1a(h, Math.round((ep.y[i] ?? 0) * 1000));
  }
  // XP gems, sorted by id.
  const g = world.pickups;
  const gorder = Array.from({ length: g.count }, (_, i) => i).sort(
    (a, b) => (g.id[a] ?? 0) - (g.id[b] ?? 0),
  );
  for (const i of gorder) {
    h = fnv1a(h, g.id[i] ?? 0);
    h = fnv1a(h, Math.round((g.x[i] ?? 0) * 1000));
    h = fnv1a(h, Math.round((g.y[i] ?? 0) * 1000));
  }
  return h >>> 0;
}

function fnv1a(hash: number, value: number): number {
  let h = hash;
  // Mix 4 bytes of the (possibly negative) integer.
  for (let i = 0; i < 4; i += 1) {
    h ^= (value >>> (i * 8)) & 0xff;
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
